/**
 * Backend API types.
 *
 * These mirror the Pydantic schemas served by the FastAPI backend.
 * Keep them hand-written rather than code-generated — the surface is
 * small, the types are stable, and the code-gen build step would cost
 * more than it saves.
 */

import type { WeeklySchedule } from "./queries/admin/machines";

// --- Catalog (GET /materials) ------------------------------------------

export interface MaterialOptionPublic {
  code: string;
  label: string;
  /** Barevný swatch (color_options / option_list s barvou). */
  hex?: string | null;
  /** Obrázkový swatch (option_list s obrázkem). */
  swatch_image_url?: string | null;
}

export interface Material {
  code: string;
  display_name: string;
  description: string | null;
  features: string | null;
  density_kg_m3: number;
  /** Offered values of every pricing variant axis, keyed by axis key
   *  (``{<axis>: [values]}``), derived from the material's active
   *  pricing rows and skill-matrix-filtered. The axis's label + unit are NOT
   *  here — they belong to the process that declares the axis, read off its
   *  manifest (`variant_param`). Optional kvůli back-compat s test fixtures. */
  variant_axis_values?: Record<string, number[]>;
  /** KAN-2190 — táž nabídka PER OPERACE (`{process_type: {axis: values}}`):
   *  jen hodnoty, které operace naceňuje a vezme aspoň jeden její stroj.
   *  Čti přes `offeredAxisValues`. */
  variant_axis_values_by_process?: Record<string, Record<string, number[]>>;
  finish_codes: string[];
  /** Filament color codes (3D-print modifiers) linkované na materiál
   *  přes `material_process_modifiers`. Public katalog z toho staví
   *  Class 2 "Barva" filtr po výběru "3D tisk". Optional kvůli back-
   *  compat s test fixtures. */
  color_option_codes?: string[];
  /** Per-material option lists keyed by material-property key (value_type
   *  `color_options` or `option_list`). Drives the configurator option widget
   *  for a `choice` field bound via `options_source.material_property` (color
   *  swatch / image swatch / plain select per option data). `code` is the join
   *  key into pricing + the cart/order snapshot. */
  option_sources?: Record<string, MaterialOptionPublic[]>;
  /** Procesy, pro které katalog materiál nabízí — bod 4B: ODVOZENÉ
   *  (proces → kategorie, volitelně zúžené allow-listem
   *  `material_process_types`). Public katalog z toho staví "kde to lze
   *  použít" filtr. Optional kvůli back-compat s test fixtures. */
  process_types?: string[];
  /** Admin-managed swatch (PR 9). FE prefers `swatch_image_url`, then
   *  `swatch_hex`, then a deterministic fallback gradient. */
  /** Engine-driven "from X CZK" — MIN unit price (qty=1) across priced
   *  variants, computed from the process's authored formula over its
   *  frozen sample snapshot. Absent/null = no formula+sample → hide. */
  price_from_czk?: number | null;
  swatch_hex?: string | null;
  swatch_image_url?: string | null;
  /** Public URL to the material datasheet PDF, set by the backend only
   *  when the tenant admin flipped `expose_certs=true` and uploaded a
   *  datasheet. `null` otherwise. */
  datasheet_url?: string | null;
}

export interface Finish {
  code: string;
  display_name: string;
  surcharge_pct: number;
  /** Admin-managed swatch (PR 9). FE prefers `swatch_image_url`, then
   *  looks up a predefined SVG glyph by `icon_key`. */
  swatch_image_url?: string | null;
  icon_key?: string | null;
}

export interface ColorOption {
  code: string;
  display_name: string;
  swatch_hex?: string | null;
  swatch_image_url?: string | null;
}

interface DeliveryOption {
  code: string;
  /** Floor on lead time. Renamed from the legacy ``days`` field on
   * 2026-04-25 alongside ``items_per_day`` capacity. The customer-
   * facing "min_days" the FE renders is computed at the call-site
   * via ``computeEffectiveDays(option, totalQty)``. */
  base_min_days: number;
  /** Capacity in pieces / day. Anything above ``base_min_days *
   * items_per_day`` stretches the lead time linearly. */
  items_per_day: number;
  multiplier: number;
  /** Flat surcharge in CZK added once per order at checkout (on top
   * of shipping) when this tier is selected. Zero for all seed tiers
   * through 2026-04-24; admins can set a positive value in the
   * „Termíny" tabu detailu uzlu mapy výroby so the pricing matches how
   * couriers actually bill express slots. */
  flat_surcharge_czk: number;
}

/**
 * Lead-time formula — must match the BE ``DeliveryTier.effective_days``
 * helper. ``items_per_day <= 0`` is treated as "infinite" so a
 * misconfigured tier never produces an absurd quote on the FE.
 */
export function computeEffectiveDays(
  option: { base_min_days: number; items_per_day: number },
  totalQuantity: number,
): number {
  const base = Math.max(0, option.base_min_days | 0);
  const cap = option.items_per_day | 0;
  if (cap <= 0) return base;
  const stretch = Math.ceil(Math.max(0, totalQuantity) / cap);
  return Math.max(base, stretch);
}

/** Public-safe slice of active 3D-print ``Machine`` rows. Build-volume
 *  dimensions only — the customer-facing configurator renders the
 *  print bed in the 3D viewer and picks the smallest fitting printer
 *  for the uploaded part. Pricing-related fields stay admin-only. */

export interface Catalog {
  lang: string;
  materials: Material[];
  finishes: Finish[];
  color_options?: ColorOption[];
  delivery_options: DeliveryOption[];
}

// --- Quotes (POST /quotes, GET /quotes/:id) ---------------------------

/**
 * Structured warning. The backend stores these as
 *   { code: "component_nearly_closed", params: { component_id: 3, gap: 0.05 } }
 * The FE translates on the fly via `t('warnings.' + w.code, w.params)`.
 */
export interface AnalyzerWarning {
  code: string;
  params: Record<string, unknown>;
}

/**
 * Bounding box returned by the 3D analyzer (mesh_analyzer).
 *
 * DXF analyzer uses a flat ``[xmin, ymin, xmax, ymax]`` tuple; the 3D
 * analyzer returns this richer dict so the configurator can show
 * size/volume without parsing the bounds twice.
 */
export interface MeshBoundingBox {
  min: [number, number, number];
  max: [number, number, number];
  size: [number, number, number];
  volume_mm3: number;
}

/** 3D analyzer's overhang block (only present when computed). */
export interface OverhangMetrics {
  overhang_area_mm2: number;
  overhang_face_count: number;
  support_volume_mm3: number;
  overhang_angle_deg: number;
}

/** 3D analyzer's auto-orient block (only present when computed). */
export interface OrientationMetrics {
  rotation: number[][];
  score: number;
  metrics: Record<string, number>;
  original_score: number;
  improvement_ratio: number;
  candidate_count: number;
}

/** 3D analyzer's OBB block (only present when computed). */
export interface ObbMetrics {
  size: [number, number, number];
  center: [number, number, number];
  axes: number[][];
  volume_mm3: number;
  is_degenerate: boolean;
}

/** Per-component metrics for multi-body STL uploads. */
export interface ComponentMetrics {
  index: number;
  triangle_count: number;
  volume_mm3: number;
  surface_area_mm2: number;
  bounding_box: {
    min: [number, number, number];
    max: [number, number, number];
    size: [number, number, number];
  } | null;
}

/** Filament + print-time estimate (only when material+printer present). */
export interface PrintEstimate {
  filament_length_mm: number;
  filament_weight_g: number;
  print_time_seconds: number;
  printed_volume_mm3: number;
  layer_count: number;
  material: string;
  printer: string;
}

export interface ThinWallMetrics {
  thin_face_count: number;
  sample_count: number;
  min_wall_mm: number;
  thinnest_mm: number | null;
}

/**
 * Subset of the analyzer's full snapshot the configurator UI consumes.
 *
 * Originally DXF-only; the 3D-print module shares the same field name
 * (``Quote.analysis``) but writes a different schema. We kept all
 * legacy DXF fields as required for back-compat with existing call
 * sites, and added optional 3D fields so the type passes for both
 * paths. Discriminate on ``Quote.process_type`` (or run the
 * ``isStl3DSnapshot`` type guard below) before reading 3D-only fields.
 */
export interface AnalysisSnapshot {
  status: "OK" | "WARNING" | "ERROR" | string;
  units?: string;
  warnings: AnalyzerWarning[];
  quality_score?: number;

  // -- DXF-only analyzer facts --------------------------------------
  primitive_count?: number;
  component_count?: number;
  closed_loop_count?: number;
  outer_count?: number;
  hole_count?: number;
  total_length?: number;
  open_length?: number;
  closed_length?: number;
  /** DXF returns a flat ``[xmin, ymin, xmax, ymax]`` tuple. */
  bounding_box?: [number, number, number, number] | MeshBoundingBox | null;

  // -- 3D-print only (mesh_analyzer) --------------------------------
  format?: "stl" | "obj" | "3mf" | string;
  content_hash?: string;
  triangle_count?: number;
  volume_mm3?: number;
  signed_volume_mm3?: number;
  surface_area_mm2?: number;
  is_watertight?: boolean;
  obb?: ObbMetrics | null;
  overhang?: OverhangMetrics | null;
  orientation?: OrientationMetrics | null;
  components?: ComponentMetrics[] | null;
  estimate?: PrintEstimate | null;
  thin_walls?: ThinWallMetrics | null;
  repair?: { holes_filled: number } | null;
  timings?: Record<string, number> | null;

  // Everything else from the snapshot is allowed but we don't type it.
  [key: string]: unknown;
}

/** Type guard — true when the snapshot was produced by mesh_analyzer. */
export function isStl3DSnapshot(snap: AnalysisSnapshot): boolean {
  return (
    typeof snap.triangle_count === "number" &&
    typeof snap.volume_mm3 === "number"
  );
}

/** Structured, client-rendered interactive view produced by an out_viz module. */
export interface ViewSpec {
  view_kind: string;
  version: number;
  data: Record<string, unknown>;
}

/** Kind of part preview: 2D vector, 3D mesh, or none. */
export type PartPreviewType = "svg" | "mesh" | "none";

export interface Quote {
  // Opaque slug (~22 chars of base64url). This is the URL segment the
  // FE routes on (/quote/:quoteId) AND the handle passed to
  // /cart/items/{id}/lines. Never an autoincrement integer — the
  // backend flipped to slugs on 2026-04-23 to close an enumeration
  // hole where /quote/7 exposed a stranger's DXF.
  id: string;
  order_id: number;
  file_name: string;
  analysis: AnalysisSnapshot;
  /**
   * Presigned URL to the quote's preview, or ``null`` when this quote has
   * no preview at all — an operation started without a file
   * (``requires_file: false``) never had a blob, and a file-based one can
   * have lost it. The BE used to answer both with ``""``; KAN-770 made it
   * ``null``, because "" tempted every reader to feed it back into a
   * presign and that is what took the fileless purchase path down.
   */
  preview_url: string | null;
  /**
   * Which ProcessModule produced the analysis (the registered slug).
   * Optional for back-compat with cached responses from old BE — the
   * FE falls back to the platform default-primary module.
   */
  process_type?: string;
  /**
   * True iff a ``RepairedGeometry`` exists for this item. The
   * configurator gates ``GET /quotes/{slug}/repair`` on this flag so
   * tenants who disabled auto-repair (and clean DXFs) don't produce
   * a noisy 404 on every page mount. Optional for back-compat with
   * cached responses from old BE — falsy = "don't fetch".
   */
  has_repair?: boolean;
  /**
   * KAN-1345 — the tenant has a placed utility node that accepts this file,
   * so the explicit-consent auto-repair has something to run. The
   * configurator offers "repair automatically" only when this is true.
   */
  auto_repair_available?: boolean;
  /**
   * Horizon 2 #3 — auto-routing recommendation. Mirror of the primary
   * op's ``suggested_chain_snapshot``. When set, the configurator
   * pre-fills the chain ops list and surfaces a "Doporučeno automaticky"
   * banner the customer can dismiss with the manual-selection toggle.
   */
  suggested_chain?: SuggestedChainSnapshot | null;
  /**
   * Presigned URL to the raw mesh upload (.stl/.obj/.3mf — whatever the
   * module's analyzer parses). The cart row and project detail wrap it in
   * ``MeshPreview`` so the thumbnail matches the configurator
   * bit-for-bit. ``null`` unless the module declares
   * ``preview_type === "mesh"`` — the API branches on that capability,
   * never on the file extension.
   */
  mesh_preview_url?: string | null;
  /**
   * KAN-1401 — preview kind of THIS part. An operation may carry a 2D and a
   * 3D analyzer, so the process-level ``preview_type`` does not decide the
   * viewer; it is only the fallback when this field is absent.
   */
  preview_type?: PartPreviewType | null;
  /**
   * Interactive view-spec (out_viz) — presigned URL to the JSON the
   * ``InteractiveViewer`` renders client-side. ``is_view_only`` is true when
   * the quote carries a view-spec instead of an svg/mesh preview, so the FE
   * never feeds the raw source URL into an <img>.
   */
  view_spec_url?: string | null;
  is_view_only?: boolean;
  /** Concrete public graph node used for the primary operation. */
  template_node_id?: string | null;
  /** Customer-visible inputs configured on that graph node. */
  public_input_dimension_keys?: string[];
  /** Platform metadata for the same customer-visible inputs. */
  /**
   * „Hotový odhad" — resolved content of the homepage estimate card, built
   * BE-side from the process's ``estimate_summary_schema`` + this upload's
   * analysis snapshot. ``null``/absent = the process authors no estimate
   * card (the homepage navigates straight to the configurator).
   */
  estimate_summary?: EstimateSummary | null;
  /**
   * KAN-414 — soubor je nahraný, ale zákazník ho JEŠTĚ NEPOTVRDIL do
   * košíku. V košíku tedy není vidět a nezvyšuje počet položek; vstoupí
   * do něj až kliknutím na „Přidat do košíku".
   *
   * Na tomhle poli stojí modál při odchodu z konfigurátoru. Nedá se
   * nahradit klientským „zrovna jsem nahrál" — po reloadu stránky by
   * takový stav byl pryč, zatímco nepotvrzená položka na serveru dál
   * leží. Volitelné kvůli zpětné kompatibilitě s cachovanou odpovědí
   * staršího BE: chybí = ber jako potvrzené (dosavadní chování).
   */
  is_staged?: boolean;
  /**
   * Operace cesty této položky. Zdroj hydratace pro
   * ``OperationPathSection`` — do KAN-414 se braly z ``GET /cart``,
   * jenže nepotvrzená položka v košíku není.
   */
  operations?: QuoteOperation[];
}

/** Operace cesty tak, jak ji vrací ``GET /quotes/{slug}`` (KAN-414).
 *
 *  Vlastní tvar, ne ``CartOperation``: košíkový payload nese navíc členy,
 *  které tenhle endpoint nevrací (``fulfillment_partner_id``), a cenu vozí
 *  pod ``unit_price_czk``. Tady je to ``unit_price`` v MAJOR jednotkách
 *  měny operace — jméno bez měny, protože platforma není jednoměnová. */
export interface QuoteOperation {
  id: number;
  process_type: string;
  order_index: number;
  template_node_id: string | null;
  unit_price: number;
  config_snapshot: Record<string, unknown> | null;
  /** KAN-1700 — hodnoty interaktivních vstupů, které zákazník změnil,
   *  ``{<klíč portu in_graph>: hodnota}``. Jen na primární operaci;
   *  chybí-li klíč, platí default modulu (kontrakt ``interactive_inputs``). */
  graph_inputs?: Record<string, unknown> | null;
  /** KAN-1708 — interaktivní vstupy vyřešené pro tenhle díl (jen primárka,
   *  ``null`` = není co měnit). Z nich kreslí ovládání prohlížeč (KAN-1703). */
  interactive_inputs?: InteractiveInputState[] | null;
  /** KAN-1711 — klíče konfigurace, jejichž změna mění analýzu dílu (jen
   *  primárka, ``null`` = žádné). Živá cena podle nich díl přeanalyzuje. */
  analysis_config_keys?: string[] | null;
  /** ``null`` = dobu nikdo neautoroval (KAN-267), ne nula sekund. */
  estimated_duration_seconds: number | null;
}

/** Tělo ``POST /quotes/{slug}/reanalyze`` (KAN-1701) — přeanalýza řízená
 *  grafem operace. ``graph_inputs`` nese jen hodnoty, které zákazník právě
 *  změnil, pod klíčem ``interactive_inputs[].key`` modulu; ``null`` hodnotu
 *  resetuje na doporučenou. Prázdné tělo přeanalyzuje s aktuální konfigurací.
 *  Odpověď je ``QuoteResponse``. */
export interface ReanalyzeQuoteRequest {
  graph_inputs?: Record<string, unknown | null>;
}

/** Interaktivní vstup modulu (kontrakt ``interactive_inputs``, KAN-1702) tak,
 *  jak ho BE servíruje u primární operace dílu (KAN-1708). Port nikde natvrdo
 *  nestojí: ``key`` je z deklarace a hodnota se vrací do přeanalýzy jako
 *  ``graph_inputs[key]``. */
export interface InteractiveInputState {
  /** Klíč v ``graph_inputs`` (port na ``in_graph``). */
  key: string;
  /** Ovládací prvek z platformní palety (``quarter_turns_3d``, …). */
  control: string;
  /** Blok vizualizace, na kterém prvek sedí (``model3d``, …). */
  view_kind?: string | null;
  /** Uložená volba → doporučení (``default_from``) → default portu. */
  value?: unknown;
  /** Doporučení bez uložené volby — kam vede „doporučená" v prohlížeči. */
  default?: unknown;
  /** Popisek portu z manifestu (``analyzer_inputs[key].label_*``). */
  label_cs?: string;
  label_en?: string;
}

/** Resolved homepage estimate card (rows share the metrics-view shape). */
export interface EstimateSummary {
  fields: EstimateSummaryRow[];
  /** Gates the "cena od" row (fetched from ``POST /pricing/price-from``). */
  show_price_from: boolean;
}

export interface EstimateSummaryRow {
  key: string;
  label: string;
  value: string;
  unit?: string | null;
}

/** ``POST /pricing/price-from`` — cheapest-material price for an analyzed
 *  part. ``price_from_czk`` null = nothing priced (FE hides the row). */
export interface PriceFromResponse {
  price_from_czk: number | null;
  material_code: string | null;
  material_name: string | null;
  quantity: number;
  /** KAN-1854 — jednorázová příprava zakázky operace (není v ceně za kus);
   *  ``0`` = operace ji nemá. Minor units v ``currency_code`` (měna obchodu). */
  setup_fee_minor?: number | null;
  currency_code?: string | null;
}

// --- Admin (GET /admin/*) -------------------------------------------
//
// Shapes mirror the FastAPI Pydantic models in app/routers/admin.py.

interface AdminOverviewTopCustomer {
  email: string;
  total_spent_czk: number;
  orders_count: number;
  /** B2B ``CustomerOrg.name`` or B2C ``User.first_name + last_name``;
   * ``null`` for guests without profile fields — FE falls back to email. */
  display_name: string | null;
}

interface AdminOverviewTopMaterial {
  material_code: string;
  /** ``Material.name`` from the tenant catalog; ``null`` when the
   *  code points at a deleted catalog row (legacy orders). */
  material_name: string | null;
  revenue_czk: number;
  quantity: number;
}

interface AdminOverviewReadinessCheck {
  key: string;
  status: "ready" | "needs_attention";
  href: string;
  // Registry metadata (spec_tenant_onboarding R1a): "check" counts
  // toward completed/total, "recipe" is an optional offer and never does.
  kind: "check" | "recipe";
  track: "sell" | "operate" | "grow";
  estimated_minutes: number | null;
  // KAN-761: krok, který se splní prodejem, ne nastavením („První
  // objednávka"). Průvodce u něj místo deep-linku nabízí pozvánku
  // „vyzkoušejte si svůj obchod" a po splnění závěr rozjezdu. Příznak
  // chodí z registru schválně — klíč natvrdo ve FE by přejmenování
  // v ``services/readiness.py`` rozbilo tiše.
  is_outcome?: boolean;
  // KAN-1097: jemnější čtení než ``status``. „seeded" = krok prochází,
  // ale nad čísly ze seedu, která tenant nečetl — oranžová, ne červená,
  // a obchod na nich normálně prodává. ``status`` zůstává dvouhodnotový,
  // takže co ho vykresluje dnes, se měnit nemusí.
  seed_state?: "none" | "seeded" | "confirmed";
  // R1b: language-free wire — FE renders
  // overview.readiness.items.<key>.detail.<detail_code> with params.
  detail_code: string;
  detail_params: Record<string, string | number | null>;
  // KAN-273: offered SECOND routes to the same check — the item's own
  // ``href`` stays the default. Label token is
  // overview.readiness.alternatives.<key>. Empty for most items.
  alternatives?: { key: string; href: string }[];
  // KAN-1843: the on-demand check offered right on the item (a token the
  // FE maps to a check it knows), e.g. „pricing_dryrun" → „Zkontrolovat ceny".
  verify_action?: string | null;
}

// KAN-1098: odpověď na „Potvrdit" u jedné položky. Vrací stav, do
// kterého se položka dostala, aby volající nemusel hádat — i když ho
// dnes stejně přebije invalidace readiness dotazu.
export interface AdminReadinessConfirmResult {
  key: string;
  seed_state: "none" | "seeded" | "confirmed";
  // KAN-2011: the registry as it reads after the confirmation — written
  // straight into the readiness cache (a refetch raced the slow GET).
  readiness?: AdminReadiness | null;
}

// Lean readiness registry for the guide dock (R3a) — same shape as
// AdminOverview["readiness"], served without the KPI bundle.
export interface AdminReadiness {
  completed: number;
  total: number;
  checks: AdminOverviewReadinessCheck[];
}

/** One sample part the onboarding price page shows (KAN-1324). */
export interface AdminSamplePartOut {
  /** ``primary`` for the operation's own sample, else the part's id. */
  key: string;
  file_name: string | null;
  /** KAN-1553 — name / description in the UI language; null = not named
   *  (show ``file_name``). */
  name?: string | null;
  description?: string | null;
  /** The frozen analyzer snapshot of the part. */
  analysis: AnalysisSnapshot;
  has_preview: boolean;
  /** False only for a CTA sample whose bytes cannot be fetched. */
  downloadable: boolean;
}

export interface AdminSampleParts {
  parts: AdminSamplePartOut[];
}

// Sellability map (R2b) — one cell per priced combination, priced by
// the same engine the storefront uses.
export interface AdminSellabilityCell {
  material_code: string;
  dims: Record<string, string>;
  status: "ok" | "failed";
  error_code: string | null;
  unit_price_czk: number | null;
}

export interface AdminSellabilityProcess {
  process_type: string;
  gate_error: string | null;
  cells: AdminSellabilityCell[];
}

export interface AdminSellabilityMap {
  processes: AdminSellabilityProcess[];
  total_cells: number;
  truncated_cells: number;
}

// Pricing dry-run (KAN-742) — one representative price per (public
// process × offered material); failures carry a typed fix hint: what
// is missing (fix_code) + the admin form where it is filled (fix_href).
export interface AdminPricingDryrunRow {
  process_type: string;
  material_code: string | null;
  dims: Record<string, string>;
  status: "ok" | "failed";
  error_code: string | null;
  error_params: Record<string, string | number>;
  fix_code: string | null;
  fix_href: string | null;
  // Minor-unit + currency pair (KAN-284): render via minorToMajor.
  unit_price_minor: number | null;
  currency_code: string | null;
  /** KAN-1854 — konfigurace, pro kterou cena platí: osy buňky pojmenované
   *  z definic dimenzí (jazyk admina) a nacenované množství. */
  axes?: AdminPricingDryrunAxis[];
  quantity?: number;
}

export interface AdminPricingDryrunAxis {
  key: string;
  label: string;
  value: string;
  /** ``null`` když ``value`` je pojmenovaná hodnota (už úplná, „3 mm"). */
  unit: string | null;
}

export interface AdminPricingDryrunResponse {
  total: number;
  failed: number;
  rows: AdminPricingDryrunRow[];
}

// „Cena procesu" (KAN-746) — setup mirror of „Proč tato cena?": every
// formula variable with its origin, current value and (when missing) a
// typed fix hint pointing at the form that sets it.
export interface AdminPriceMapVariable {
  source: string;
  origin:
    | "rate"
    | "material_cost"
    | "material"
    | "machine"
    | "analysis"
    | "config"
    | "price"
    | "duration"
    | "operation"
    | "rate_table"
    | "order";
  label: string | null;
  unit: string | null;
  value: string | null;
  missing: boolean;
  fix_code: string | null;
  fix_href: string | null;
  // KAN-1351 — where the input is determined and how it enters the price.
  // ``category`` groups the form (machine / material / tenant_rate /
  // rate_table / operation / order / measured / customer / derived / other);
  // ``via`` is the derived leaf it is read through; ``share`` (0..1) the part
  // of the raw per-piece price that disappears without it; ``factors`` the
  // leaves it is multiplied or divided with on the way to ``feeds``.
  // Optional in the type (the BE always sends them) so existing literals in
  // tests that predate KAN-1351 need no update.
  category?: string;
  required?: boolean;
  via?: string | null;
  target?: AdminPricingLabWriteTarget | AdminPriceMapExtraTarget | null;
  readonly_reason?: string | null;
  share?: number | null;
  feeds?: string[];
  factors?: string[];
  params?: Record<string, unknown>;
}

/** Records the price map names that the pricing lab does not (KAN-1351). */
export interface AdminPriceMapExtraTarget {
  kind: "material_cost" | "setup_fee";
  record_id: number | null;
  field: string;
  record_label: string;
  href: string | null;
}

export interface AdminProcessPriceMap {
  ok: boolean;
  error_code: string | null;
  process_type: string;
  material_code: string | null;
  sample_name: string | null;
  formula_spec: Record<string, unknown> | null;
  markup: string | null;
  variables: AdminPriceMapVariable[];
  price_ok: boolean;
  price_error_code: string | null;
  price_error_params: Record<string, string | number>;
  price_fix_code: string | null;
  price_fix_href: string | null;
  unit_price_minor: number | null;
  /** KAN-1528 — součet sazeb PŘED přirážkou. Vedle výsledné ceny, ať se
   *  přirážka nedopočítává dělením (po zaokrouhlení je to jiné číslo). */
  subtotal_minor: number | null;
  /** KAN-1854 — jednorázová příprava zakázky (v ``unit_price_minor`` není). */
  setup_fee_minor?: number | null;
  /** KAN-1854 — 1 ks celkem včetně přípravy (``line_total`` řádku vzorku). */
  line_total_minor?: number | null;
  currency_code: string | null;
  /** KAN-2012 — co přesně se nacenilo: díl, počet kusů a varianta. */
  sample_key?: string | null;
  quantity?: number;
  dimensions?: Record<string, unknown>;
  /** KAN-2012 — rozpad ceny za kus (po přirážce) podle složek vzorce. */
  breakdown?: AdminPriceMapBreakdownRow[];
  /** KAN-2012 — všechny materiály operace s nákupní cenou. */
  materials?: AdminPriceMapMaterial[];
}

export interface AdminPriceMapBreakdownRow {
  key: string;
  label: string | null;
  amount_minor: number;
}

export interface AdminPriceMapMaterial {
  material_id: number;
  material_code: string;
  /** Nákupní cena celého materiálu (bez variant); null = nezadaná. */
  cost: string | null;
  cost_uom: string | null;
  /** Kolik variant má vlastní (odlišnou) nákupní cenu. */
  variant_costs: number;
}

// Cenová laboratoř (KAN-1072) — the tenant's own recent parts, broken into
// the rows their formula produces, with an override channel that previews a
// change before anything is saved. The lab stores nothing: every movable
// number carries the record an edit lands in, and the save goes to the
// endpoint that already owns that record.
export interface AdminPricingLabWriteTarget {
  kind:
    | "resource_rate"
    | "machine_config"
    | "rate_table_rate"
    | "rate_table_markup"
    | "tenant_rate"
    // KAN-1350 — the rate book row of a group of identical machines.
    | "resource_rate_group";
  record_id: number | null;
  field: string;
  // The record in the tenant's own words ("Fiber laser 3 kW"), not its id.
  record_label: string;
  href: string | null;
  // A record named by a key rather than an id — the machine group (KAN-1350).
  record_key?: string | null;
}

export interface AdminPricingLabVariable {
  source: string;
  process_type: string;
  value: string | null;
  unit: string | null;
  label: string | null;
  // Mutually exclusive with readonly_reason: a value either names the record
  // it writes into, or says why it cannot. There is no third state.
  target: AdminPricingLabWriteTarget | null;
  readonly_reason:
    "derived" | "measured" | "customer_input" | "no_record" | null;
}

export interface AdminPricingLabRow {
  key: string;
  // Admin-authored (formula output name / component label); null = the
  // formula names nothing here, so the raw key is shown.
  label: string | null;
  amount_minor: number;
  // false = the setup fee, charged once per batch, not per piece.
  per_piece: boolean;
}

export interface AdminPricingLabPart {
  operation_id: number;
  label: string;
  process_type: string;
  material_code: string;
  quantity: number;
  rows: AdminPricingLabRow[];
  unit_price_minor: number;
  setup_fee_minor: number;
  line_total_minor: number;
  currency_code: string;
  markup: string;
  rate_table_id: number | null;
  // What the customer was CHARGED. Frozen, never recomputed — shown beside
  // today's repriced baseline so a divergence is visible, not averaged away.
  frozen_unit_price_minor: number | null;
  simulated_unit_price_minor: number | null;
  simulated_line_total_minor: number | null;
  simulated_error: string | null;
  /** KAN-2123 — klíče měněných hodnot, které cenu dílu hýbou; ``null`` = nic se nemění. */
  affected_by?: string[] | null;
}

export interface AdminPricingLabSimulation {
  simulation: boolean;
  sample_size: number;
  priced: number;
  failed: number;
  median_shift_pct: string | null;
  min_shift_pct: string | null;
  max_shift_pct: string | null;
}

export interface AdminPricingLab {
  ok: boolean;
  error_code: string | null;
  parts: AdminPricingLabPart[];
  variables: AdminPricingLabVariable[];
  simulation: AdminPricingLabSimulation | null;
}

// Readiness probe (spec_tenant_onboarding R2a) — dry-run of the
// customer order chain; first failure carries a typed code + deep link.
export interface AdminReadinessProbeRequest {
  process_type?: string | null;
  material_code?: string | null;
}

export interface AdminReadinessProbeStep {
  key: string;
  status: "passed" | "failed" | "skipped";
  href: string;
  error_code: string | null;
  // Language-free (KAN-78): passed-step facts as a code + params,
  // rendered via overview.probe.details.<detail_code>. Failure data
  // (violation params, uncovered countries…) rides in detail_params.
  detail_code: string | null;
  detail_params: Record<string, string | number>;
  // KAN-745: typed fix hint on pricing failures — what is missing
  // (fix_code, i18n token) + where it is filled in (fix_href).
  fix_code?: string | null;
  fix_href?: string | null;
}

export interface AdminReadinessProbeResponse {
  ready: boolean;
  process_type: string | null;
  material_code: string | null;
  sample_name: string | null;
  first_failure: string | null;
  steps: AdminReadinessProbeStep[];
}

interface AdminOverviewActionBucket {
  key: string;
  // KAN-690: verdikt bez jazyka — větu skládá FE z
  // ``overview.checks.<key>.detail.<detail_code>``.
  detail_code: string;
  detail_params: Record<string, string | number>;
  count: number;
  href: string;
  tone: "neutral" | "warning";
}

interface AdminOverviewLowStockItem {
  material_id: number;
  material_code: string;
  material_name: string;
  dimensions: Record<string, unknown>;
  stock_uom: string;
  qty_available: string;
  low_stock_threshold: string;
  suggested_qty_order: string;
}

interface AdminOverviewPoAlert {
  id: number;
  po_number: string;
  supplier_name: string;
  status: string;
  expected_delivery_date: string | null;
}

interface AdminOverviewFailedQuote {
  ts: string;
  /** Same error code the customer's response carried. */
  reason: string;
  process_type: string | null;
  file_name: string | null;
  /** Handle to quote when reporting the failure. */
  request_id: string | null;
  /** KAN-1294 — set on a pricing failure (no file there, but a material). */
  material_code?: string | null;
}

interface AdminOverviewCapacityProcess {
  process_type: string;
  machines: number;
  /** Supply: summed weekly schedules of the machines running this
   *  process. */
  weekly_hours: number;
  /** Demand: estimated hours of queued work aimed at this process —
   *  the same durations the global ``planned_capacity_horizon_hours``
   *  makespan is scheduled from. */
  planned_hours: number;
  /** ``planned_hours`` against ONE week of ``weekly_hours``, so 120 %
   *  means "over a week of work is queued". ``null`` — not 0 — when the
   *  process has no scheduled capacity, because a share of nothing is
   *  undefined and a 0 % bar would read as "idle". */
  load_pct: number | null;
  /** Queued steps for this process with no duration estimate. They
   *  contribute nothing to ``planned_hours``, so any non-zero value
   *  makes the bar a LOWER bound — render the caveat or don't render
   *  the bar. */
  unestimated_operations: number;
  /** KAN-2098 — stroje operace po jednom: rezervované kroky a běžící odstávka. */
  machine_rows?: Array<{
    machine_id: number;
    name: string;
    weekly_hours: number;
    planned_steps: number;
    down_now: boolean;
  }>;
}

/** One equal-width slice of the resolved window (KAN-688).
 *
 *  Values are per bucket, not cumulative — accumulate on the FE where a
 *  running total is wanted. ``bucket_days`` is constant across the
 *  series and widens with the window so the list stays bounded.
 *
 *  ⚠️ ``_base_major``, not ``_czk``: major units of the books base,
 *  same as the sibling scalars, without spreading the currency into a
 *  new wire name. */
interface AdminOverviewTrendBucket {
  bucket_start: string;
  bucket_days: number;
  orders_paid: number;
  revenue_base_major: number;
  profit_base_major: number;
  margin_pct: number;
}

export interface AdminOverviewParams {
  /** Preset window length. Sent when no explicit range is set. */
  window_days?: number;
  /** Explicit ISO-8601 range (overrides ``window_days`` server-side). */
  from?: string;
  to?: string;
}

export interface AdminOverview {
  /** Resolved window length in days. When the operator picked an
   * explicit ``from`` / ``to`` range this is the day-delta, not the
   * preset they asked for. */
  window_days: number;
  window_from: string | null;
  window_to: string | null;
  orders_total: number;
  orders_by_status: Record<string, number>;
  revenue_czk_all_time: number;
  revenue_czk_window: number;
  /** Profit derived from per-line ``markup`` snapshotted in
   * ``price_breakdown`` at line creation. Historical orders reflect
   * the markup that was in effect at pricing time, not today's. */
  profit_czk_window: number;
  avg_margin_pct_window: number;
  /** Real material margin from finalize-time cost snapshots (P1
   * ceník+sklad → COGS). Unlike the markup-derived profit above this
   * uses the captured acquisition cost (manual → WAC). ``null`` when
   * no line in the window carries a cost snapshot. */
  material_margin_window: {
    revenue_covered_czk: number;
    material_cost: number;
    margin_czk: number;
    margin_pct: number;
    lines_covered: number;
    lines_total: number;
  } | null;
  /** Distributional stats — ``null`` when the window has zero paid
   * orders. FE renders an em-dash in that case. */
  avg_order_value_czk: number | null;
  avg_part_area_m2: number | null;
  avg_order_weight_kg: number | null;
  top_materials: AdminOverviewTopMaterial[];
  top_customers: AdminOverviewTopCustomer[];
  /** Phase J: per-process_type KPI breakdown for the window. One
   *  entry per process_type that produced any operation; sorted
   *  by op_count desc. ``surcharge_revenue_czk`` is the sum of
   *  each chain op's stamped ``unit_price_czk`` (zero for the
   *  the primary today since chain ops are where surcharges
   *  accrue). */
  by_process_type: Array<{
    process_type: string;
    op_count: number;
    surcharge_revenue_czk: number;
  }>;
  /** The window sliced into equal buckets, oldest first. Always at
   *  least one entry; empty slices are present and zero. */
  trend: AdminOverviewTrendBucket[];
  readiness: {
    completed: number;
    total: number;
    checks: AdminOverviewReadinessCheck[];
  };
  action_center: {
    buckets: AdminOverviewActionBucket[];
    low_stock_items: AdminOverviewLowStockItem[];
    overdue_purchase_orders: AdminOverviewPoAlert[];
    capacity_by_process: AdminOverviewCapacityProcess[];
    /** Public quote uploads that ended in an error, newest first. Optional
     *  so a page served during a deploy skew (FE ahead of API) renders the
     *  rest of the dashboard instead of blanking on a missing key. */
    failed_quotes?: AdminOverviewFailedQuote[];
    /** KAN-2022 — výroba zastavená hlášením z terminálu (důvod, kdo, kdy),
     *  nejstarší první. Volitelné kvůli rozjetému deployi FE/API. */
    stopped_steps?: AdminOverviewStoppedStep[];
  };
}

/** KAN-2022 — krok zastavený hlášením z terminálu. */
export interface AdminOverviewStoppedStep {
  order_number: string;
  public_number: string | null;
  problem_label: string;
  note: string | null;
  reported_by: string | null;
  reported_at: string;
}

interface AdminOrderRow {
  order_id: number;
  order_number: string;
  uid: string | null;
  /** KAN-1430 — veřejné číslo objednávky ``xxx-xxx-xxx``; zobrazuje ho ``displayId``. */
  public_number: string | null;
  status: string;
  /** MAJOR units of the order's BOOKS BASE — the `_czk` suffix is a legacy
   *  wire name, not the denomination. */
  total_czk: number;
  /** DISPLAY currency the buyer transacted in, frozen on the row. */
  currency: string;
  /** Frozen display → books-base rate. With `currency` it is what renders
   *  `total_czk` in the currency the order was placed in (KAN-558). */
  fx_rate_to_base: number;
  customer_email: string | null;
  /** KAN-2021 — jméno a firma z adresy objednávky (fakturační, jinak
   *  doručovací); ``null`` = adresa je nenese. */
  customer_name?: string | null;
  customer_company?: string | null;
  country_code: string | null;
  /** KAN-992 — číslo objednávky ODBĚRATELE. Vyhledávání ``q`` ho bere
   *  taky, takže obchodník najde objednávku podle čísla, které mu
   *  nákupčí nadiktuje. Volitelné: starší API klíč neposílá. */
  customer_reference?: string | null;
  created_at: string;
  paid_at: string | null;
  /** Customer typed a free-text note (order-level or on any part) —
   *  the list renders a 💬 indicator so the operator opens the detail. */
  has_customer_note?: boolean;
  /** KAN-748 — testovací objednávka (badge TEST; mimo tržby/provize/statistiky). */
  is_test?: boolean;
}

export interface AdminOrdersPage {
  total: number;
  page: number;
  page_size: number;
  items: AdminOrderRow[];
}

// --- Admin: customer detail (PR #74) ---------------------------------

interface AdminCustomerOrderRow {
  order_number: string;
  uid: string | null;
  /** KAN-1430 — veřejné číslo objednávky ``xxx-xxx-xxx``; zobrazuje ho ``displayId``. */
  public_number: string | null;
  status: string;
  /** MAJOR units of the order's BOOKS BASE. */
  total_czk: number;
  /** DISPLAY currency the buyer transacted in, frozen on the row. */
  currency: string;
  /** Frozen display → books-base rate (KAN-558). */
  fx_rate_to_base: number;
  created_at: string;
  paid_at: string | null;
  /** KAN-1685 — obchod, ve kterém objednávka vznikla (čísla jsou řada per obchod). */
  shop_name: string | null;
}

interface AdminCustomerTopMaterial {
  material_code: string;
  /** Human label for the material; falls back to the code server-side.
   *  The code itself is an internal join key and is not shown. */
  material_display_name?: string;
  revenue_czk: number;
  quantity: number;
}

export interface AdminCustomerDetail {
  id: number;
  email: string;
  preferred_lang: string;
  preferred_currency: string;
  created_at: string;
  /** Free-text note only visible to admins. Shown on the admin
   * customer detail page; never exported in GDPR zip. */
  admin_note: string | null;
  deleted_at: string | null;
  // Profile fields
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  company: string | null;
  is_platform_admin: boolean;
  is_2fa_enabled: boolean;
  orders_count: number;
  /** KAN-1685 — z toho zaplacených; utraceno i průměr se počítají jen z nich. */
  paid_orders_count: number;
  total_spent_czk: number;
  avg_order_value_czk: number;
  last_order_at: string | null;
  top_materials: AdminCustomerTopMaterial[];
  recent_orders: AdminCustomerOrderRow[];
  /** Shipping + billing pulled from the customer's most recent
   * non-draft order. Null when the customer has never placed a
   * non-draft order. ``latest_billing_address`` is also null when
   * the customer checked "same as shipping" on their most recent
   * order (nothing was persisted in the billing column). */
  latest_shipping_address: Address | null;
  latest_billing_address: Address | null;
}

export interface AdminCustomerUpdateRequest {
  admin_note?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  company?: string | null;
  preferred_lang?: string;
  preferred_currency?: string;
  is_platform_admin?: boolean;
}

// PR13 cleanup: `AdminCreateUserRequest` smazán — POST /admin/users
// endpoint odstraněn (AdminCustomers create-modal pryč v PR10c).

// --- Admin customer address book ------------------------------------
//
// One ``CustomerAddress`` row per saved entry, distinguished by
// ``created_by_admin``: True = admin-authored (editable here, hidden
// from the customer's /account/addresses), False = customer-authored
// (read-only in admin UI). Default-shipping / default-billing flags
// only apply to customer-authored rows.

export interface AdminCustomerAddress {
  id: number;
  label: string;
  address: Address;
  is_default_shipping: boolean;
  is_default_billing: boolean;
  created_by_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminCustomerAddressCreate {
  label: string;
  address: Address;
}

export interface AdminCustomerAddressUpdate {
  label?: string;
  address?: Address;
}

// --- Admin: item operations (Phase J PR 23 + PR 35) -----------------
//
// The chain of operations attached to each OrderItem. Admin can
// reassign the partner per-op and record state-transition events
// (assigned / started / completed / blocked / note). The kanban
// list shape is lighter — drops the event timeline so cross-order
// pages render hundreds of cards without a full event load.

export interface AdminOperationEvent {
  id: number;
  event_type: string;
  payload: Record<string, unknown> | null;
  actor_user_id: number | null;
  created_at: string;
}

export interface AdminOperation {
  id: number;
  order_item_slug: string;
  /** KAN-1409 — páruje operaci s ``OrderItem.id`` na detailu objednávky. */
  order_item_id?: number;
  file_name: string;
  process_type: string;
  operation_label?: string;
  template_node_id?: string | null;
  order_index: number;
  unit_price_czk: number;
  // KAN-269: null = dobu nikdo neautoroval, 0 = autorovana nula.
  // Do KAN-269 sem BE posilal `or 0`, takze oba stavy vypadaly jako
  // "0 s" a nebylo poznat, ktery krok jeste ceka na odhad.
  estimated_duration_seconds: number | null;
  /** KAN-2185 — set-up operace (jednou za dávku, ne za kus); ``null`` =
   *  neautorováno. Odhad dílu = set-up + čas na kus × kusy, jako plán. */
  estimated_setup_seconds?: number | null;
  /** KAN-1478 — PROČ odhad chybí, když je `estimated_duration_seconds` null.
   *  `error_code` + `params` ve slovníku enginu; `params.var` je list, na
   *  který čeká vzorec doby TÉ operace (platforma si klíč nevymýšlí),
   *  `params.fix_code` / `params.fix_href` jen tam, kde to tenant doplní ve
   *  formuláři. `null` = není co hlásit — což NENÍ autorovaná nula. */
  duration_missing_reason?: {
    error_code: string;
    params: Record<string, unknown>;
  } | null;
  fulfillment_partner_id: number | null;
  fulfillment_partner_code: string | null;
  /** Human label; the code is the partner's natural key, not a caption. */
  fulfillment_partner_name?: string | null;
  // Horizon 1 #4 traceability lite — populated when admin sets per-op
  // machine + operator (legacy ops keep these NULL).
  machine_id?: number | null;
  machine_name?: string | null;
  operator_user_id?: number | null;
  operator_email?: string | null;
  /**
   * Artifact I/O kontrakty — akumulovaný stav artefaktu po této operaci
   * ({property_key: value}). Absent/null = legacy operace bez snapshotu;
   * FE pak sekci historie artefaktu skryje.
   */
  artifact_state_snapshot?: Record<string, unknown> | null;
  /**
   * KAN-1409 — geometrie dílu podle analyzátoru operace. Obrys má tolik
   * os, kolik jich analyzátor změřil (plech 2, mesh 3); objem jen když
   * ho analyzátor dodal. ``null`` = operace nemá snímek analýzy.
   */
  envelope_mm?: number[] | null;
  volume_mm3?: number | null;
  /** Počet zmrazených varování vyrobitelnosti; ``null`` = kontrola neproběhla. */
  manufacturability_warning_count?: number | null;
  events: AdminOperationEvent[];
}

export interface AdminKanbanOperation {
  id: number;
  order_number: string;
  uid: string | null;
  /** KAN-1660 — veřejné číslo; zobrazuj přes ``displayId``. */
  public_number?: string | null;
  order_item_slug: string;
  file_name: string;
  process_type: string;
  operation_label?: string;
  order_index: number;
  /** Uzel grafu za operací — tabule podle něj řadí karty na pracoviště. */
  template_node_id?: string | null;
  /** KAN-237 — z persistovaného snapshotu objednávky; null = bez časování. */
  on_critical_path?: boolean | null;
  earliest_start_s?: number | null;
  fulfillment_partner_id: number | null;
  fulfillment_partner_code: string | null;
  /** Human label; the code is the partner's natural key, not a caption. */
  fulfillment_partner_name?: string | null;
  /** KAN-2009 — ``internal`` = vlastní dílna (karta ukáže stroj). */
  fulfillment_partner_kind?: string | null;
  latest_event_type: string | null;
  latest_event_at: string | null;
  /** KAN-2009 — krok výroby operace a jeho stav (``blocked`` = stojí). */
  work_item_id?: number | null;
  work_item_state?: string | null;
  /** KAN-2009 — stroj z rezervace kroku. */
  machine_label?: string | null;
  /** KAN-2098 — id stroje z rezervace; tabule „podle pracoviště" dělá sloupec na stroj. */
  machine_id?: number | null;
  /** KAN-2098 — objednávka zaplacená; nezaplacená karta nenabízí Začít/Dokončit. */
  order_paid?: boolean;
  /** KAN-2009 — proč krok stojí (hlášení z terminálu: druh, poznámka, kdo, kdy). */
  blocked_reason?: string | null;
  problem_label?: string | null;
  problem_note?: string | null;
  problem_reported_by?: string | null;
  problem_reported_at?: string | null;
}

export interface AdminOperationUpdate {
  fulfillment_partner_id?: number | null;
  // Horizon 1 #4 — explicit ``null`` clears, absent leaves untouched
  // (mirrors backend ``model_fields_set`` semantics).
  machine_id?: number | null;
  operator_user_id?: number | null;
}

export interface RecordOperationEventRequest {
  event_type: string;
  payload?: Record<string, unknown> | null;
}

// --- Admin: manufacturing partners (Phase J PR 22 + PR 34) ----------
//
// Partners are the vendors a process module can route to (a primary
// → the outsourced vendor, finish → ``in_house``). The admin UI lets ops
// add / rename / retire partners and pull a per-partner CSV of the
// open work queue.

export interface CapabilityDuration {
  /** Doba odeslání materiálu/dat partnerovi (dny). ``null`` = neuvedeno. */
  dispatch_days: number | null;
  /** Doba dodání hotových dílů zpět (dny). ``null`` = neuvedeno. */
  delivery_days: number | null;
  /** Free-text labely materiálů/procesů, které partner pro tuto
   * schopnost zvládá (např. ``["ocel S235", "nerez 1.4301"]``).
   * Display-only — propojení s ``Material`` katalogem je samostatný
   * feature. Empty/missing pole znamená neuvedeno. */
  materials: string[];
}

/** Per-capability rate card. Read by the partner_assignment ranker
 * during quote creation to pick the cheapest fitting partner. */
export interface CapabilityPricing {
  /** Hodinová sazba (Kč/h) pro tuto schopnost. ``null`` = neuvedeno. */
  hourly_rate_czk: number | null;
  /** Paušální setup poplatek (Kč) přičtený před aplikací minima. */
  setup_fee_czk: number | null;
  /** Minimální fakturovaná cena za kus (Kč) — propočet níže se zvedne. */
  minimum_charge_czk: number | null;
}

/** One volume-discount step of a shop's capacity credit terms
 * (KAN-1762): ``discount_pct`` off from ``min_credit`` up — whole units
 * of the shop's base currency. */
export interface CapacityDiscountTier {
  min_credit: number;
  discount_pct: number;
}

/** A shop's terms for prepaid capacity credit (KAN-1762). Tenant-only —
 * no platform default: a shop without terms does not sell credit.
 * ``validity_days`` ``null`` = bought credit does not expire. */
export interface CapacityPricing {
  discount_tiers: CapacityDiscountTier[];
  validity_days: number | null;
}

/** What ``GET /admin/tenant-capacity-pricing`` answers. ``own`` ``null``
 * = the shop does not sell credit; ``currency_code`` = its base currency,
 * the denomination of ``min_credit``. */
export interface TenantCapacityPricing {
  own: CapacityPricing | null;
  currency_code: string;
  /** KAN-2118 — tarif prodej kreditu zahrnuje (``capacity_contracts``). */
  plan_allows: boolean;
}

/** One prepaid credit contract (KAN-1762). Amounts are MINOR units of
 * ``currency_code``. */
export interface CapacityContract {
  id: number;
  customer_org_id: number;
  provider_org_id: number;
  credit_minor: number;
  remaining_minor: number;
  discount_pct: number;
  price_minor: number;
  currency_code: string;
  valid_from: string;
  valid_until: string | null;
  status: "pending" | "active" | "exhausted" | "expired" | "cancelled";
  source: "stripe" | "manual";
  advance_invoice_id: number | null;
  created_at: string;
}

/** One signed movement of prepaid credit. */
export interface CapacityCreditEntry {
  id: number;
  contract_id: number;
  kind:
    | "purchase"
    | "opening"
    | "draw"
    | "reversal"
    | "expiry"
    | "cancellation"
    | "guarantee_forfeit";
  amount_minor: number;
  currency_code: string;
  order_id: number | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

export interface CapacityCurrencyBalance {
  currency_code: string;
  balance_minor: number;
}

/** ``GET /capacity/customers/{id}/credit`` (shop admin). */
export interface CustomerCapacityCredit {
  customer_org_id: number;
  balances: CapacityCurrencyBalance[];
  contracts: CapacityContract[];
  entries: CapacityCreditEntry[];
}

/** ``GET /customer-capacity/balance`` (the buyer's own view). */
export interface BuyerCapacityBalance extends CustomerCapacityCredit {
  customer_org_name: string;
  purchase_available: boolean;
}

/** One period of a guaranteed capacity (KAN-1762 3/3). Hours. */
export interface CapacityGuaranteePeriod {
  period_start: string;
  period_end: string;
  allotted_hours: number;
  carried_in_hours: number;
  used_hours: number;
  remaining_hours: number;
  /** The period's close, once it has ended: expired hours and what they
   * cost — MINOR units of ``forfeit_currency``. Absent while it runs. */
  expired_hours?: number | null;
  forfeit_minor?: number | null;
  forfeit_drawn_minor?: number | null;
  forfeit_unpaid_minor?: number | null;
  forfeit_currency?: string | null;
  forfeit_reason?: "no_sell_rate" | "fx_missing" | "vat_rate_missing" | null;
  /** The monthly invoice that billed the unpaid part, once billed. */
  forfeit_invoice_id?: number | null;
}

/** Guaranteed machine capacity of one B2B partner (KAN-1762 3/3). */
export interface CapacityGuarantee {
  id: number;
  customer_org_id: number;
  machine_id: number | null;
  resource_key: string | null;
  machine_names: string[];
  hours_per_period: number;
  period: "week" | "month";
  unused_policy: "expire" | "roll_over";
  release_days_before: number;
  /** % of the machine's sell rate charged per expired hour. */
  forfeit_pct: number;
  valid_from: string;
  valid_until: string | null;
  status: "active" | "cancelled";
  periods: CapacityGuaranteePeriod[];
}

/** ``GET /capacity/customers/{id}/guarantees`` (shop admin). */
export interface CustomerCapacityGuarantees {
  customer_org_id: number;
  items: CapacityGuarantee[];
  machines: { machine_id: number; name: string; resource_key: string | null }[];
  groups: { resource_key: string; machine_names: string[] }[];
}

/** ``GET /customer-capacity/guarantees`` (the buyer's own view). */
export interface BuyerCapacityGuarantees {
  customer_org_id: number;
  items: CapacityGuarantee[];
}

/** ``GET /customer-capacity/quote`` — MINOR units of ``currency_code``. */
export interface CapacityQuote {
  credit_minor: number;
  discount_pct: number;
  price_minor: number;
  currency_code: string;
  validity_days: number | null;
  discount_tiers: CapacityDiscountTier[];
}

/** One tenant in ``GET /admin/platform/setting-overrides`` (KAN-1365). */
export interface SettingOverridesTenant {
  org_id: number;
  slug: string;
  name: string;
  status: string;
}

/** A tenant that does NOT inherit one layered setting.
 *
 * ``fields`` lists what it overrides for a ``field``-granular key; it is
 * ``null`` for a ``document`` key, where the tenant's row replaces the
 * platform default whole. */
export interface SettingOverride {
  org_id: number;
  fields: string[] | null;
}

export interface SettingOverridesEntry {
  key: string;
  granularity: "document" | "field";
  /** Only the tenants that deviate — every other tenant inherits. */
  overrides: SettingOverride[];
}

/** Which tenants override which platform default. Reports WHETHER, never
 * the values. */
export interface SettingOverridesReport {
  tenants: SettingOverridesTenant[];
  settings: SettingOverridesEntry[];
}

export interface AdminPartner {
  id: number;
  code: string;
  name: string;
  /** ``"internal"`` = the tenant's own production facility (one per
   * tenant, auto-created); ``"external"`` = a subcontractor. */
  kind: "internal" | "external";
  capabilities: string[];
  /** Per-capability lead-time bundle keyed by capability token —
   * subset of ``capabilities``; missing key = neuvedeno. */
  capability_durations: Record<string, CapabilityDuration>;
  /** Per-capability rate card keyed by capability token. Subset of
   * ``capabilities``; missing key = bez ceníku (suggester přepadne na
   * deterministický kind+id tiebreak). */
  capability_pricing: Record<string, CapabilityPricing>;
  contact_info: Record<string, unknown> | null;
  active: boolean;
  /** ISO-8601 timestamp; surfaced on the detail page hero/meta strip. */
  created_at: string;
  updated_at: string;
  /** Distinct count of open orders (paid → ready_to_ship) that route
   * at least one operation to this partner. Populated only by
   * ``GET /admin/partners/{id}`` (detail view); ``GET /admin/partners``
   * leaves it ``null`` to keep the list endpoint single-query. */
  open_orders_count: number | null;
  /** Subset of ``open_orders_count`` past their promised delivery date
   * (``paid_at + delivery_selection.days_calendar < now``). Orders
   * without a paid_at / delivery snapshot count as open but not
   * overdue. ``null`` when not computed (list endpoint). */
  overdue_orders_count: number | null;
}

export interface AdminPartnerCreate {
  /** Optional — server auto-slugs from ``name`` and resolves
   * collisions within the tenant. */
  code?: string;
  name: string;
  capabilities?: string[];
  capability_durations?: Record<string, CapabilityDuration>;
  capability_pricing?: Record<string, CapabilityPricing>;
  contact_info?: Record<string, unknown> | null;
}

export interface AdminPartnerUpdate {
  name?: string;
  capabilities?: string[];
  capability_durations?: Record<string, CapabilityDuration>;
  capability_pricing?: Record<string, CapabilityPricing>;
  contact_info?: Record<string, unknown> | null;
  active?: boolean;
}

export interface PartnerAddress {
  id: number;
  kind: string;
  label: string;
  street: string;
  city: string;
  postal_code: string;
  country: string;
  is_default: boolean;
}

export interface PartnerAddressCreate {
  kind: string;
  label: string;
  street: string;
  city: string;
  postal_code: string;
  country?: string;
  is_default?: boolean;
}

export interface PartnerAddressUpdate {
  kind?: string;
  label?: string;
  street?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  /** ``true`` demotes every other address of the same ``kind`` for
   * this partner — the BE enforces "max one default per kind". */
  is_default?: boolean;
}

// --- Admin: order-limit guardrails (PR #76) --------------------------
//
// Cart/checkout knobs stored in the ``settings`` key-value table.
// Every field is ``null``-able on the wire; ``null`` means "no limit
// on this axis". The admin UI renders ``null`` as an empty input box
// and sends ``null`` back to clear a limit. Upload-time
// part-dimension limits used to live here too — they're now derived
// from machine.constraints (see /admin/machines).

export interface AdminOrderLimits {
  max_qty_per_line: number | null;
  max_items_per_order: number | null;
  min_subtotal_czk: number | null;
  /** Dorovnání (KAN-1428): objednávka pod touto částkou (zboží + příplatky,
   *  bez dopravy) se dopočítá do ní. ``null`` = žádné dorovnání. */
  minimum_order_value: number | null;
  max_subtotal_czk: number | null;
  max_total_weight_kg: number | null;
}

export type AdminOrderLimitsUpdate = Partial<AdminOrderLimits>;

// --- Admin: shipping carriers + rates (PR #75) -----------------------
//
// Carriers are curated (seed rows + optional renames). The admin only
// flips ``is_active`` / ``sort_order`` / ``name`` — ``code`` is the
// stable FK referenced by ``shipping_rates.carrier_id`` and stamped
// into order notes.
//
// Rates are the (carrier × country × weight band → price) table. A
// soft-delete (``is_active=false``) keeps historical orders' locked-in
// ``shipping_czk`` lookups intact while pulling the row out of the
// checkout quote loop.

/** ``GET/PUT /admin/personal-pickup`` — osobní odběr jako způsob doručení
 *  obchodu (KAN-1605). Místo a hodiny jsou jen ke čtení: odvozují se
 *  z Profilu firmy (adresa odběru, jinak sídlo). */
export interface AdminPersonalPickup {
  settings: {
    enabled: boolean;
    /** Minor jednotky ``currency_code`` (KAN-284). */
    price_minor: number;
    /** Základní měna tenanta; ``null``, dokud se nastavení neuložilo. */
    currency_code: string | null;
  };
  location: OrderPickup;
}

export interface AdminPersonalPickupUpdate {
  enabled: boolean;
  price_minor: number;
}

/** KAN-1901 — výchozí dodací lhůta materiálu obchodu v pracovních dnech.
 * ``null`` = nevyplněná: košík s chybějícím materiálem bez vlastní lhůty
 * pak nenabídne rychlé úrovně a termín jen „potvrdí". */
export interface AdminMaterialDefaultLeadTime {
  business_days: number | null;
}

export interface AdminCarrier {
  id: number;
  code: string;
  name: string;
  /** Optional English display-name override (NULL ⇒ English customers fall
   * back to ``name``). Authored alongside ``name`` via I18nField. */
  name_en: string | null;
  /** Název per jazyk platformy (KAN-1804); ``name`` / ``name_en`` je zrcadlo. */
  name_i18n?: Record<string, string>;
  is_active: boolean;
  sort_order: number;
  /** Preset key (``slate``/``blue``/``emerald``/``amber``/``red``/
   * ``violet``) or ``#RRGGBB`` hex. NULL = default slate tint. */
  color: string | null;
  /** Optional uploaded logo URL — when set, replaces the colour
   * badge in the carrier list. Uploaded via ``POST /admin/uploads/
   * image`` which returns a public asset URL. */
  logo_url: string | null;
  /** Tracking-link pattern, ``{code}`` → parcel number (KAN-1605). NULL =
   * no public tracker — which today also marks personal pickup. */
  tracking_url_template: string | null;
  /** "Počet dní navíc pro dopravu" — transit days this carrier adds
   * to the customer's promised delivery time (on top of country +
   * matched-rate extra days). */
  extra_days: number;
  /** ISO timestamp from the BE — feeds the "Aktualizace" stat in
   * the merged Doprava admin header. */
  updated_at: string;
  /** Aggregated count of every rate row under this carrier — active
   * AND inactive. The admin UI uses it to show "N rules" per carrier
   * and to disable the "add rule" button while the list is still
   * loading. */
  rate_count: number;
}

export interface AdminCarrierCreate {
  /** Must match BE regex ``^[a-z][a-z0-9_]*$`` — lowercase, starts
   * with a letter. This is the stable handle we reference from
   * ``shipping_rates.carrier_id``, so the BE rejects uppercase / hyphens. */
  code: string;
  name?: string;
  /** Optional English override (omit ⇒ none). Czech goes in ``name``. */
  name_en?: string;
  /** Název per jazyk platformy; má přednost před ``name`` / ``name_en`` (KAN-1804). */
  name_i18n?: Record<string, string>;
  /** Omit to auto-append at the end of the tenant's carrier list. */
  sort_order?: number;
  color?: string | null;
  logo_url?: string | null;
  /** Omit / empty ⇒ NULL (no public tracker). */
  tracking_url_template?: string | null;
  /** Omit ⇒ 0 (no extra transit days). */
  extra_days?: number;
}

export interface AdminCarrierUpdate {
  name?: string;
  /** Empty string clears the English override; omit ⇒ left untouched. */
  name_en?: string;
  /** Nahradí celý překladový blob; má přednost před ``name`` / ``name_en`` (KAN-1804). */
  name_i18n?: Record<string, string>;
  is_active?: boolean;
  sort_order?: number;
  color?: string | null;
  logo_url?: string | null;
  /** Empty string / null clears it; omit ⇒ left untouched. */
  tracking_url_template?: string | null;
  extra_days?: number;
}

export interface AdminCarrierReorderEntry {
  id: number;
  sort_order: number;
}

export interface AdminCarrierReorderRequest {
  items: AdminCarrierReorderEntry[];
}

export interface AdminShippingRate {
  id: number;
  carrier_id: number;
  /** Denormalised join on the BE so the FE can group rows by carrier
   * without a second fetch. */
  carrier_code: string;
  country_code: string;
  max_weight_kg: number;
  /** MINOR units of ``currency_code`` (KAN-284 W1). */
  price_minor: number;
  currency_code: string;
  /** Per-band transit days added to the promised delivery time (on
   * top of carrier + country extra days). */
  extra_days: number;
  is_active: boolean;
  sort_order: number;
  updated_at: string;
  /** KAN-484 — what fits into the band; ``null`` = no size limit. */
  fit_spec: ShippingRateFitSpec | null;
}

/** KAN-484 — carrier band limit, the ``machine_fit`` shape plus the
 * band's own envelope values (BE ``app.services.shipping_fit``). Key
 * names are tenant data, never axes the code knows. */
export interface ShippingRateFitSpec {
  kind: "rect_2d" | "box_3d" | "linear" | "formula";
  envelope_keys?: string[];
  formula_graph?: Record<string, unknown>;
  lenient_no_geometry?: boolean;
  envelope: Record<string, number>;
  /** The tenant's name for each value, by key. Specs written before the
   * names existed have none — the key is shown instead. */
  envelope_labels?: Record<string, string>;
}

/** Write-only form of a preset band limit: the tenant names each value
 * and the BE mints the key from the name (``resolve_shipping_fit_input``).
 * A row that already has a key sends it back so a rename keeps it. */
export interface ShippingRateFitRowsInput {
  kind: "rect_2d" | "box_3d" | "linear";
  envelope_rows: { key: string | null; label: string; value: number }[];
  lenient_no_geometry: boolean;
}

export type ShippingRateFitSpecInput =
  ShippingRateFitSpec | ShippingRateFitRowsInput;

export interface AdminShippingRateCreate {
  carrier_id: number;
  /** Must match BE regex ``^[A-Z]{2}$`` — ISO-3166-1 alpha-2, upper.
   * Also must be enabled in the org's ``OrganizationDeliveryCountry``
   * — the BE rejects with ``country_not_enabled_for_delivery`` (422)
   * if the FE allowed selecting a non-enabled country. */
  country_code: string;
  max_weight_kg: number;
  price_minor: number;
  /** Omit ⇒ 0 (no extra transit days). */
  extra_days?: number;
  fit_spec?: ShippingRateFitSpecInput | null;
}

export interface AdminShippingRateUpdate {
  max_weight_kg?: number;
  price_minor?: number;
  extra_days?: number;
  is_active?: boolean;
  /** ``null`` clears the limit (KAN-484). */
  fit_spec?: ShippingRateFitSpecInput | null;
}

export interface AdminShippingRateReorderEntry {
  id: number;
  sort_order: number;
}

export interface AdminShippingRateReorderRequest {
  carrier_id: number;
  items: AdminShippingRateReorderEntry[];
}

// --- Admin: discount tiers (PR #72) ----------------------------------

export interface AdminQuantityTier {
  id: number;
  /** NULL = global default, applies to any material that doesn't
   * have its own override. */
  material_code: string | null;
  min_qty: number;
  /** NULL = unbounded / ∞. */
  max_qty: number | null;
  discount_pct: number;
  is_active: boolean;
}

export interface AdminQuantityTierCreate {
  material_code?: string | null;
  /** When supplied, creates one tier row per code in a single request
   * (multi-material picker UX). Mutually exclusive with
   * ``material_code``; ``material_codes`` wins when both present. */
  material_codes?: string[];
  min_qty: number;
  max_qty?: number | null;
  discount_pct: number;
}

export interface AdminQuantityTierBulkCreateResponse {
  items: AdminQuantityTier[];
}

export interface AdminQuantityTierUpdate {
  min_qty?: number;
  max_qty?: number | null;
  discount_pct?: number;
  is_active?: boolean;
}

export interface AdminOrderTotalTier {
  id: number;
  /** MINOR units of ``currency_code`` (KAN-284 W1). */
  min_total_minor: number;
  currency_code: string;
  /** ``min_distinct_materials >= 2`` encodes "discount kicks in
   * only when the cart has at least two distinct material codes" —
   * the cross-selling gate. Default 1 = any cart above the
   * subtotal threshold qualifies. */
  min_distinct_materials: number;
  discount_pct: number;
  is_active: boolean;
}

export interface AdminOrderTotalTierCreate {
  min_total_minor: number;
  min_distinct_materials?: number;
  discount_pct: number;
}

export interface AdminOrderTotalTierUpdate {
  min_total_minor?: number;
  min_distinct_materials?: number;
  discount_pct?: number;
  is_active?: boolean;
}

export interface AdminAuditRow {
  id: number;
  tenant_id: string | null;
  user_id: number | null;
  user_email: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  diff: Record<string, unknown> | unknown[] | null;
  created_at: string;
}

/** One period the tenant had the ``audit_log`` feature switched off.
 * Events from inside it are filtered out of the tenant pane; the page
 * renders the window so the gap is explained rather than silent. */
export interface AdminAuditBlackoutWindow {
  started_at: string;
  ended_at: string;
}

export interface AdminAuditPage {
  total: number;
  page: number;
  page_size: number;
  items: AdminAuditRow[];
  /** Always empty on the cross-tenant platform endpoint — that view is
   * deliberately unfiltered, so it has no gaps to explain. */
  blackout_windows?: AdminAuditBlackoutWindow[];
}

export interface AdminUpdateOrderStatusRequest {
  status: string;
  note?: string;
}

// --- Auth (/auth/me) -------------------------------------------------
//
// Lives here (not in a separate file) so every admin page imports
// from one place. No global ``role`` since KAN-231 — a role belongs to
// an org membership (``org_memberships[].role``), never to the account.

export interface AuthMe {
  id: number;
  email: string;
  preferred_lang: string;
  preferred_currency: string;
  /** Dark-mode preference: ``system`` follows ``prefers-color-scheme``,
   * ``light`` / ``dark`` force a theme. Applied on the admin/operator
   * surface only; the storefront + marketing site stay light. */
  ui_theme: "system" | "light" | "dark";
  /** Ingot accent family (KAN-648). Names a quartet of CSS tokens with
   * separate light/dark values; ``blue`` is the default. Optional so a
   * client talking to an older API — or the anonymous placeholder —
   * reads as "unset", not as a family nobody picked. */
  ui_accent?: "blue" | "emerald" | "orange" | "violet" | "slate";
  /** Skladový slovník (easy | expert | hybrid) — zobrazovací volba,
   * přepíná i18n varianty skladových ploch. Default „easy". */
  ui_stock_vocabulary?: "easy" | "expert" | "hybrid";
  /** Profile fields added alongside password auth. ``null`` for
   * pre-password (magic-link-only) accounts until they opt in. The
   * ``/account`` page reads these to prefill the profile form. */
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  company: string | null;
  /** KAN-1917 — identifikátory firmy (CZ: IČO / DIČ); pokladna je doplní
   * z objednávky do prázdných polí. */
  ico?: string | null;
  dic?: string | null;
  /** True if the account has an Argon2id password_hash set. Drives
   * the "Set password" vs "Change password" copy on /account and
   * lets the sign-in page know whether a password tab makes sense
   * for this identity. */
  has_password: boolean;
  /** KAN-343 — the rest of the auth-method inventory, same flat shape
   * as `has_password`. What counts toward "at least two ways in" is
   * decided server-side (docs/security/AUTH_RECOVERY.md §2); do NOT
   * re-derive the count from these booleans here, or the two answers
   * will drift. */
  has_passkey?: boolean;
  has_google?: boolean;
  has_sso?: boolean;
  /** Passkeys across every RP ID. A passkey is bound to the host it was
   * registered under, so this is "how many you own", not "how many work
   * here" — the per-host list is `GET /auth/passkeys`. */
  passkey_count?: number;
  /** KAN-373 — whether one of those passkeys answers for THIS host.
   * `passkey_count` cannot carry it, and the offer needs this one: a
   * person whose only passkey belongs to another tenant has nothing to
   * unlock this page with, so treating "owns one" as "covered here"
   * strands them. Display only — the recovery gate stays host-blind
   * (docs/security/AUTH_RECOVERY.md §3). */
  passkey_usable_here?: boolean;
  /** Counted methods and the target they are measured against. Sent by
   * the server so the rule has one implementation. */
  auth_method_count?: number;
  auth_method_target?: number;
  /** False once the account reached the target: the magic-link door is
   * closed for it. Hide the offer when this is false — the refusal is
   * enforced server-side regardless. */
  magic_link_enabled?: boolean;
  /** KAN-346 — whether to offer this person a second method right now.
   * The server owns the 30-day window, the stamp and the target
   * (`services/auth_methods.prompt_is_due`); render the offer iff this
   * is true and do NOT re-derive it here. */
  auth_methods_prompt_due?: boolean;
  /** Marketing newsletter consent. Default false (CZ ePrivacy
   * requires explicit affirmative action). Toggled at sign-up
   * + via the /account page. */
  newsletter_opt_in: boolean;
  /** Inline-help / annotations toggle — whether the shared <HelpNote>
   * components render across the admin UI. Default on; the user flips
   * it off from the admin header. */
  show_inline_help?: boolean;
  /** Raw-JSON blocks toggle — whether the advanced "surový JSON" escape
   * hatches render across the admin UI. Default OFF (opt-in); the user
   * flips it on from the account menu under their initials. */
  show_raw_json?: boolean;
  /** True if the user has completed TOTP enrollment (verify step
   * passed). Drives the /account page's 2FA section (enroll vs
   * "already enabled" badge) and lets the sign-in page route a
   * privileged role to setup before issuing a session. */
  is_2fa_enabled?: boolean;
  /** Whether this account may turn off 2FA. Privileged roles are required
   * to retain it by the server policy. */
  can_disable_2fa?: boolean;
  /** True for Forgmatic staff (Czech: "Správce platformy"). Drives
   * the platform-admin shell decision: only platform admins can
   * access ``admin.forgmatic.com`` (cross-tenant directory, global
   * feature flags, the cross-tenant audit pane, impersonation).
   * Independent of ``role`` and of ``org_memberships`` — a platform
   * admin needs no membership and an org admin in any tenant cannot
   * be a platform admin without an explicit grant. */
  is_platform_admin?: boolean;
  /** ProviderOrg memberships — one per Organization the user
   * belongs to with their role inside it. Empty array for a
   * bare-customer user without any membership. Drives the
   * ``<OrgSwitcher>`` in the admin nav (PR-60). */
  org_memberships?: OrgMembershipOut[];
  /** CustomerOrg (B2B team-account) memberships — drives the
   * /account page's "B2B teams" section so the user can navigate
   * directly to ``/buyer/{customer_org_id}``. Empty array for a
   * solo customer. */
  customer_org_memberships?: CustomerOrgMembershipOut[];
  /** Set only while this very session token was minted by the
   * impersonation flow; absent/null for an ordinary sign-in. The
   * server derives it from the token's claims, so it is the ONLY
   * trustworthy source for the "acting as" banner — the banner used
   * to read ``?impersonating=1`` from the URL, which any visitor
   * could fake and the impersonator could drop. */
  impersonation?: ImpersonationOut | null;
}

export interface ImpersonationOut {
  /** The real human behind the session; ``id`` / ``email`` on the
   * enclosing payload stay the impersonated user's. */
  impersonator_id: number;
  /** True for the cross-tenant platform-support flow — the banner
   * renders in the louder danger palette for it. */
  platform_initiated?: boolean;
}

export interface OrgMembershipOut {
  org_id: number;
  slug: string;
  name: string;
  role: string;
  plan_tier: string;
  /** Per-org Stripe test/live toggle (zz39). When true, Connect
   * onboarding + checkout + webhooks route through Stripe's test
   * sandbox so an admin can dry-run the full workflow. */
  stripe_test_mode?: boolean;
  /** Effective ``acct_…`` for the active mode (test acct when in
   * test mode, live acct otherwise). Null until onboarding finishes. */
  stripe_account_id?: string | null;
  /** KYC ``charges_enabled`` flag for the active mode. */
  stripe_charges_enabled?: boolean;
  /** Tenant lifecycle state (KAN-1357). ``suspended`` and ``deleted``
   * memberships stay in the payload — the switcher hides them, the
   * "Moje organizace" page shows them with their state, which is the
   * only place the user can find out where a tenant went. */
  status?: OrgLifecycleStatus;
  /** The user's own position for this membership (drag & drop on
   * "Moje organizace"). ``null``/absent = never ordered, read as
   * alphabetical by the consumers. */
  sort_order?: number | null;
  /** The address to show for this tenant: its own domain when it has
   * one, the platform-assigned ``{slug}.{apex}`` otherwise. Composed on
   * the BE (``tenant_urls.tenant_display_host``) — the FE does not build
   * tenant hosts itself. ``null`` on a deploy without a tenant zone. */
  public_host?: string | null;
}

/** ``OrgStatus`` on the BE (``app/db/models/organization.py``). */
export type OrgLifecycleStatus = "active" | "suspended" | "deleted";

export interface StripeModeResponse {
  mode: "live" | "test";
  account_id: string | null;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  account_status: string | null;
}

interface CustomerOrgMembershipOut {
  customer_org_id: number;
  name: string;
  role: string;
  provider_org_id: number;
  provider_slug: string;
  /** KAN-2109 — the shop offers order approvals (``b2b.approval``). */
  approvals_enabled?: boolean;
}

// --- Admin: materials/axis-values/finishes UPDATE + DELETE (post-#83) ---

export interface AdminMaterialUpdateRequest {
  density_kg_m3?: number;
  sort_order?: number;
  is_active?: boolean;
  /**
   * H2 #4 PR-C — opt-in stock awareness toggle. ``true`` enables stock
   * tracking (Stripe paid hook reserves, quote shows stock_status
   * badge); ``false`` disables. Existing ``MaterialStock`` rows stay
   * intact when toggled off but are ignored by reservation/pricing.
   */
  inventory_managed?: boolean;
  expose_certs?: boolean;
  /** Stock unit-of-measure (registry-native). Edited from the Ceník
   *  „Provoz a viditelnost" section; only meaningful when inventory tracking
   *  is on. Enum enforced server-side by the property definition. */
  stock_uom?: string;
  /** KAN-1839 — dodací lhůta materiálu ve dnech; `null` lhůtu smaže. */
  lead_time_days?: number | null;
  display_name_cs?: string;
  display_name_en?: string;
  description_cs?: string | null;
  description_en?: string | null;
  features_cs?: string | null;
  features_en?: string | null;
  /** PR 9 — admin-managed swatch. `null` clears the field. */
  swatch_hex?: string | null;
  swatch_image_url?: string | null;
  /**
   * Replace the materiálu process ALLOW-LIST wholesale (M:N přes
   * material_process_types) — bod 4B: neprázdný seznam je autoritativní
   * výčet procesů, ``[]`` allow-list smaže a nabídku určuje plná
   * kategorie materiálu (proces → kategorie). ``undefined`` (omit)
   * zachová stávající stav.
   */
  process_types?: string[];
  /**
   * Sklad §7.1 — výchozí místo příjmu tohohle materiálu. Příjem PO,
   * který místo nedostane explicitně, se zeptá nejdřív sem a teprve
   * pak na výchozí místo tenanta (KAN-150). `null` volbu zruší.
   */
  default_putaway_location_id?: number | null;
  /** ms03 — režim sledovanosti položky (none|qty|qty_place). */
  tracking_mode?: string;
}

// --- Account: saved address book (post-#82) -------------------------
//
// Lets a signed-in customer keep a labeled list of shipping / billing
// addresses ("Doma", "Kancelář", …) and pick one at checkout instead
// of retyping. Editing an address-book entry never back-fills past
// orders — they keep their immutable ``shipping_address`` snapshot.

export interface CustomerAddressBookEntry {
  id: number;
  label: string;
  address: Address;
  is_default_shipping: boolean;
  is_default_billing: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerAddressCreate {
  label: string;
  address: Address;
  is_default_shipping?: boolean;
  is_default_billing?: boolean;
}

export interface CustomerAddressUpdate {
  label?: string;
  address?: Address;
  is_default_shipping?: boolean;
  is_default_billing?: boolean;
}

// --- Auth: password flows (sign-in / reset / profile) ---------------

export interface SignInRequest {
  email: string;
  password: string;
  /** Required only after the server confirms this account has 2FA enabled. */
  totp_code?: string;
  /** "Keep me signed in" — longer refresh horizon, no idle timeout. */
  remember_me?: boolean;
}

export interface SetPasswordRequest {
  /** KAN-369 — `POST /auth/password`, the session-proved way to set an
   * account's FIRST password. No token and no `totp_code` on purpose:
   * the session is the proof. The mailbox path still refuses this
   * account with `password_set_not_available`, which is what made the
   * `/account` button a dead end until this route existed. */
  password: string;
}

export interface PasswordResetRequestBody {
  email: string;
}

export interface PasswordResetConfirmBody {
  token: string;
  password: string;
  /** KAN-345 — sent only when the account has an authenticator enrolled.
   * The client cannot know that in advance: submit without it and a 401
   * `invalid_totp` is the server asking for one. Demanding it up front
   * is not an option — it would tell an unauthenticated caller which
   * addresses have TOTP. */
  totp_code?: string;
}

// --- Auth: ProviderOrg self-serve signup (PR-B) ---------------------
//
// `POST /api/v1/orgs/signup-request` — issues a magic link that the
// signer redeems via `POST /api/v1/orgs/signup-verify`. The verify
// step provisions the schema and seats the caller as the org's
// first admin (sets the session cookie too).

/** One catalog machine in a pre-registration draft (KAN-1475). */
export interface OnboardingDraftMachine {
  catalog_key: string;
  addon_keys: string[];
  quantity: number;
}

export interface OnboardingDraftOperation {
  slug: string;
  machines: OnboardingDraftMachine[];
  material_codes: string[];
}

/**
 * What a visitor picked on the landing's „try it" page — the body of
 * ``POST /admin/onboarding/start`` minus everything own. Named types on
 * purpose: the OpenAPI contract test reads interface fields line by line.
 */
export interface OnboardingDraft {
  buyers: "b2b" | "b2c" | "both" | null;
  country_codes: string[];
  carrier_codes: string[];
  operations: OnboardingDraftOperation[];
}

export interface OrgSignupRequest {
  email: string;
  slug: string;
  name: string;
  /** Cloudflare Turnstile token from the SignUpShop CAPTCHA widget.
   * Required in production; the BE bypasses the check when its
   * Turnstile secret is unset (dev / preview convenience). */
  captcha_token?: string | null;
  /** Výslovný souhlas s podmínkami užívání služby. **Povinné** — BE
   * odmítne registraci bez něj (422 ``terms_not_accepted``). Doklad se
   * ukládá až při ověření e-mailu, kdy už existuje organizace. */
  terms_accepted: boolean;
  /** Jazyk, ve kterém byly podmínky zobrazeny. */
  terms_lang?: string;
  /** Výběr z předregistrace (KAN-1475). Nepovinné; bez něj je registrace
   * tatáž jako dřív. BE ho přísně validuje — je to anonymní vstup. */
  onboarding_draft?: OnboardingDraft | null;
  /** Personal code from an outreach link (`?p=`, KAN-1574). Optional; a
   * value that cannot be a code is dropped by the BE without an error. */
  personal_code?: string | null;
}

export interface OrgSignupRequestResponse {
  status: string;
  /** Kolik minut platí odkaz v e-mailu. Odvozeno na BE z
   * ``_SIGNUP_TOKEN_TTL`` — jediné místo, kde to číslo žije.
   * Do KAN-570 měl FE napsáno „60 minut" prózou a e-mail tvrdil 15. */
  expires_in_minutes: number;
}

export interface OrgSignupVerifyRequest {
  token: string;
  /** SetupIntent id from the FE's successful ``stripe.confirmSetup``
   * call (signup-hardening). The BE refuses to provision the tenant
   * without it. */
  setup_intent_id?: string | null;
}

export interface OrgSignupVerifyResponse {
  org: {
    id: number;
    slug: string;
    name: string;
    plan_tier: string;
    entitlements_json: Record<string, unknown>;
    service_capabilities: string[];
    created_at: string;
  };
}

/** `GET /api/v1/orgs/{slug}/domain-status`
 *
 * Drives the post-signup waiting screen. ``ready`` is a server-side
 * probe of the tenant hostname, NOT a database flag: ``attached`` only
 * records that Vercel accepted the host, and the certificate lands
 * seconds to minutes later. Redirecting on ``attached`` would send the
 * browser into the TLS warning the waiting screen exists to prevent. */
export interface OrgDomainStatus {
  ready: boolean;
  /** ``null`` on deploys with no configured apex — nothing to wait for. */
  host: string | null;
  attached: boolean;
  /** Last attach failure, so the screen can stop spinning and say
   * something true instead of timing out in silence. */
  error: string | null;
}

// --- Auth: CustomerOrg public self-serve signup (PR-D) -------------
//
// `POST /api/v1/customer-orgs/public-create` — any logged-in user can
// create a B2B team under a ProviderOrg (identified by slug) and is
// seated as the team's first admin. The new sign-up-team page sends
// a magic-link first; the redirect lands the user back signed-in
// and the FE then POSTs this with the captured form fields.

export interface CustomerOrgPublicCreate {
  provider_slug: string;
  name: string;
  ico?: string | null;
  dic?: string | null;
  /** MINOR units of the provider tenant's base currency (KAN-284 W1). */
  approval_threshold_minor?: number;
  admin_email?: string | null;
}

export interface CustomerOrgPublicCreateResponse {
  id: number;
  name: string;
  ico: string | null;
  dic: string | null;
  approval_threshold_minor: number;
  currency_code: string;
  provider_slug: string;
  provider_name: string;
}

export interface ProfileUpdateRequest {
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  company?: string | null;
  /** KAN-1917 — ``null`` / prázdný řetězec maže. */
  ico?: string | null;
  dic?: string | null;
  newsletter_opt_in?: boolean | null;
  /** KAN-661 — jazyk je účtová preference; sloupec (``preferred_lang``)
   *  existoval, jen ho tahle routa neuměla zapsat. Není to union: sada
   *  routovatelných jazyků je běhová (platform admin je zapíná), takže
   *  server odmítne kód, který zapnutý není. */
  preferred_lang?: string | null;
  ui_theme?: "system" | "light" | "dark" | null;
  ui_accent?: "blue" | "emerald" | "orange" | "violet" | "slate" | null;
  ui_stock_vocabulary?: "easy" | "expert" | "hybrid" | null;
  show_inline_help?: boolean | null;
  show_raw_json?: boolean | null;
}

// --- Checkout (POST /checkout, GET /orders/:number) -----------------

export interface Address {
  /** Post-2026-04-24 split. Pre-cutover orders persisted a single
   * ``name`` field instead; the BE invoice renderer still reads
   * that as a fallback and we mirror the same fallback on the FE
   * where we *display* addresses (OrderThanks, admin detail), so
   * historical order reads stay intact. New submissions MUST carry
   * both halves — the BE rejects payloads missing either. */
  first_name: string;
  last_name: string;
  street: string;
  city: string;
  postal_code: string;
  country_code: string;
  company?: string;
  phone?: string;
  email?: string;
  note?: string;
  /** Legacy single-name field, only present on orders persisted
   * before 2026-04-24. Never sent back up — the checkout form
   * always submits ``first_name`` + ``last_name``. */
  name?: string;
}

/** ``GET /checkout/prefill`` (KAN-923). Every address field optional —
 * a partial prefill still beats an empty form. */
export type CheckoutPrefillAddress = Partial<
  Record<
    | "first_name"
    | "last_name"
    | "street"
    | "city"
    | "postal_code"
    | "country_code"
    | "company"
    | "phone"
    | "email",
    string | null
  >
>;

export interface CheckoutPrefill {
  shipping_address: CheckoutPrefillAddress | null;
  /** ``null`` ⇒ keep "billing same as shipping" ticked. */
  billing_address: CheckoutPrefillAddress | null;
  ico: string | null;
  dic: string | null;
  /** Where the shipping address came from. */
  source: "address_book" | "last_order" | "profile" | "quote" | "none";
  /** KAN-2093 — where company / IČO / DIČ came from. Never a previous
   *  order: address book → company account → profile. */
  company_source?: "address_book" | "customer_org" | "profile" | "quote" | "none";
  /** KAN-2093 — where the shipping phone (and so its dial code) came from. */
  phone_source?: "address_book" | "last_order" | "profile" | "none";
}

/** ``GET /checkout/default-country`` (KAN-1928) — země, na které pokladna
 *  začíná: země zákazníka podle IP, pokud tam obchod doručuje, jinak první
 *  doručovací země obchodu. ``null`` = obchod nedoručuje nikam. */
export interface CheckoutDefaultCountry {
  country_code: string | null;
}

export interface CheckoutRequest {
  shipping_address: Address;
  billing_address?: Address;
  /** Povinné s ``delivery_method: "carrier"`` (výchozí); u osobního
   *  odběru se neposílá (KAN-1605). */
  carrier_code?: string;
  /** KAN-1605 — osobní odběr je samostatný způsob doručení, ne dopravce. */
  delivery_method?: "carrier" | "pickup";
  ico?: string;
  dic?: string;
  currency?: string;
  /** Order-level delivery tier picked on the checkout page. When
   * present the BE re-prices every line with this multiplier so the
   * whole order shares one lead time. Optional for backwards compat
   * with integrators that don't know about the field yet. */
  delivery_code?: string;
  /** Canonical ladder rung id from ``/cart/delivery-options/ladders``.
   * BE picks ``_reprice_lines_for_ladder`` when set. Position-based
   * payloads were dropped in PR14. */
  delivery_ladder_id?: number;
  /** Výslovný souhlas s obchodními podmínkami prodávajícího.
   * **Povinné** — BE odmítne objednávku bez něj (422
   * ``terms_not_accepted``) a uloží doklad s hashem znění, které
   * kupující viděl. Záměrně bez výchozí hodnoty, aby starý klient
   * neobjednával bez souhlasu a tiše. */
  terms_accepted: boolean;
  /** Jazyk, ve kterém byly podmínky zobrazeny — doklad musí nést tuhle
   * verzi, ne serverový default. */
  terms_lang?: string;
  /** KAN-748 — testovací objednávka. Honorováno JEN pro přihlášeného
   * admina vlastního tenanta; od kohokoli jiného BE příznak tiše
   * ignoruje. */
  is_test?: boolean;
  /** KAN-993 — čím zákazník platí. Vynechané pole = server dosadí
   * první dostupný způsob, tedy přesně to, co se dělo do KAN-993;
   * vyžádaný, ale nedostupný způsob je 422
   * ``payment_method_not_available``. Nabídku vrací
   * ``GET /checkout/payment-methods`` — závisí na tom, KDO se ptá. */
  payment_method?: "card" | "bank_transfer" | "account";
  /** KAN-1883 — celková částka (minor), kterou zákazník potvrdil po 409
   * ``price_changed`` (``params.current_total_minor``). Objednávka vznikne
   * jen na částku ze souhrnu pokladny, nebo na tuhle potvrzenou. */
  confirmed_total_minor?: number;
  /** KAN-761 — podepsaná pozvánka „vyzkoušej si svůj obchod" z odkazu
   * nebo QR kódu v checklistu rozjezdu. Přikládá ji ``useCheckout``
   * sama; objednávka díky ní vznikne jako testovací i anonymnímu
   * návštěvníkovi. Neplatná pozvánka je 422 ``trial_token_invalid`` —
   * tichý propad na ostrou objednávku by byl horší. */
  trial_token?: string;
  /** KAN-992 — číslo objednávky, které si vystavil ODBĚRATEL. Pokladna
   * pole ukazuje jen přihlášenému členovi firemního účtu u toho
   * tenanta; BE ho ale přijme od kohokoli, aby checkout nepadal na
   * poli, které jeho UI zrovna nenabízí. Tiskne se na potvrzení,
   * dodacím listu a faktuře a hledá se podle něj v adminu. */
  customer_reference?: string;
}

export interface CheckoutResponse {
  order_id: number;
  order_number: string;
  uid: string | null;
  /** KAN-1430 — veřejné číslo objednávky ``xxx-xxx-xxx``; zobrazuje ho ``displayId``. */
  public_number: string | null;
  status: string;
  subtotal_czk: number;
  shipping_czk: number;
  total_czk: number;
  /** ``null`` u osobního odběru (KAN-1605). */
  carrier_code: string | null;
  country_code: string;
  currency: string;
  /** CZK per 1 unit of `currency`. Stays CZK-denominated even when the
   * tenant's books base is not CZK (chained display→base→CZK). */
  fx_rate_to_czk: number;
  /** Units of the tenant's BOOKS base per 1 unit of `currency`. Equals
   * `fx_rate_to_czk` while that base is CZK. */
  fx_rate_to_base: number;
}

/** Payload for POST /cart/rfq — the cart assembly gate's third path
 * (KAN-301/KAN-302): "I don't have it in this shape / want someone to
 * look at it". Converts the whole cart into an RFQ instead of checkout. */
/** KAN-928 — „Budeme s díly něco dělat?" */
export type AssemblyIntent = "none" | "assembly" | "kit" | "unsure";

export interface CartRfqRequest {
  name?: string;
  email: string;
  phone?: string;
  message?: string;
  /** KAN-1754 — why the cart goes to an enquiry when the platform knows:
   * no shipping band takes the parts. Lands in the shop's note. */
  reason?: "shipping_does_not_fit";
}

export interface CartRfqResponse {
  public_id: string;
  status: string;
  order_number: string;
}

/** ``POST /cart/split-rfq`` (KAN-1049) — the priced half stays a cart,
 *  the unpriced half becomes a ``draft`` CustomQuote. */
export interface CartSplitResponse {
  public_id: string;
  status: string;
  /** Order the customer takes to checkout — the priced half. */
  cart_order_number: string;
  moved_item_count: number;
}

/** ``GET /cart/split-preview`` (KAN-1049) — read-only, changes nothing.
 *
 *  Asked BEFORE the customer commits, because the split is irreversible:
 *  they must see a minimum-order top-up in advance rather than discover
 *  it afterwards (the failure mode KAN-738 was about). */
export interface CartSplitPreview {
  can_split: boolean;
  /** ``split_would_break_assembly`` = the owner's 2026-08-16 rule still
   *  holds inside an assembly; ``cart_has_no_priced_items`` = nothing to
   *  split, the whole cart should go to ``/cart/rfq`` instead. */
  blocked_code: string | null;
  blocked_params: Record<string, unknown>;
  priced_item_count: number;
  unpriced_item_count: number;
  priced_subtotal_minor: number;
  /** MINOR units. ``0`` = none. Non-zero does NOT block the split — it
   *  is something the customer has to be told, not forbidden. */
  minimum_order_topup_minor: number;
  /** Currency both minor amounts above are denominated in. */
  currency_code: string;
}

/** Authoritative breakdown of the last screen before payment (KAN-1514).
 *
 *  Until KAN-1514 the checkout assembled its own total: it took
 *  ``minimum_order_topup_minor`` from ``GET /cart`` — where the top-up is
 *  computed WITHOUT shipping, because the cart knows no carrier — and then
 *  added shipping on top a second time. A 282 order with 119 shipping under a
 *  500 minimum showed the customer 619 while the order and the document both
 *  said 500.
 *
 *  ``subtotal + delivery_surcharge + shipping + addons + minimum_order_topup
 *  + vat === total`` holds by construction, so the screen renders rows and
 *  adds nothing up itself. All amounts are MINOR units of ``currency_code``. */
/** KAN-2055 — dorovnání minima jedné skupiny operací (řádek za pravidlo
 *  obchodu). ``amount_minor`` v MINOR jednotkách měny souhrnu. */
export interface OperationGroupTopup {
  name: string;
  amount_minor: number;
  /** KAN-2078 — ``order_surcharge`` = příplatek za zakázku sdílený položkami
   *  se stejnou volbou (``name`` nese pravidlo i hodnotu volby); chybí-li,
   *  jde o dorovnání minima (KAN-2055). Peníze se chovají stejně. */
  kind?: "minimum_topup" | "order_surcharge";
}

/** KAN-2055 — pravidlo „Minimum zakázky pro skupinu operací" v adminu. */
export interface OperationGroupMinimumRule {
  id: number;
  name: string;
  /** MINOR jednotky ``currency_code`` (účetní báze obchodu). */
  amount_minor: number;
  currency_code: string;
  /** Slugy procesů ve skupině; jeden slug patří nejvýš do jednoho pravidla. */
  process_types: string[];
}

/** KAN-2078 — stupeň příplatku za zakázku: od součtu veličiny (včetně). */
export interface OrderSurchargeTier {
  from_quantity: number;
  /** MINOR jednotky účetní báze obchodu. */
  amount_minor: number;
}

/** KAN-2078 — pravidlo „Příplatek za zakázku" v adminu. */
export interface OrderSurchargeRule {
  id: number;
  name: string;
  /** Částka bez stupňů / pod prvním stupněm, MINOR jednotky ``currency_code``. */
  amount_minor: number;
  currency_code: string;
  /** Slugy procesů, jejichž operace do pravidla vstupují. */
  process_types: string[];
  /** ``field`` = pole konfigurace operace (``group_key``), ``material_variant``
   *  = materiál + varianta řádku. */
  group_by: "field" | "material_variant";
  group_key: string | null;
  /** ``quantity`` | ``analysis.<key>`` | ``config.<key>`` (na kus × kusy). */
  quantity_source: string | null;
  tiers: OrderSurchargeTier[];
  /** Vlastnost materiálu/varianty, která řádek osvobodí. */
  exempt_property_key: string | null;
  /** Hodnota vlastnosti; ``null`` = stačí pravdivá hodnota. */
  exempt_value: string | null;
  is_active: boolean;
}

export interface CheckoutTotalsPreview {
  currency_code: string;
  subtotal_minor: number;
  delivery_surcharge_minor: number;
  shipping_minor: number;
  addons_minor: number;
  minimum_order_topup_minor: number;
  /** KAN-2055 — dorovnání minim skupin operací; jejich součet patří do
   *  invariantu výš vedle ``minimum_order_topup``. */
  operation_group_topups?: OperationGroupTopup[];
  /** The tenant's order minimum, so the top-up row can say WHICH minimum
   *  applies instead of showing an unexplained fee. ``0`` = no minimum. */
  minimum_order_value_minor: number;
  vat_minor: number;
  vat_rate_pct: number | string;
  /** Země, jejíž SAZBA se použila (KAN-1783) — ne nutně země doručení. */
  vat_country_code?: string | null;
  total_minor: number;
  /** ``reverse_charge_eu`` = 0 % s přenesením daňové povinnosti. */
  vat_scheme?: string;
  /** KAN-2100 — podle jakého pravidla se sazba vybrala (``reason`` výpočtu
   *  DPH), např. ``home_country_rate_below_threshold``, ``oss_destination_rate``,
   *  ``b2b_taxed_in_dispatch_country``, ``export_outside_eu``. */
  vat_reason?: string | null;
  /** Verdikt VIES o zadaném DIČ; ``null`` = DIČ nezadáno. */
  vat_id_status?: "valid" | "invalid" | "unavailable" | null;
  /** Dalo by platné DIČ reverse charge (KAN-1868)? ``false`` u tuzemského
   *  B2B — věta o neověřeném DIČ se pak neukazuje. */
  vat_id_can_reverse_charge?: boolean;
}

/** Display metadata for one ``dimensions`` key of an order line (KAN-926).
 *  ``label`` names the key („Výplň"), ``value_label`` the value („Černá");
 *  either is empty when nobody authored it. ``is_modifier`` marks the slot
 *  the process-modifier (finish) selection was folded into. */
export interface OrderLineDimensionLabel {
  label: string;
  unit?: string | null;
  value_label?: string | null;
  is_modifier?: boolean;
}

interface OrderLine {
  material_code: string;
  /** Human label for the material; falls back to the code server-side.
   *  The code itself is an internal join key and is not shown. */
  material_display_name?: string;
  /** Per-key labels for ``dimensions`` — same contract as
   *  ``material_display_name``: render THESE, never the key. See
   *  ``lib/orderDisplay``. */
  dimension_labels?: Record<string, OrderLineDimensionLabel>;
  // Universal variant identity (post-PR12) — the full per-axis dims dict;
  // area finishes (paint, anodize): ``{}``. PR15 dropped the legacy flat
  // axis alias from this response — readers extract ``dimensions.<axis>``
  // if needed.
  dimensions: Record<string, unknown>;
  stock_uom: string;
  // Surface finishes are chain operations; the selection is folded into
  // ``dimensions`` under its process_modifier key (generic channel). The
  // chain ops live in ``OrderItem.operations``.
  delivery_code: string;
  quantity: number;
  /** KAN-1661 — kusy, které dílna nedodá; ``quantity`` zůstává objednané. */
  undelivered_qty?: number;
  unit_price_czk: number;
  line_total_czk: number;
  /** KAN-2099 — cena VÝROBY bez příplatku za termín (jak ji ukázal
   *  konfigurátor). Chybí/``null`` = objednávka příplatek nerozepisuje. */
  production_unit_price_major?: number | null;
  production_line_total_major?: number | null;
  /** Customer-typed note for this variant (configurator → admin).
   *  ``null`` when the customer didn't write anything. */
  customer_note?: string | null;
}

export interface OrderItem {
  /** Veřejný klíč položky (``OrderItem.slug``) — zákazník jím vybírá položky
   *  reklamace. Interní id (DXF, štítky) má jen admin ve workbench
   *  (``AdminOrderWorkbench.items``, KAN-1957). */
  slug: string;
  file_name: string;
  // Inline thumbnail URLs (presigned, ~10 min), picked by the module's
  // declared preview capability: ``svg`` → the pre-rendered preview,
  // ``mesh`` → the raw upload, rendered client-side by ``MeshPreview``.
  // Exactly one is ever set; both are null for legacy items missing the
  // artefact and for modules that declare no part preview.
  svg_preview_url?: string | null;
  mesh_preview_url?: string | null;
  lines: OrderLine[];
}

/**
 * KAN-1490 — kam si zákazník přijde, když si dopravu zvolil jako osobní
 * odběr. Objednávka do té doby vypisovala „Dodací adresa: …", tedy
 * adresu, na kterou nikdo nic nepošle.
 *
 * Adresa i hodiny jsou ŽIVÉ z profilu firmy, ne zmrazené na objednávce:
 * když se dílna přestěhuje, zákazník s nevyzvednutou zásilkou má vidět
 * nové místo.
 */
export interface OrderPickup {
  /** ``null`` = obchod nevyplnil adresu odběru ani sídlo; stránka pak
   *  řekne, že se obchod ozve, místo aby místo vymyslela. */
  address?: string | null;
  /** Volný text otevíracích dob — starší pole, dokud ``schedule`` nic
   *  neotevírá (táž přednost jako na veřejném profilu obchodu). */
  hours?: string | null;
  /** Týdenní rozvrh; má před ``hours`` přednost. */
  schedule?: WeeklySchedule | null;
}

/** Kam si zákazník přijde (KAN-1605) — z Profilu firmy: adresa odběru,
 *  jinak sídlo. Týž tvar jako {@link OrderPickup}. */
export type PickupLocation = OrderPickup;

/** ``GET /checkout/pickup`` — osobní odběr obchodu; ``null`` = nenabízí. */
export interface CheckoutPickup {
  /** Minor jednotky ``currency_code`` (KAN-284). */
  price_minor: number;
  currency_code: string;
  /** Vždy 0 — termín odběru = dokončení výroby. */
  extra_days: number;
  location: PickupLocation;
}

/** One accounting document of an order (KAN-1550). */
export interface OrderDocument {
  id: number;
  type: "invoice" | "proforma" | "credit_note";
  number: string | null;
  issued_at: string;
  /** Part of THIS order the document bills, minor units; ``null`` = unknown. */
  amount_minor: number | null;
  currency_code: string;
}

/** KAN-1661 — nedodané kusy, za které obchod ještě nevrátil peníze. */
export interface PendingShortDelivery {
  pieces: number;
  /** Návrh vratky v MINOR jednotkách ``currency``. */
  refund_due_minor: number;
  currency: string;
  /** Jde vrátit kartou (Stripe); jinak převodem přes výplatu (KAN-1735). */
  can_refund_online: boolean;
}

export interface OrderDetail {
  // KAN-1957 — ``order_id``, ``user_id``, ``is_test``, ``fx_rate_to_base`` a id
  // položek tenhle DTO nenese (vidí ho i anonym); admin je má v
  // ``AdminOrderWorkbench`` a detail si je skládá v ``AdminOrderView``.
  order_number: string;
  uid: string | null;
  /** KAN-1430 — veřejné číslo objednávky ``xxx-xxx-xxx``; zobrazuje ho ``displayId``. */
  public_number: string | null;
  status: string;
  /** KAN-993 — čím se objednávka platí. ``null`` = objednávka je starší
   * než ta volba; stránka poděkování na ni odpovídá po staru, tedy
   * podle ``shop_readiness.card_payments_ready``. */
  payment_method?: "card" | "bank_transfer" | "account" | null;
  subtotal_czk: number;
  shipping_czk: number;
  vat_czk: number;
  total_czk: number;
  /** Order-level discount (``OrderTotalDiscountTier``) stamped at
   * checkout. Zero if no tier fired. Surfaced here so the thanks
   * page + admin detail can render the saving as a discrete line
   * rather than a silently-smaller subtotal. */
  order_discount_czk: number;
  /** KAN-738 — dorovnání minimální objednávky zmrazené při checkoutu,
   * v MINOR jednotkách knih tenanta (nové pole měnu do wire nešíří).
   * Total i základ DPH ho UŽ obsahují; samostatně jde ven, aby detail
   * objednávky vykreslil vlastní řádek a součet rozpadu seděl. */
  minimum_order_topup_base_major?: number;
  /** KAN-2055 — dorovnání minim skupin operací zmrazená při checkoutu
   * (MAJOR jednotky knih tenanta, jako ``minimum_order_topup_base_major``). */
  operation_group_topups?: {
    name: string;
    amount_base_major: number;
    /** KAN-2078 — ``order_surcharge`` = příplatek za zakázku (vlastní jméno). */
    kind?: "minimum_topup" | "order_surcharge";
  }[];
  /** KAN-2099 — příplatek za úroveň termínu jako vlastní řádek. Peníze se
   *  nemění: mezisoučet objednávky dál obsahuje ``in_subtotal_base_major``
   *  a doprava ``in_shipping_base_major``; detail je vyjme. */
  delivery_surcharge?: {
    label: string;
    amount_base_major: number;
    in_subtotal_base_major: number;
    in_shipping_base_major: number;
  } | null;
  /** KAN-1409 — sazba DPH zmrazená na objednávce, v procentech. */
  vat_rate_pct?: number;
  /** KAN-1409 — okamžik přijetí (odeslání; u starších objednávek vznik). */
  ordered_at?: string | null;
  /** KAN-1409 — okamžik zaplacení; ``null`` = platba ještě nedorazila. */
  paid_at?: string | null;
  /** KAN-1551 — stáhne odkaz na fakturu / proformu doklad? U obchodu, který
   *  doklady vystavuje jinde, až ve chvíli, kdy doklad dorazil. */
  invoice_available?: boolean;
  proforma_available?: boolean;
  // KAN-1934 — důvod blokace faktury (``invoice_blocked_reason``) tenhle DTO
  // nenese; admin detail ho čte z ``AdminOrderWorkbench``. Zákazník má jen
  // ``invoice_available`` („doklad se připravuje").
  /** KAN-1550 — every issued document covering this order, newest first
   *  (partial invoices, a summary invoice, credit notes, documents from an
   *  external system). */
  documents?: OrderDocument[];
  currency: string;
  /** CZK per 1 unit of `currency` — render the total + line items in the
   * transaction currency (Model A). 1.0 for CZK orders. */
  fx_rate_to_czk: number;
  shipping_address: Address | null;
  billing_address: Address | null;
  /** KAN-1490 — vyplněné jen u objednávek s osobním odběrem; detail pak
   *  ukáže místo a hodiny odběru MÍSTO dodací adresy. */
  pickup?: OrderPickup | null;
  items: OrderItem[];
  // Order-level delivery tier chosen at checkout — ``standard`` /
  // ``express`` / ``rush`` (or any code configured in the admin
  // pricing page). Null for draft orders that never reached the
  // checkout page. Prefer this over the per-line ``delivery_code``
  // on ``OrderLine`` — the line-level value is kept as a historical
  // snapshot of the multiplier at pricing time; the order-level
  // value is the customer-visible truth. See backend migration
  // ``d4e5f6a7b8c9_orders_delivery_option`` for the cutover.
  delivery_code: string | null;
  estimated_production_complete_at?: string | null;
  estimated_delivery_at?: string | null;
  /** KAN-1850 — IANA pásmo obchodu; časy objednávky se zobrazují v něm. */
  shop_timezone?: string | null;
  /** KAN-1641 — co objednávka z tohoto stavu nabízí zákazníkovi
   *  (``cancel``, ``request_cancel``, ``pay``, ``resend_invoice``,
   *  ``delivery_estimate``). Jeden zdroj na serveru — FE si podmínky
   *  nedopočítává. */
  available_actions?: string[];
  // KAN-1929 — admin-only pole (akce admina, „Co teď", chybějící materiál,
  // žádost o zrušení) tenhle DTO nenese: vidí ho i anonym. Admin detail je
  // čte z ``AdminOrderWorkbench`` (``GET /admin/orders/{n}/workbench``).
  // KAN-269: obe pole vyse jsou null, protoze v planu stoji krok bez
  // autorovane doby - ne proto, ze by objednavka termin nemela.
  eta_pending_estimate?: boolean;
  // KAN-1934 — ``supplier_invoice_number`` (admin-only) žije
  // v ``AdminOrderWorkbench``.
  /** KAN-992 — číslo objednávky ODBĚRATELE. Tiskne se na potvrzení,
   *  dodacím listu a faktuře. Editace přes
   *  ``PATCH /admin/orders/{n}/customer-reference``. */
  customer_reference?: string | null;
  /** KAN-2111 — IČO / DIČ odběratele z pokladny. */
  ico?: string | null;
  dic?: string | null;
  /** KAN-2111 — veřejné id nabídky, ze které objednávka vznikla. */
  quote_public_id?: string | null;
  // Carbon footprint snapshot in g CO2e (PR-63). Null when no
  // CarbonFactor is registered for the material the order used (the
  // BE subscriber logs + skips). The FE renders <CarbonBadge> only
  // when this is non-null.
  carbon_footprint_gco2e?: number | null;
  /** KAN-1653 — časová osa a průběh výroby z TÉHOŽ builderu jako
   *  ``/track`` (dřív si je FE dopočítával z ``routing_graph_json``). */
  timeline?: CustomerTimelineEvent[];
  production_progress?: CustomerProductionProgress | null;
  /** Customer-typed free-text note for the whole order (cart →
   *  admin). ``null`` when the customer didn't write anything. */
  customer_note?: string | null;
  /** KAN-1957 — zvolená rychlost dodání v zákaznické podobě (popisky, ne
   *  klíče). Zmrazený snapshot s interními klíči má admin ve workbench. */
  delivery_selection?: OrderDeliveryView | null;
  /** Dokumenty odsouhlasené u téhle objednávky, ve znění platném
   *  **v okamžiku nákupu**. Prázdné u objednávek z doby před zavedením
   *  souhlasu — zpětně se nedoplňují. */
  legal_acceptances?: OrderLegalAcceptance[];
}

export interface OrderLegalAcceptance {
  kind_key: string;
  /** ``null`` u druhu bez veřejné stránky — pak jen název a verze. */
  slug: string | null;
  title: string;
  version: string;
  accepted_at: string;
}

/** KAN-1957 — zvolená rychlost dodání, jak ji čte zákazník. */
export interface OrderDeliveryView {
  /** Zvolená rychlost v jazyce zobrazení; ``null`` = snapshot ji nenese. */
  label: string | null;
  /** Rychlost, na které skončil každý druh výroby. */
  per_process: Array<{ process_type: string; label: string }>;
  /** Procesy, které zvolenou rychlost neuměly a spadly na pomalejší. */
  fallbacks: Array<{
    process_type: string;
    requested_label: string;
    actual_label: string;
  }>;
  /** Položky se zvlášť zvolenou rychlostí; ``actual_label`` = kam spadla. */
  line_overrides: Array<{
    file_name: string;
    label: string;
    actual_label: string | null;
  }>;
}

/** Interní snapshot termínu (``Order.delivery_selection``) — jen
 *  ``AdminOrderWorkbench.delivery_selection`` (KAN-1957). */
export interface OrderDeliverySelectionCapacity {
  tier_id: number;
  code: string;
  label_cz: string;
  label_localized?: string;
  sort_order: number;
  multiplier: number;
  flat_surcharge_czk: number;
  base_min_days: number;
  items_per_day: number;
  day_basis: "calendar" | "business";
  /** New shape only — links the per-service tier back to the canonical
   *  ladder rung the picker resolved to. Older snapshots omit this. */
  ladder_code?: string;
}

export interface OrderDeliveryLadderFallback {
  process_type: string;
  requested_ladder_code: string;
  actual_ladder_code: string;
  actual_ladder_sort_order: number;
}

export interface OrderDeliveryLineOverride {
  ladder_id: number;
  ladder_code: string;
  actual_ladder_code: string;
  tier_id: number;
}

export interface OrderDeliverySelection {
  // Ladder-picker shape (PR4+).
  ladder_id?: number;
  ladder_code?: string;
  ladder_label_cz?: string;
  /** KAN-1818 — popisek v jazyce zobrazení (API ho doplní při čtení). */
  ladder_label_localized?: string;
  ladder_sort_order?: number;
  fallbacks?: OrderDeliveryLadderFallback[];
  /** Keyed by ``OrderLine.id`` as a string (JSON object key). */
  line_overrides?: Record<string, OrderDeliveryLineOverride>;
  // Legacy position-picker shape.
  position?: number;
  label_cz?: string;
  // Shared fields.
  days_calendar?: number;
  days_business?: number | null;
  day_basis?: string;
  flat_surcharge_czk?: number;
  per_capacity?: Record<string, OrderDeliverySelectionCapacity>;
}

/** Milník časové osy objednávky pro zákazníka (KAN-1653) — stav, do
 *  kterého uzel lifecycle zakázku posunul; překládá se ``track:status.*``. */
export interface CustomerTimelineEvent {
  key: string;
  status: string;
  state: string;
  occurred_at: string | null;
}

export type CustomerProgressState = "done" | "current" | "upcoming";

/** Průběh výroby pro zákazníka — ``/track`` i detail objednávky. */
export interface CustomerProductionProgress {
  steps: Array<{
    key: string;
    label_key: string;
    state: CustomerProgressState;
    /** Operace kroku; pojmenuje se názvem z katalogu, nikdy klíčem. */
    process_type?: string | null;
    completed_count?: number | null;
    total_count?: number | null;
    active_count?: number | null;
    branches?: Array<{
      key: string;
      label_key: string;
      state: CustomerProgressState;
      process_type?: string | null;
    }>;
    // Vyplněné jen u brány, kterou dílna otevřela na úroveň „vidí
    // výsledek". Úroveň „vidí krok" ukáže stupeň a nic víc — proto
    // dvě pole navíc, ne jeden příznak.
    verdict_outcome?: string | null;
    protocol_work_item_id?: number | null;
    /** KAN-1882 — krok šarže dovýroby (zbylé kusy po odvedení méně). */
    is_remake?: boolean;
  }>;
  alternative_path_count: number;
}

/**
 * KAN-1882 — „Odeslali jsme 8 z 10 ks, zbylé 2 ks dovyrábíme, odhad X".
 * Týž výpočet jako e-mail ``shipment_shipped``; ``null``, dokud nic
 * neodešlo. ``remake_estimated_at`` ``null`` = termín dovýroby neznáme.
 */
export interface CustomerDispatch {
  ordered_pieces: number;
  shipped_pieces: number;
  remake_pieces: number;
  remake_estimated_at: string | null;
}

/**
 * How to pay an unpaid order by transfer
 * (``GET /orders/:n/payment-instructions``, KAN-764).
 *
 * Shown only by shops with no working card payment. Every number comes
 * off the order's proforma row server-side, so this and the PDF cannot
 * name two different references. Deliberately carries no amount — the
 * caller already holds the order and renders its total — except the
 * remainder of a partly paid order (KAN-1991).
 */
export interface OrderPaymentInstructions {
  beneficiary: string;
  /** ``null`` when the shop has not filled its account in. */
  account: string | null;
  variable_symbol: string;
  /** Proforma number, so the customer can match this against the PDF.
   *  ``null`` when the shop issues documents elsewhere and none has
   *  arrived yet (KAN-1551). */
  document_number: string | null;
  /** KAN-1991 — částečně zaplacená objednávka: co už přišlo a co zbývá,
   *  MINOR jednotky ``currency_code`` (měna knih). Všechna tři ``null``,
   *  dokud nic nedorazilo — pak platí celek objednávky. */
  received_minor?: number | null;
  remaining_minor?: number | null;
  currency_code?: string | null;
  /** KAN-2013 — QR platba (SPAYD) na tutéž částku, účet a VS jako QR na
   *  zálohové faktuře; ``null``, když účet obchodu není IBAN. */
  qr_payload?: string | null;
}

export interface OrderMaterialCertificate {
  material_code: string;
  /** Human label for the material; falls back to the code server-side.
   *  The code itself is an internal join key and is not shown. */
  material_display_name?: string;
  /** The batch's variant identity — generic dimensions dict. */
  dimensions?: Record<string, unknown>;
  batch_number: string;
  supplier_name?: string | null;
  received_at?: string | null;
  certificate_kind: string;
  download_url: string;
}

// --- Cart (POST /cart/items/:id/lines, GET /cart, DELETE ...) --------

export interface AddLineRequest {
  material_code?: string | null;
  quantity?: number | null;
  /**
   * Universal per-process variant dimensions — the canonical channel.
   * Per-process parameters (layer height, colour, …) are DB-defined
   * dimensions and ride this dict; the wire contract carries no typed
   * per-process fields.
   */
  dimensions?: Record<string, unknown>;
  extra_config?: Record<string, unknown>;
  /** Customer-typed note for this variant. Written between the
   *  configuration and the price block in the configurator. ``null``
   *  or empty string is normalized to ``null`` server-side. */
  customer_note?: string | null;
  /** KAN-1047 — the buyer's explicit "add it anyway, quote it by hand".
   *  Send it ONLY after a 422 whose ``params.can_add_unpriced`` was
   *  true; the server grants it only for failures caused by the shop's
   *  own data, so sending it blindly changes nothing but hides which
   *  branch the UI believed it was on. */
  allow_unpriced?: boolean;
}

/**
 * PATCH /cart/lines/{id}. Separate from {@link AddLineRequest} because the
 * price override exists only on the edit path — POST silently ignores an
 * unknown field, so a shared type would let a caller believe it had priced a
 * brand-new line when nothing was written.
 *
 * ``material_code`` and ``quantity`` stay required in practice: the router
 * resolves them from the payload only (422 ``line_dimensions_missing``
 * otherwise), so an override-only body is not a valid PATCH.
 */
export interface UpdateLineRequest extends AddLineRequest {
  /** Admin-authored per-piece price in MINOR units, replacing what the
   *  pricing engine computed. Omit the key to leave any existing override
   *  alone; send explicit ``null`` to drop it and go back to the engine.
   *  Accepted only with a ``?quote_id=`` admin-builder context. */
  unit_price_override_minor?: number | null;
  /** KAN-1659 — proč admin cenu přepsal. Tytéž tři stavy a táž brána
   *  jako cena; bez přepisu se zahodí. */
  unit_price_override_reason?: string | null;
}

export type StockStatus =
  "in_stock" | "low" | "partial" | "out_of_stock" | "not_managed";

/** KAN-1876 — viz ``CartLine.price_list_change``. */
export interface CartLinePriceListChange {
  previous_unit_minor: number;
  current_unit_minor: number;
  currency_code: string;
}

export interface CartLine {
  id: number;
  material_code: string;
  /** Human label for the material; falls back to the code server-side.
   *  The code itself is an internal join key and is not shown. */
  material_display_name?: string;
  quantity: number;
  // Universal variant identity — the full per-axis dims dict; paint:
  // ``{}``. The legacy flat axis scalar field was dropped in
  // Phase II/5a — extract via ``lib/variantSignature.dimensionNumber()``.
  dimensions?: Record<string, unknown>;
  unit_price_czk: number;
  line_total_czk: number;
  // Per-process setup fee for this line — extracted from the frozen
  // price_breakdown snapshot. 0 when no setup was charged (variants
  // after the first, or legacy snapshots predating the per-process
  // series). CartSummaryV2 surfaces a "vč. setup XX Kč" sub-row per
  // operation when the sum across an item's lines is > 0.
  setup_fee_czk?: number;
  /** KAN-996 — non-null when an admin replaced the engine's per-piece
   *  price on this line, in MINOR units of the tenant's books currency.
   *  ``unit_price_czk`` above already carries the same (charged) amount,
   *  so a renderer that ignores this shows the right money; it just
   *  cannot say the number was authored rather than computed.
   *  Settable only from the admin quote builder — a public cart PATCH
   *  carrying it is refused with ``price_override_requires_admin_quote``. */
  unit_price_override_minor?: number | null;
  /** KAN-1659 — důvod přepisu ceny; ``null`` bez přepisu. */
  unit_price_override_reason?: string | null;
  /** KAN-1659 — co spočítal ceník, dokud platí přepis (jinak ``null``). */
  computed_unit_price_minor?: number | null;
  config_snapshot: Record<string, unknown> | null;
  // H2 #4 PR-B — per-line stock awareness banner. ``null`` when the
  // BE didn't compute one (legacy paths). FE renders no badge for
  // ``null`` or ``not_managed``.
  stock_status?: StockStatus | null;
  /** KAN-1839/1851 — dodací lhůta (dny) u `out_of_stock` a `partial`; null = obchod ji nenastavil. */
  stock_lead_time_days?: number | null;
  /** KAN-1851 — u `partial`: kolik je skladem (v jednotce `stock_uom`). */
  stock_available_qty?: number | null;
  stock_uom?: string | null;
  /** Customer-typed note for this variant (configurator → cart →
   *  admin). ``null`` when the customer didn't write anything. */
  customer_note?: string | null;
  /** Per-line delivery-ladder override (PR6/7 of the unified-ladder
   *  series). ``null`` = inherit the cart-wide rung from
   *  ``Order.delivery_selection``. Edited via the per-line picker
   *  popover in ``VariantRow``. */
  ladder_tier_id?: number | null;
  /** KAN-1047 — why this line has NO price: ``{error_code, params}``
   *  when the engine could not price it and the customer added it
   *  anyway to be quoted by hand. ``null`` on every priced line.
   *
   *  ⚠️ ``unit_price_czk`` / ``line_total_czk`` above are ZERO on such a
   *  row, so a renderer that ignores this field shows the part as FREE.
   *  Anything that prints a price or sums lines has to check it. */
  unpriced_reason?: {
    error_code: string;
    params?: Record<string, unknown>;
  } | null;
  /** KAN-1876 — jen v odpovědi ``PATCH /cart/lines/{id}``: úprava řádek
   * přecenila změněným ceníkem (zmrazená → nová cena/ks, minor). ``null``
   * = cena se změnila nanejvýš množstevním pásmem. */
  price_list_change?: CartLinePriceListChange | null;
  /** KAN-1027 — z čeho se ``unit_price_czk`` a ``line_total_czk``
   *  skládají, operaci po operaci. Košík vozil dvě čísla, která zákazník
   *  neuměl srovnat („85 Kč/ks" vs. „134 Kč celkem" při 1 ks); tohle je
   *  to, co mezi nimi chybělo.
   *
   *  ⚠️ NENÍ totéž co ``CartItem.operations[].unit_price_czk`` — ten je
   *  snapshot z okamžiku přidání do košíku (změřeno 0 a 1 Kč u řádku za
   *  134 Kč). Tohle je živá cena TOHOTO řádku, proti jeho materiálu a
   *  množství. Prázdné na zápisových odpovědích, které nejdou přes
   *  ``GET /cart``. */
  operation_prices?: CartLineOperationPrice[];
  /** Měnově neutrální protějšek ``line_total_czk`` (KAN-301), v MINOR
   *  jednotkách ``Cart.currency_code``. Nové čtecí cesty berou tohle —
   *  korunové pole je jeho zaokrouhlený stín. */
  line_total_minor?: number;
  /** KAN-1900 — cena za kus vč. řetězu PŘESNĚ v MINOR (``unit_price_czk``
   *  je její zaokrouhlený stín, 100,80 → 101). ``line_total_minor`` =
   *  tohle × ``quantity`` + příprava. */
  unit_price_minor?: number;
  /** KAN-1027 — popisky ke klíčům v ``dimensions``; týž tvar, jaký
   *  objednávkovým řádkům dala KAN-926 (``orderLineLabelMap`` je čte).
   *  ``dimensions`` zůstává strojovým klíčem, tohle čte zákazník.
   *  Prázdné na odpovědích mimo ``GET /cart``. */
  dimension_labels?: Record<string, OrderLineDimensionLabel>;
}

export interface CartLineOperationPrice {
  process_type: string;
  /** Za KUS, v MINOR jednotkách ``Cart.currency_code``. Součet přes
   *  všechny položky = ``CartLine.unit_price_czk``. Nová cenová pole jdou
   *  měnově neutrálním seamem (KAN-301) i vedle historických ``*_czk``;
   *  čti je přes ``moneyFromPair`` / ``minorToMajor``. */
  unit_price_minor: number;
  operation_id?: number | null;
  is_primary?: boolean;
  /** Jednorázově na řádek, ne za kus — proto vlastní pole. Právě on je
   *  ten rozdíl mezi kus-cenou a celkem u množství 1. Minor jednotky. */
  setup_fee_minor?: number;
}

export interface CartOperation {
  id: number;
  process_type: string;
  order_index: number;
  unit_price_czk: number;
  // Persisted per-op config (e.g. anodize ``color``). Round-trippable
  // — exposed so flows like Replace-DXF carryover can replay the op
  // onto a different item without losing the user's chain-specific
  // picks. The primary's cascaded identity (material, quantity, the
  // declared variant axes inside ``dimensions``) is stored here too
  // because the BE merges it from the primary op's snapshot at write
  // time.
  config_snapshot: Record<string, unknown> | null;
  /** KAN-1700 — hodnoty interaktivních vstupů, které zákazník změnil,
   *  ``{<klíč portu in_graph>: hodnota}``. Jen na primární operaci;
   *  chybí-li klíč, platí default modulu (kontrakt ``interactive_inputs``). */
  graph_inputs?: Record<string, unknown> | null;
  /** KAN-1708 — interaktivní vstupy vyřešené pro tenhle díl (jen primárka,
   *  ``null`` = není co měnit). Z nich kreslí ovládání prohlížeč (KAN-1703). */
  interactive_inputs?: InteractiveInputState[] | null;
  // BE-priced production duration for this op. Surfaced on the cart
  // view so a chain hydrated from the cart shows the same ETA the
  // add/update mutations return — replaces the old per-finish-slug
  // fallback table in the configurator's ChainEtaPreview. ``0`` when
  // the BE hasn't priced a duration (e.g. schema-driven module without
  // a throughput override).
  estimated_duration_seconds?: number;
  // KAN-459: ``estimated_duration_seconds`` cannot tell an authored ``0`` from
  // a step nobody has estimated yet (NULL on the row, KAN-267) — both arrive as
  // a number. This flag is the difference. It is data, not copy: whether the
  // customer sees "bez odhadu" is a product decision that has not been made.
  duration_estimate_missing?: boolean;
}

/**
 * Auto-routing recommendation from the BE rule engine (Horizon 2 #3).
 *
 * Stamped on ``ItemOperation.suggested_chain_snapshot`` for the primary
 * op when one or more ``chain_recommendation`` ``TenantRule`` rows fired
 * against the analyzer snapshot. Configurator pre-fills chain slots
 * from ``suggested_chain`` and falls back to the empty-chain UX when
 * the field is missing or the customer opts into manual selection.
 */
export interface SuggestedChainItem {
  process_type: string;
  config_hint?: Record<string, unknown>;
  matched_rule_ids?: number[];
}

export interface SuggestedChainSnapshot {
  schema_version: number;
  primary_process_type: string;
  suggested_chain: SuggestedChainItem[];
  matched_rule_ids?: number[];
  rationale?: string | null;
  evaluated_at?: string;
  payload_snapshot?: Record<string, unknown>;
}

export interface CartItem {
  // Opaque slug — same handle the FE builds the
  // ``DELETE /cart/items/{id}`` URL from. String, never integer.
  id: string;
  file_name: string;
  preview_url: string;
  analysis: AnalysisSnapshot;
  lines: CartLine[];
  // PR 21 of the modularisation roadmap. Empty for items with only
  // an implicit primary op (back-compat); populated when the
  // chain has finishing ops attached. Multi-op items render an
  // expandable sub-row in the cart UI.
  operations: CartOperation[];
  // Presigned URL to the raw mesh upload (.stl/.obj/.3mf). Cart wraps it
  // in ``MeshPreview`` so the thumbnail mirrors the configurator. Null
  // unless the module declares ``preview_type === "mesh"``, and for
  // legacy items where ``file_key`` is missing.
  mesh_preview_url?: string | null;
  // KAN-1401 — preview kind of THIS part (see ``Quote.preview_type``).
  preview_type?: PartPreviewType | null;
  // PR11 — count of analyzer warnings stamped on the primary op's
  // snapshot. Operation section header aggregates these into a single
  // warning pill so customers see how many parts need attention.
  warnings_count?: number;
  /**
   * KAN-301 — strom kontejnerů jako odkazy mezi PLOCHÝMI řádky.
   * ``cart.items`` zůstává plochý seznam; zanoření si vykreslí FE.
   *
   * Kontejner (sestava / kompletace) je položka, která má neprázdné
   * ``child_ids`` a prázdné ``lines`` — cena zůstává na dětech, takže
   * do mezisoučtu přispívá nulou. Kdo počítá cokoli přes ``items``,
   * musí kontejnery přeskočit, jinak vyrobí fantomový řádek.
   *
   * Volitelné, protože starší BE (a mocky v testech) pole neposílají.
   */
  parent_id?: string | null;
  child_ids?: string[];
  /** Násobnost na hraně k rodiči (KAN-297). U kořene 1. */
  quantity_per_parent?: number;
}

/** Jedno dítě v požadavku na náhled / složení sestavy (KAN-301). */
export interface AssemblyChildRequest {
  item_id: string;
  quantity_per_parent: number;
}

export interface AssemblyChildPreview {
  item_id: string;
  file_name: string;
  quantity_per_parent: number;
  /** Kolik kusů zákazník zadal (napříč konfiguracemi dílu). */
  available_quantity: number;
  /** Kolik jich spotřebují sestavy. */
  consumed_quantity: number;
  /** Zbytek → samostatný řádek náhradních dílů. */
  spare_quantity: number;
  /** Cena za KUS dítěte. */
  unit_price: number;
  /** Cena tohohle dílu v JEDNÉ sestavě. */
  per_assembly_price: number;
  spare_total: number;
}

export interface AssemblyPreview {
  assembly_count: number;
  /** „Kolik stojí sestava" — jednorázový přípravný poplatek se neúčtuje
   *  na sestavu, ale na položku, takže tu záměrně NENÍ. */
  assembly_unit_price: number;
  assemblies_total: number;
  spares_total: number;
  total: number;
  children: AssemblyChildPreview[];
}

export interface AssemblyComposeResponse {
  container_id: string;
  preview: AssemblyPreview;
}

/** Per-rate row of the §29 ZDPH "Rekapitulace DPH" table. */
/**
 * Jeden objednávkový příplatek, který si zákazník může přikoupit
 * (``GET /cart/addons``). Dnes jediný zdroj nabídek je protokol
 * o kontrole, ale FE o tom neví — vykresluje, co přijde.
 */
export interface CartAddon {
  key: string;
  label_i18n: Record<string, string>;
  price_czk: number;
  selected: boolean;
}

export interface CartAddons {
  addons: CartAddon[];
}

export interface Cart {
  order_id: number | null;
  order_number: string | null;
  items: CartItem[];
  subtotal_czk: number;
  total_czk: number;
  /** KAN-928 — remembered answer of the assembly gate; null = not asked
   *  yet (or a one-part cart the gate never asks). */
  assembly_intent?: AssemblyIntent | null;
  max_extra_variants_per_item: number | null;
  /**
   * Order-level sleva / přirážka (CZK), které ``subtotal_czk`` výše UŽ
   * obsahuje — BE je resolvuje ze slevových žebříků + tenant pravidel
   * (``services.order_adjustments``). ``CartSummaryV2`` je vykresluje
   * jako vlastní řádky rozpadu; bez nich se sleva projeví jen menším
   * mezisoučtem a zákazník o ní neví. ``0`` = nic nepadlo.
   */
  order_discount_czk: number;
  order_surcharge_czk: number;
  /**
   * Objednávkové příplatky (KAN-142) — co si zákazník přikoupil k CELÉ
   * zakázce. ``total_czk`` je UŽ obsahuje, ale ``subtotal_czk`` ne (ten
   * je součet řádků), takže se musí vykreslit jako vlastní řádek
   * rozpadu — jinak souhrn nesouhlasí a zákazník vidí nevysvětlený
   * rozdíl. ``0`` = nic přikoupeného.
   */
  addons_czk: number;
  /**
   * VAT breakdown pro cart summary (PR 1.2). ``vat_country_code`` je
   * default ze ``OrganizationDeliveryCountry`` před checkoutem nebo
   * ``shipping_address.country_code`` po.
   */
  vat_czk: number;
  /** Decimal as string ("21.00", "19.00"). 0.00 = no VAT applied. */
  vat_rate_pct: string;
  vat_country_code: string | null;
  /**
   * PR12 — aggregated kg of every variant. Cart summary "X kg" pill
   * and downstream shipping price preview both consume the same
   * number checkout will charge against. ``0`` for empty/un-priced carts.
   */
  total_weight_kg?: number;
  /**
   * KAN-606 — the AUTHORITATIVE amounts, in minor units of `currency_code`.
   * Every `*_czk` field above is their rounded shadow: since the engine
   * aggregates in minor units, a cart owing 28.77 in tax reports `vat_czk: 29`
   * and `vat_minor: 2877`, and only the latter matches what checkout stamps.
   * Prefer these; the whole-unit halves are the fallback for a response
   * predating them (`moneyFromPair`).
   */
  subtotal_minor?: number;
  total_minor?: number;
  vat_minor?: number;
  order_discount_minor?: number;
  order_surcharge_minor?: number;
  addons_minor?: number;
  /**
   * KAN-738 — dorovnání minimální objednávky (engine top-up), MINOR
   * jednotky `currency_code`. Total i základ DPH ho UŽ obsahují;
   * vlastní řádek rozpadu je jediné, co brání „Mezisoučet 20 /
   * DPH 105 / Celkem 605" bez vysvětlení. ``0`` = nad minimem.
   */
  minimum_order_topup_minor?: number;
  /** KAN-2055 — dorovnání minim skupin operací (řádek za pravidlo), MINOR
   *  jednotky ``currency_code``. Total i základ DPH je UŽ obsahují. */
  operation_group_topups?: OperationGroupTopup[];
  /** KAN-1428 — tvrdá hranice objednávky v MINOR jednotkách
   *  ``currency_code``; pod ní pokladna objednávku neodešle. ``null`` =
   *  tenant hranici nemá. */
  min_subtotal_minor?: number | null;
  /** KAN-1692 — verdikt serveru: pokladna by objednávku odmítla
   *  (`order_below_min_subtotal`, včetně dorovnání minima). Klient
   *  hranici sám nepočítá. */
  below_min_subtotal?: boolean;
  /** KAN-1827 — pokladna by objednávku odmítla kvůli HORNÍ hranici
   *  (`order_above_max_subtotal` / `order_exceeds_max_weight` /
   *  `order_items_exceed_max`); `null` = v mezích. Verdikt je serverový,
   *  klient hranici nepočítá. */
  limit_violation?: string | null;
  /** Parametry kódu výš — částky v MAJOR jednotkách, hmotnost v kg. */
  limit_violation_params?: Record<string, unknown> | null;
  currency_code?: string;
  /**
   * Validity window pro „Uložit jako nabídku" (dny). Resolved BE-side
   * z ``QuoteSettings`` (tenant override → platform default → 14).
   * CartSummaryV2 + SaveAsQuoteModal vykreslují pluralizovaný caption
   * „Platnost nabídky {{count}} dní" — číslo musí matchovat ``expires_at``
   * stamp, který vrátí ``POST /cart/save-as-quote``.
   */
  quote_validity_days: number;
  /**
   * Customer-typed free-text note for the whole order (cart summary
   * textarea → admin order detail). ``null`` when the customer hasn't
   * typed anything. Live-saved via ``PATCH /cart/note``.
   */
  customer_note?: string | null;
}

// --- Pricing (POST /pricing/line) -------------------------------------

/** Jeden krok cesty za primárkou pro náhled ceny (KAN-121). Server
 *  z nich spočítá cenu celé položky; klient nic nesčítá. */
export interface PathOperationInput {
  process_type: string;
  config?: Record<string, unknown>;
}

export interface LineInput {
  material_code: string;
  quantity: number;
  /** Full analyzer snapshot — THE geometry channel (F2). The BE derives
   *  reserved physics conveniences and the process's formula reads the
   *  facts it declares; no typed per-process geometry fields. */
  analysis: Record<string, unknown>;
  is_first_line_of_item?: boolean;
  /**
   * Process-specific knobs. The line's variant axes ride inside
   * ``dimensions`` under their own admin-declared keys —
   * there is no top-level axis slot, and the BE resolves it from there.
   */
  extra_config?: Record<string, unknown>;
  /** Cesta za primárkou. Prázdná / vynechaná = čistě primární náhled. */
  path_operations?: PathOperationInput[];
}

/**
 * Note: Pydantic v2 emits Decimals as strings, not numbers. The FE
 * converts with Number() at the display boundary — never use the raw
 * values in arithmetic.
 */
// One named per-piece output of a pricing formula (``formula_graph``).
// ``amount_czk`` is the per-piece value AFTER markup × delivery × discount —
// named per_piece outputs sum (±1 CZK/row) to ``unit_price_czk``. ``name``
// null = unnamed (the FE skips rendering it and falls back to the component
// breakdown). ``setup_base`` outputs are NOT here (they are only the base of
// the setup fee). ``per_batch`` outputs (KAN-1959) ARE, with mode
// "per_batch": charged once per line after margin and already INSIDE
// ``setup_fee_czk`` — render them as one-time rows, never add them again.
export interface FormulaOutputBreakdown {
  name: string | null;
  /** KAN-1964 — překlady názvu per jazyk platformy (snímek je nese s sebou). */
  name_i18n?: Record<string, string> | null;
  /** KAN-1964 — název v jazyce požadavku, jak ho vybralo API. Zobrazuje se
   *  tohle; ``name`` je identita výstupu a text základního jazyka. */
  display_name?: string | null;
  mode: string; // "per_piece" | "per_batch"
  amount_czk: number;
  amount_minor?: number;
  currency_code?: string;
}

// Čím byla linie spočítaná (KAN-270). Zbytek breakdownu nese VÝSLEDEK, tenhle
// blok VSTUP: otisk formule, verzi definice a rozlišené sazby, zmrazené spolu
// s cenou. Čistě auditní — na cenu nemá vliv a nikdy se nepřepočítává.
export interface FormulaProvenance {
  /** Sha256 kanonického ``pricing_formula_schema`` v okamžiku naceňování.
   *  ``null`` = proces neměl autorovanou formuli (sandbox sentinel). */
  formula_fingerprint: string | null;
  /** ``ProcessModuleDefinition.version`` při naceňování. Otisk říká *co*,
   *  verze *pokolikáté*. */
  formula_version: number | null;
  /** ``rate_tables.id`` řádku, kterým se linie nakonec nacenila (proces může
   *  mít víc kandidátních tabulek a engine bere nejlevnější výslednou cenu). */
  rate_table_id: number | null;
  /** Rozlišené sazby ``{rate_key: hodnota}`` tak, jak je engine dosadil.
   *  Hodnoty jsou STRINGY desetinného čísla, ne čísla — ``0.1`` přes JS
   *  ``number`` je ``0.1000000000000000055`` a auditní záznam sazby nesmí
   *  lhát v tom, co admin zadal. */
  rates: Record<string, string>;
}

export interface LinePriceBreakdown {
  // The line's variant identity is NOT echoed here — read it from the line's
  // ``dimensions`` (the single source), not from the price breakdown.
  material_code: string;
  quantity: number;

  setup_fee_czk: number;
  subtotal_czk: number;

  // KAN-284 b2 — the same four amounts as integers in the MINOR unit of
  // ``currency_code``, which is the only spelling that can express a
  // sub-unit price. Optional: frozen snapshots written before the
  // transition carry only the ``*_czk`` half. Readers use
  // ``lib/money.moneyFromPair`` so the minor value wins when present.
  unit_price_minor?: number | null;
  line_total_minor?: number | null;
  setup_fee_minor?: number | null;
  subtotal_minor?: number | null;
  currency_code?: string | null;

  // The one per-component view — a dict keyed by the process formula's
  // own component vocabulary (admin-authored data; e.g. {material,
  // machine} or {bends}). There are no named or positional cost slots.
  cost_breakdown?: Record<string, number>;

  // Same keys as ``cost_breakdown`` but values are per-piece AFTER
  // markup × delivery × discount, rounded — i.e. components of the
  // headline ``unit_price_czk`` (sum ≈ unit_price within ±1 CZK
  // tolerance per key). FE breakdown rows read this so the customer
  // can manually reconcile rows × qty + setup → line_total. Empty
  // for legacy snapshots; fall back to ``cost_breakdown``.
  effective_breakdown_czk?: Record<string, number>;

  // Same map in minor units (KAN-284 b2); wins over the ``_czk`` twin.
  effective_breakdown_minor?: Record<string, number>;

  // Admin-authored display label per ``cost_breakdown`` key
  // (``CostComponentSpec.label`` from the process's pricing formula). Keyed
  // identically to ``cost_breakdown``; only carries components whose formula
  // declares a non-empty label. ``PriceBreakdown`` renders this as the row
  // label — the single source of truth for wording — falling back to a
  // built-in i18n label for known keys, then the humanized key. Empty for
  // legacy snapshots / formulas that declare no labels.
  cost_labels?: Record<string, string>;

  // Named per-piece outputs of the pricing formula. When the admin names the
  // formula's outputs, ``PriceBreakdown`` renders these as the breakdown rows
  // INSTEAD of the component ``cost_breakdown`` (the admin picks the row
  // granularity by naming outputs). Empty for flat / unnamed formulas → fall
  // back to the component breakdown.
  formula_outputs?: FormulaOutputBreakdown[];

  markup: string;
  delivery_multiplier: string;
  discount_pct: string;

  unit_price_czk: number;
  line_total_czk: number;

  /** Čím to bylo spočítané (KAN-270). ``null``/absent u KAŽDÉHO snapshotu
   *  vystaveného před KAN-270 — otisk se zpětně dopočítat nedá, takže
   *  „nabídka bez otisku" je legitimní stav a čtenář ho musí unést (vykreslí
   *  rozpad bez sekce rozdílu, ne chybu). */
  formula_provenance?: FormulaProvenance | null;

  /** Doba výroby, se kterou tahle cena počítala (KAN-1439) — v sekundách,
   *  na kus a za dávku. Cena a plán berou číslo z TÉHOŽ běhu vzorce, takže
   *  rozpad ceny a rozvrh dílny nemůžou tvrdit jiný čas. ``null``/absent =
   *  odhad NENÍ (což není nula: autorovaná nula je krok, který se nepočítá),
   *  a ``duration_missing_reason`` řekne proč — kódem chyby enginu, typicky
   *  ``formula_var_unresolved`` s klíčem, na který vzorec doby čeká. */
  duration_per_piece_seconds?: number | null;
  duration_setup_seconds?: number | null;
  duration_missing_reason?: Record<string, unknown> | null;

  /** ``/pricing/line`` only: line weight (per-piece × qty) from the
   *  process's admin-defined ``weight_kg`` physical-quantities formula —
   *  the same one checkout's shipping weight sums. ``null``/absent when
   *  the process declares no weight formula or inputs are incomplete. */
  weight_kg?: number | null;

  /** ``/pricing/line`` only: součet cen kroků cesty za primárkou, UŽ
   *  započítaný v ``unit_price_czk`` i ``line_total_czk``. Je tu zvlášť
   *  jen kvůli rozpadu ceny — klient nesčítá (KAN-121). */
  path_surcharge_czk?: number;
}

// --- „Proč tato cena" (KAN-271) --------------------------------------
//
// ⛔ Historická cena se NEPŘEPOČÍTÁVÁ. ``breakdown`` je to, co bylo zmrazeno
// při vystavení; „dnešní" hodnoty žijí výhradně v ``drift`` a jsou tím
// oddělené i v datech, ne jen v UI.

/** Jedna sazba: co bylo zmrazeno vs. co platí dnes. */
export interface RateDriftRow {
  rate_key: string;
  frozen_value: string;
  /** ``null`` = sazba dnes v tabulce NENÍ. Není to nula — nula je platná
   *  sazba a věta „klesla z 450 na 0" by popisovala něco, co se nestalo. */
  current_value: string | null;
  changed: boolean;
  /** KAN-601 — kdy a kdo sazbu naposled hnul (ISO 8601, z ``price_history``).
   *  ``null`` u nezměněné sazby i tam, kde log řádek nemá: začal se plnit
   *  s KAN-462, takže starší změna v něm není. Chybí ⇒ větu vynech. */
  changed_at: string | null;
  /** Jméno, nebo e-mail. ``null`` i když ``changed_at`` je vyplněné —
   *  uživatel mohl být mezitím smazán (FK ``ON DELETE SET NULL``). */
  changed_by: string | null;
}

/** Co se od vystavení nabídky změnilo. */
export interface PriceFormulaDrift {
  formula_changed: boolean;
  fingerprint_frozen: string | null;
  fingerprint_current: string | null;
  version_frozen: number | null;
  version_current: number | null;
  rates: RateDriftRow[];
  /** Souhrn — vykreslit sekci rozdílu vůbec, nebo jen „beze změny". */
  any_change: boolean;
}

export interface PriceExplanation {
  order_number: string;
  operation_id: number;
  process_type: string;
  /** Zmrazený rozpad tak, jak byl uložen (rehydratovaný, nepřepočítaný). */
  breakdown: LinePriceBreakdown;
  /** Vstupy výpočtu — analyzer snapshot + konfigurace linky. */
  analysis: Record<string, unknown>;
  config: Record<string, unknown>;
  /** ``null`` u nabídky BEZ otisku (vystavené před KAN-270) — vykreslí se
   *  rozpad bez sekce rozdílu, ne chyba. */
  drift: PriceFormulaDrift | null;
}

/** P2 ceník+sklad → COGS: per-variantní pořizovací náklad (cost home).
 *  WAC píší příjmy (PO / manuální pohyby s cenou); ``manual_unit_cost``
 *  je admin override editovaný přes ``PUT /admin/material-costs``.
 *  ``effective_unit_cost`` = precedence manuál → WAC → null.
 *
 *  ⚠️ ``PUT`` vrací ``AdminMaterialVariantCost | null`` — ``null`` když
 *  po volání nezůstal žádný řádek (zrušený přepis na variantě, která
 *  nemá ani vážený průměr). Prázdný řádek se schválně neukládá: výpis
 *  variant sjednocuje sklad + náklad + ceník, takže by MINTNUL variantu,
 *  kterou tenant nezaložil (KAN-1075). */
export interface AdminMaterialVariantCost {
  id: number;
  material_id: number;
  dimensions: Record<string, unknown>;
  variant_signature_key: string | null;
  cost_uom: string;
  wac_unit_cost: number | null;
  manual_unit_cost: number | null;
  effective_unit_cost: number | null;
  updated_at: string;
}

export interface AdminMaterialVariantCostListResponse {
  items: AdminMaterialVariantCost[];
}

/** KAN-1074 — per-variantní prodejní cena položky prodávané SAMOSTATNĚ
 *  (``GET``/``PUT /admin/material-sale-prices``). Jmenovatel se bere
 *  z registry položky (nikdy z konstanty, viz KAN-938); ``PUT`` s
 *  ``unit_price: null`` řádek MAŽE — pod prodejní cenou není žádná
 *  vrstva, na kterou by se dalo spadnout. */
export interface AdminMaterialVariantSalePrice {
  id: number;
  material_id: number;
  dimensions: Record<string, unknown>;
  variant_signature_key: string | null;
  price_uom: string;
  unit_price: number;
  currency_code: string;
  updated_at: string;
}

export interface AdminMaterialVariantSalePriceListResponse {
  items: AdminMaterialVariantSalePrice[];
}

// --- M4 material-variant aggregation ----------------------------------
// (GET /admin/materials/{id}/variants and /variant?variant_signature_key=)
// The FE unification: ceník + sklad projected as one "položka / varianta"
// entity keyed on the shared variant_signature_key.

export interface MaterialVariantPricingRow {
  pricing_id: number;
  process_type: string;
  is_active: boolean;
  markup: number | null;
  pricing_extra: Record<string, unknown>;
}

export interface MaterialVariantStockFormat {
  stock_id: number;
  /** Zobrazení formátu složené z čísel os („1.5×3 m"). */
  label: string;
  axes: Record<string, unknown>;
  stock_uom: string | null;
  qty_on_hand: number;
  qty_reserved: number;
  qty_available: number;
  low_stock_threshold: number | null;
  lead_time_days: number | null;
}

export interface MaterialVariantSummary {
  material_id: number;
  variant_signature_key: string | null;
  dimensions: Record<string, unknown>;
  // null = jednotku neurčuje ani varianta, ani položka, ani její kategorie
  stock_uom: string | null;
  // stock roll-up (null/zero when the variant is priced/costed but unstocked)
  stock_id: number | null;
  qty_on_hand: number;
  qty_reserved: number;
  qty_available: number;
  low_stock_threshold: number | null;
  /** KAN-1901 — dodací lhůta varianty (dny); null = dědí z materiálu
   * / výchozí lhůty obchodu. */
  lead_time_days?: number | null;
  is_low: boolean | null;
  // ms03 — uložený režim řádku varianty (none|qty|qty_place);
  // null = varianta nemá vlastní skladový řádek
  tracking_mode: string | null;
  inventory_managed: boolean | null;
  // KAN-1904 — EFEKTIVNÍ režim varianty (BE ``variant_tracking_mode``):
  // tentýž výrok, kterým se řídí rezervace při zaplacení. Varianta bez
  // skladového řádku je „none" i u položky se zapnutým skladem.
  effective_tracking_mode: string;
  /** KAN-2113 — formáty varianty skladem (osy „jen sklad", KAN-2045).
   * Qty výš jsou jejich součet; každý formát je vlastní skladový řádek. */
  stock_formats?: MaterialVariantStockFormat[];
  // acquisition cost (precedence manual → WAC → null)
  wac_unit_cost: number | null;
  manual_unit_cost: number | null;
  effective_unit_cost: number | null;
  cost_uom: string | null;
  // KAN-1074 — prodejní cena varianty PRODÁVANÉ SAMOSTATNĚ (bez operace).
  // Není to fallback odvozené ceny ani se s ní nesčítá: odvozená platí,
  // když je položka vstupem operace, tahle když se prodává tak, jak je.
  // Trojice cestuje spolu — číslo bez jmenovatele a měny není cena.
  // null = nikdy nezadaná.
  sale_unit_price: number | null;
  sale_price_uom: string | null;
  sale_currency_code: string | null;
  // distinct active process_types priced on this variant
  priced_process_count: number;
  // planned sell coverage: distinct nodes + markup-derived margin range
  sell_node_count: number;
  margin_pct_min: number | null;
  margin_pct_max: number | null;
}

export interface MaterialVariantMaterial {
  id: number;
  code: string;
  display_name: string | null;
  inventory_managed: boolean;
  process_types: string[];
  stock_uom: string | null;
}

export interface MaterialVariantListResponse {
  material_id: number;
  material_code: string;
  material_display_name: string | null;
  variant_param_keys: string[];
  items: MaterialVariantSummary[];
}

export interface MaterialRollupItem {
  material_id: number;
  variant_count: number;
  in_stock_variant_count: number;
  low_stock_variant_count: number;
  priced_variant_count: number;
  // Union of dimension keys — the „Parametry variant" chips.
  variant_param_keys: string[];
  // Total on-hand qty when all stock rows share one uom; else both null
  // and the list falls back to the in-stock/total counts.
  qty_on_hand_total: number | null;
  stock_uom: string | null;
  // KAN-1074 — rozpětí prodejních cen položky prodávané samostatně.
  // Jednotka a měna jen když se na nich VŠECHNY naceněné varianty shodnou
  // (rozpětí ve dvou jmenovatelích není rozpětí — táž zásada jako
  // ``qty_on_hand_total`` výš).
  sale_priced_variant_count: number;
  sale_price_min: number | null;
  sale_price_max: number | null;
  sale_price_uom: string | null;
  sale_currency_code: string | null;
  // Planned sell coverage (design pill „3 uzly · 31–38 %").
  sell_node_count: number;
  margin_pct_min: number | null;
  margin_pct_max: number | null;
}

// KAN-1856 — lidský název osy, kterou proces deklaruje aliasem nad
// vlastností materiálu (registr vlastností alias nezná → čip „Parametry
// variant" ukazoval syrový klíč). Stejné pravidlo jako ``label_cs`` osy
// v detailu položky (``/materials/{id}/variant-axes``).
export interface MaterialRollupAxisLabel {
  axis_key: string;
  label_cs: string;
  unit: string | null;
}

export interface MaterialRollupResponse {
  items: MaterialRollupItem[];
  axis_labels: MaterialRollupAxisLabel[];
}

/** One recent covered order line — plán (finalize snapshot) vs.
 *  skutečnost (same qty at the variant's current effective cost). */
export interface MaterialVariantMarginLine {
  order_id: number;
  order_serial: string | null;
  /** Admin's Map label of the producing node; null → fall back to
   *  `node_process_type` via useProcessTypeLabel. */
  node_label: string | null;
  node_process_type: string | null;
  expected_cost: number;
  actual_cost: number | null;
  deviation_pct: number | null;
  margin_pct: number;
}

export interface MaterialVariantMargin {
  window_days: number;
  revenue_covered_czk: number;
  material_cost: number;
  margin_czk: number;
  margin_pct: number;
  lines_covered: number;
  lines_total: number;
  lines: MaterialVariantMarginLine[];
}

export interface MaterialVariantDetail {
  material: MaterialVariantMaterial;
  variant: MaterialVariantSummary;
  pricing_rows: MaterialVariantPricingRow[];
  // owning stock row id → FE reuses GET /admin/stock/{id}/context
  stock_context_id: number | null;
  // Marže section — realised margin from paid order lines (null =
  // no paid line in the window; FE shows an empty note, not zeros).
  margin: MaterialVariantMargin | null;
}

// --- M4 rate_table (SSoT sell-rate entity) — /admin/rate-tables --------

export interface RateTableNodeRead {
  node_layout_id: number;
  node_id: string | null;
  process_type: string | null;
  label: string | null;
}

export interface RateTableVariantRef {
  material_id: number;
  // null = every variant of the material
  variant_signature_key: string | null;
}

/** KAN-441 — read-side variant ref carries display info the write-side
 *  ``RateTableVariantRef`` doesn't need. NULL when the material row no
 *  longer exists (deleted since assignment). */
export interface RateTableVariantRead extends RateTableVariantRef {
  material_code: string | null;
  material_display_name: string | null;
}

export interface RateTable {
  id: number;
  name: string;
  process_type: string;
  /** KAN-1528 — `null` = tenant se nerozhodl a násobí se
   *  `effective_markup` (katalogový default). Do té doby se
   *  „nerozhodnuto" psalo jako `1.000`, takže majitel v adminu četl
   *  jedničku a účtovalo se mu 1.3 — obě čísla v jedné odpovědi. */
  markup: number | null;
  /** Čím se cena DOOPRAVDY násobí. */
  effective_markup: number;
  /** Odkud `effective_markup` je — bez toho je to jen další číslo. */
  markup_source: "table" | "catalog_default";
  rate_extra: Record<string, string>;
  /** KAN-1604 — jméno a jednotka sazby z palety pro klíče `rate_extra`.
   *  Klíč, který paleta nezná, chybí — pak se ukáže klíč. */
  rate_labels?: Record<string, { label: string; unit: string }>;
  /** W4 (P1a) — nákladová strana vedle prodejní. Prázdno = tenant
   *  náklady nedeklaroval; absorpce pak mlčí, netvrdí nulu. */
  cost_extra: Record<string, string>;
  /** R4a: tabulka z klonu šablony — čísla nikdo nepotvrdil
   *  („zkontroluj čísla"). Edit sazeb flag shazuje na BE.
   *
   *  ⚠️ Červený check „Ceník" to NEZNAMENÁ. Rozhodnutí vlastníka C to
   *  kdysi říkalo, ale KAN-1097 ho nahradilo: seed je ORANŽOVÝ a prodej
   *  neblokuje. Zelenou dělá až zkušební nacenění
   *  (``pricing_dryrun``), ne editace sazby. Tenhle komentář lhal do
   *  KAN-1490 a próza u něj lhala i v UI. */
  is_placeholder: boolean;
  nodes: RateTableNodeRead[];
  variants: RateTableVariantRead[];
  created_at: string;
  updated_at: string;
}

export interface RateTableListResponse {
  items: RateTable[];
}

export interface RateTableCreateRequest {
  name: string;
  process_type: string;
  /** Vynecháno / `null` = „nerozhodnuto", ne 1.0 (KAN-1528). */
  markup?: number | null;
  rate_extra?: Record<string, string>;
  cost_extra?: Record<string, string>;
  node_layout_ids?: number[];
  variants?: RateTableVariantRef[];
}

/** KAN-1020 — one markup / rate change across every table of a process.
 *  Rate bags MERGE key-by-key server-side, so a key this body does not name
 *  keeps its per-table value; ``null`` removes a key. */
export interface RateTableBulkRatesRequest {
  process_type: string;
  /** Omitted = every table of the process in this tenant. */
  rate_table_ids?: number[];
  markup?: number;
  rate_extra?: Record<string, string | null>;
  cost_extra?: Record<string, string | null>;
}

export interface RateTableBulkRatesResponse {
  updated: number;
  items: RateTable[];
}

export interface RateTableUpdateRequest {
  name?: string;
  /** `null` vrátí tabulku na katalogový default (KAN-1528). */
  markup?: number | null;
  // KAN-1524 — bagy se PATCHUJÍ klíč po klíči, stejně jako v bulk-rates:
  // nejmenovaný klíč si drží hodnotu, ``null`` klíč odstraní. Poslat celý
  // bag je pořád v pořádku, jen už tím nic nezmizí implicitně.
  rate_extra?: Record<string, string | null>;
  cost_extra?: Record<string, string | null>;
  // REPLACE the assignment set (editor full-set write)…
  node_layout_ids?: number[];
  variants?: RateTableVariantRef[];
  // …or APPEND to it (attach flows) — server-side de-dupe in one
  // transaction, so concurrent attaches can't drop each other's rows.
  add_node_layout_ids?: number[];
  add_variants?: RateTableVariantRef[];
}

/**
 * POST /admin/rate-tables/{id}/narrow — split one variant off a shared
 * table so it can be repriced alone (KAN-1071).
 *
 * The fork inherits the rates AND the node scope verbatim, so nothing
 * moves until it is edited; narrowing changes WHO a rate applies to,
 * never what it is. Responds with the new table.
 */
export interface RateTableNarrowRequest {
  material_id: number;
  /** null = the parameterless variant. */
  variant_signature_key?: string | null;
  /** Names the fork; defaults to the source table's name. */
  name?: string;
}

// Skill-matrix node summary (GET /admin/capabilities/by-node) — used by
// the "Přidat do uzlu" picker.
export interface NodeCapabilitySummary {
  node_layout_id: number;
  node_id: string;
  process_type: string;
  label_override: string | null;
}

export interface NodeCapabilitySummaryResponse {
  items: NodeCapabilitySummary[];
}

// --- Process modules (GET /api/v1/processes) -------------------------
//
// One entry per registered ``ProcessModule`` on the backend. Powers
// the landing picker (PR 16) + the chain dropdown (PR 20).

export interface ProcessLeadTime {
  dispatch_days: number;
  delivery_days: number;
}

/** One file a process accepts (multi-file F4) — drives a public dropzone slot. */
export interface FileInput {
  /** ``file_inputs`` key ("source_file" = primary); sent as ``extra_file_keys``. */
  key: string;
  label: string;
  accepted_file_types: string[];
  max_upload_mb: number;
  required: boolean;
}

export interface Process {
  /** ``process_type`` slug — data-driven, no built-in vocabulary. */
  name: string;
  slug: string;
  capability_key: string;
  display_name: string;
  description: string;
  icon: string;
  /**
   * Platform-managed icon asset URL (R2-served SVG/PNG). When set, FE
   * should render this as an ``<img>`` instead of the static ``icon``
   * Lucide name. Returns ``null`` when no icon has been uploaded (FE
   * falls back to ``icon`` or a letter glyph).
   *
   * Do KAN-1052 tudy tekl i per-tenant override, odlišený přes
   * ``icon_source``; ikona platformní operace je platformní, takže
   * ``icon_source`` je dnes vždycky ``"platform"`` nebo ``null``.
   */
  icon_url?: string | null;
  icon_source?: "platform" | null;
  /**
   * Built-in library icon token (``<icon>`` / ``<icon>:black`` / …) the
   * platform admin picked from ``processIconLibrary``. Rendered only when
   * ``icon_url`` is unset — an uploaded asset always wins. The bare token
   * is inked with ``operation_category_color``; ``:black`` / ``:white``
   * force a monochrome icon on transparent background.
   */
  icon_key?: string | null;
  /**
   * Demo/"try sample file" URL (platform default or tenant override). When
   * set, the service-page dropzone shows a "Vyzkoušet na ukázkovém souboru"
   * CTA that fetches this file and feeds it into the analyzer. ``null`` ⇒ no
   * CTA.
   */
  sample_file_url?: string | null;
  color: string;
  /**
   * Material-axis slug (``plech`` / ``plast`` / ``kov`` / …) — drives
   * the landing ProcessPicker chip filter. Source of truth is the
   * platform-managed ``platform_material_categories`` table; admins
   * can add / rename / recolor entries without a deploy.
   */
  material_category: string;
  /**
   * Full set of material categories this process belongs to — always
   * includes ``material_category``. Source of truth is the skill envelope
   * (``platform_skill_catalog.envelope_json.material_categories``), with the
   * primary unioned in BE-side. Drives the landing ProcessPicker chip
   * filter: the process appears under any of these. Optional for back-compat
   * with older payloads/fixtures (callers fall back to ``[material_category]``).
   */
  material_categories?: string[];
  /**
   * Platform-admin-managed operation-category slug (e.g. "cutting",
   * "additive"). Backed by
   * ``ProcessModuleDefinition.operation_category`` — a different axis
   * from ``material_category`` above (operation type vs material).
   * ``null`` only if the operation has no definition row, which
   * shouldn't happen for any built-in process.
   */
  operation_category?: string | null;
  /**
   * Localized label for ``operation_category`` (e.g. "Řezání",
   * "Aditivní výroba"). Read from ``PlatformOperationCategory.label``
   * — platform admin can edit labels and add new categories, so the
   * FE never owns this string.
   */
  operation_category_label?: string | null;
  /** Optional English override of the category label (null ⇒ fall back to
   * ``operation_category_label``). Picked by ``i18n.language`` in the cart. */
  operation_category_label_en?: string | null;
  /**
   * One sentence about the category, authored in Platform admin. The
   * marketing landing groups its operation list by category and prints this
   * under the group name; ``null`` ⇒ the group shows its name alone rather
   * than filler prose. Same English-override rule as the label.
   */
  operation_category_description?: string | null;
  operation_category_description_en?: string | null;
  /** Per jazyk platformy ``{kód jazyka: text}`` (KAN-1804); ``*_en`` je zrcadlo. */
  operation_category_label_i18n?: Record<string, string>;
  operation_category_description_i18n?: Record<string, string>;
  /** Hex colour of the platform operation category ("Systémové kategorie").
   * Single tint source for the library icon's category variant. */
  operation_category_color?: string | null;
  landing_order: number;
  is_available: boolean;
  accepted_file_types: string[];
  requires_file: boolean;
  /** Per-process max upload size (MB); 0/undefined → FE default. */
  max_upload_mb?: number;
  /**
   * Multi-file (F4): every file the process accepts, one dropzone slot each.
   * The flat accepted_file_types/requires_file/max_upload_mb above mirror the
   * PRIMARY (first) entry. A single-file process has one entry (key
   * "source_file"); a multi-file process lists each named input.
   */
  file_inputs?: FileInput[];
  lead_time?: ProcessLeadTime | null;
  /**
   * JSON configurator schema declared on the module's
   * `ProcessModuleDefinition` row. The FE `DynamicConfigurator`
   * renders every process from this — there are no hand-coded
   * per-process components.
   */
  configurator_schema?: Record<string, unknown>;
  /**
   * Kind of binary preview the module's adapter renders: `svg`
   * (2D vector), `mesh` (3D model URL), `none` (placeholder).
   */
  preview_type?: "svg" | "mesh" | "none";
  /**
   * Whether the customer configurator renders the "Tok výroby"
   * production-flow card for this operation. Derived BE-side from the
   * operation's ``visualization_schema`` — true iff it declares a
   * ``production_flow`` block. Opt-in: absent/false hides the card.
   */
  show_production_flow?: boolean;
}

/**
 * One operation on the platform marketing landing — ``GET
 * /public/platform-operations`` (KAN-1600). Exactly what ``PlatformSegments``
 * renders; the tenant catalog (``Process``) is not served on the platform host.
 */
export interface PlatformOperation {
  slug: string;
  display_name: string;
  description: string;
  accepted_file_types: string[];
  operation_category?: string | null;
  operation_category_label?: string | null;
  operation_category_label_en?: string | null;
  operation_category_color?: string | null;
  operation_category_description?: string | null;
  operation_category_description_en?: string | null;
  operation_category_label_i18n?: Record<string, string>;
  operation_category_description_i18n?: Record<string, string>;
  /** The admin-picked Ingot icon — the landing switcher's tile (KAN-1962). */
  icon_key?: string | null;
  /** Operation identity — the onboarding catalog's key; ``slug`` is the
   *  landing page's URL slug (KAN-1962). */
  name?: string;
}

/**
 * ``Process`` plus what only the admin reads — ``GET /admin/process-catalog``
 * behind the tenant-admin gate (KAN-1471). Never on an anonymous response.
 */
export interface AdminProcess extends Process {
  /**
   * Material default density (kg/m³) declared on the module — seeds the admin
   * pricing "add material" form. ``null`` when the module declares none (FE
   * falls back to a neutral default). Replaces a hardcoded per-process branch.
   */
  default_density_kg_m3?: number | null;
  /**
   * Artifact I/O kontrakt — vstupní požadavky na vlastnosti polotovaru a
   * vlastnosti, které operace přidává. Editor grafu z nich počítá
   * kompatibilitu hran (mirror BE ``find_incompatible_edge``). Absent /
   * prázdné = vakuózní kontrakt (přijímá cokoliv / nepřidává nic).
   */
  artifact_inputs?: ArtifactInputRequirement[];
  artifact_outputs?: ArtifactOutputSpec[];
  /**
   * Artefaktové klíče nalinkovaných vstupních dimenzí (unifikace dimenzí
   * a artefaktů) — editor je seeduje jako dynamické outputs uzlu vedle
   * explicitních ``artifact_outputs``. ``dynamic_artifact_domains`` nese
   * typovanou doménu hodnot z validation_json dimenze (jen klíče s
   * využitelnou informací; chybějící záznam = unbounded doména).
   */
  dynamic_artifact_keys?: string[];
  dynamic_artifact_domains?: Record<
    string,
    {
      allowed_values?: ArtifactValue[];
      min?: number | null;
      max?: number | null;
    }
  >;
}

// --- Artifact I/O kontrakty ------------------------------------------
//
// Klíče odkazují na platform registr ``ArtifactPropertyDefinition``
// (GET /admin/artifact-properties). Tvar zrcadlí BE
// ``app.services.processes.artifact_contracts``.

export type ArtifactValue = boolean | number | string;

export interface ArtifactInputRequirement {
  property_key: string;
  /** Default ``present`` — klíč musí existovat (libovolná hodnota). */
  requirement?: "present" | "equals" | "one_of" | "range";
  value?: ArtifactValue | null;
  values?: ArtifactValue[];
  /** Jen pro ``range`` — alespoň jedna mez, inkluzivní. */
  min?: number | null;
  max?: number | null;
}

export interface ArtifactOutputSpec {
  property_key: string;
  /** Default ``true`` — „artefakt teď tuhle vlastnost má". */
  value?: ArtifactValue;
  /**
   * Runtime binding — cesta do hodnot VLASTNÍ operace (``analysis.`` /
   * ``config.`` / ``material.`` / ``physical.`` / ``duration.``; holá cesta
   * = analýza), z níž se hodnota resolvuje při stampu (KAN-1339). Save-time
   * = unbounded doména (optimistic-exists). Vzájemně výlučné s ``value``.
   */
  source?: string | null;
  /**
   * Jednotka, ve které měří analyzátor TÉTO operace (KAN-1042). Runtime
   * hodnotu přepočítá na ``unit`` (jednotku vlastnosti v registru), takže
   * „plocha dílu [m²]" je JEDNA vlastnost bez ohledu na to, jestli ji vydal
   * laser v m² nebo mesh analyzátor v mm². Buď obě jednotky, nebo žádná.
   */
  source_unit?: string | null;
  unit?: string | null;
}

export interface ArtifactPropertyDefinition {
  id: number;
  key: string;
  value_type: string;
  unit?: string | null;
  label_cs: string;
  label_en: string;
  description_cs?: string | null;
  description_en?: string | null;
  /** Per jazyk platformy ``{kód jazyka: text}`` (KAN-1804); ``*_cs`` / ``*_en`` je zrcadlo. */
  label_i18n?: Record<string, string>;
  description_i18n?: Record<string, string>;
  validation_json?: Record<string, unknown>;
  /** "derived" = typ/validace zrcadlí vlastnickou dimenzi (read-only); "manual" = ručně autorovaná. */
  source_mode?: string;
  /**
   * Co se s vlastností stane při spojení dílů (KAN-298):
   * ``sum`` | ``max`` | ``min`` | ``union`` | ``all`` | ``drop``.
   * Vybírá platform admin u vlastnosti — ne graf a ne kód. Chybí-li,
   * platí ``drop`` (nevymýšlet hodnotu, kterou nikdo nedeklaroval).
   * Mirror: BE ``artifact_contracts.ArtifactMergeRule``.
   */
  merge_rule?: string;
  /**
   * KAN-1691 krok 5 — „tahle veličina JE údaj materiálu": ``material`` (sám
   * vybraný materiál) nebo ``material.<klíč>`` (platformní materiálová
   * vlastnost). Parametr, který ten údaj čte, se na vlastnost napojí sám.
   */
  material_source?: string | null;
  is_active: boolean;
  sort_order: number;
}

// --- Process pricing manifest (GET /admin/process-catalog/{slug}/manifest) -------
//
// Drives the unified admin Ceník: per-process variant axis label +
// pricing field set + price unit. The FE renders the same Materials →
// Variants → Modifiers shape across every tab; only these three
// strings change per process.

export interface VariantParamSpec {
  key: string;
  label_cs: string;
  label_en: string;
  unit: string | null;
  // "string" = enum/label osa (kanonický JSON klíč — povýšená barva, RAL kód);
  // "number" = měřená osa (kompaktní klíč). Typ pochází z DEKLARACE
  // (axis_type_for_dimension), nikdy z hodnoty.
  type: "number" | "string" | "none";
  /**
   * The ``MaterialPropertyDefinition`` key whose registry values populate this
   * axis — declared by the process, so admin UI can offer and author values for
   * ANY axis. NOT the axis key: the property key and the axis key differ by
   * convention. ``null`` = free-form axis with no registry.
   */
  property_key?: string | null;
}

export interface ProcessManifest {
  process_type: string;
  variant_param: VariantParamSpec;
  /** VŠECHNY flagnuté osy procesu, typované, v sort order (plural kanál —
   *  `variant_param` výše zůstává první ČÍSELNÁ osa pro numeric-only
   *  konzumenty). Optional kvůli back-compat s test fixtures. */
  variant_params?: VariantParamSpec[];
  // ``pricing_fields`` / ``price_unit_label_cs`` / ``headline_field`` sem
  // NEPATŘÍ: server je posílá, ale žádná komponenta je nečte — sazby se
  // editují přes rate-table endpointy. Typ popisuje, co FE opravdu bere.
  // A2 — pricing data-model discriminator; absent ⇒ "rate_table".
  // "process_modifier" (surface_finishes) ⇒ FE renders the finish catalog
  // (FinishesSection); "rate_table" processes are priced by rate_tables,
  // edited on the variant / operation node.
  pricing_model?: "rate_table" | "process_modifier";
}

/**
 * Public projection of the manifest (``GET /processes/{slug}/manifest``,
 * KAN-1471): the variant axis the customer configurator, bulk-configure and
 * the public material catalog render — nothing else.
 */
export interface ProcessVariantAxis {
  process_type: string;
  variant_param: Omit<VariantParamSpec, "property_key">;
}

// --- Process modifiers (GET /admin/modifiers) -----------------------
//
// Sjednocený view nad ``Finish`` (process_type='surface_finishes') a
// ``FilamentColor`` (jeden proces) po PR-E1/E2a. Stejný
// shape per proces.

export interface ProcessModifierTranslation {
  lang: string;
  display_name: string;
}

export interface ProcessModifierRow {
  id: number;
  process_type: string;
  code: string;
  modifier_kind: "pct" | "flat" | string;
  amount: number;
  swatch_hex?: string | null;
  swatch_image_url?: string | null;
  icon_key?: string | null;
  is_active: boolean;
  sort_order: number;
  translations: ProcessModifierTranslation[];
  material_codes: string[];
  // Horizon 1 #3 finishes_full_module — per-tenant routing fields.
  supports_partners?: boolean;
  default_partner_id?: number | null;
}

export interface ProcessModifiersListResponse {
  items: ProcessModifierRow[];
}

// --- Cart chain operations (POST /cart/items/:slug/operations) -------

export interface ChainContinuation {
  name: string;
  slug: string;
  display_name: string;
  description: string;
  icon: string;
  color: string;
  template_node_id?: string | null;
  public_input_dimension_keys?: string[];
}

export interface AddOperationRequest {
  process_type: string;
  template_node_id?: string | null;
  config?: Record<string, unknown>;
}

export interface ItemOperation {
  id: number;
  process_type: string;
  order_index: number;
  template_node_id?: string | null;
  fulfillment_partner_id: number | null;
  config_snapshot: Record<string, unknown> | null;
  /** KAN-1700 — hodnoty interaktivních vstupů, které zákazník změnil,
   *  ``{<klíč portu in_graph>: hodnota}``. Jen na primární operaci;
   *  chybí-li klíč, platí default modulu (kontrakt ``interactive_inputs``). */
  graph_inputs?: Record<string, unknown> | null;
  unit_price_czk: number;
  estimated_duration_seconds: number;
  // KAN-459: ``estimated_duration_seconds`` cannot tell an authored ``0`` from
  // a step nobody has estimated yet (NULL on the row, KAN-267) — both arrive as
  // a number. This flag is the difference. It is data, not copy: whether the
  // customer sees "bez odhadu" is a product decision that has not been made.
  duration_estimate_missing?: boolean;
}

// --- Invitations (PR-E) --------------------------------------------
//
// Unified accept/decline surface for pending CustomerOrg member
// rows. The legacy invite endpoint silently activated memberships;
// PR-E adds a `require_accept` flag on it plus the new
// `/invitations` router so the invitee gets a confirmation step.

/**
 * Discriminated invitation row. ``customer_org`` rows carry the B2B
 * fields; ``org_member`` rows (PR-E2 — tenant team invites) leave them
 * NULL since they don't belong to a CustomerOrg.
 */
export interface PendingInvitation {
  kind: "customer_org" | "org_member";
  member_id: number;
  customer_org_id: number | null;
  customer_org_name: string | null;
  provider_org_slug: string;
  provider_org_name: string;
  role: string;
  invited_at: string;
}

export interface InvitationAcceptResponse {
  member_id: number;
  customer_org_id: number;
  accepted_at: string;
}

/** PR-E2: tenant-team invite acceptance — no CustomerOrg fields. */
export interface OrgMemberAcceptResponse {
  member_id: number;
  org_id: number;
  accepted_at: string;
}

export interface InvitationDeclineResponse {
  member_id: number;
  declined: boolean;
}

// --- Program P — náhled dopadu (GET /admin/<entita>/{id}/usage) --------

/** Jedna položka dopadu: stabilní důvodový kód (i18n klíč
 *  ``admin:impact.reason.<reason>``), úplný počet a omezený vzorek
 *  zobrazitelných hodnot. Týž tvar nese 409 ``impact_confirm_required``
 *  v ``params.cascade``. */
export interface UsageItem {
  reason: string;
  count: number;
  labels: string[];
}

/** Sdílený tvar všech usage endpointů programu P — kaskáda (zmizí /
 *  přestane fungovat), blocking („nejdřív ukliď X"), historie (jen
 *  zmínky, nikdy neblokuje). */
export interface EntityUsageResponse {
  cascade: UsageItem[];
  blocking: UsageItem[];
  history: UsageItem[];
  is_empty: boolean;
}

// --- Zásoba hotového dílu (KAN-311/312, admin UI KAN-473) ---------------
//
// Decimal pole BE serializuje jako string (stejná konvence jako
// ``MovementRow.delta`` v AdminStockMovements) — žádné zaokrouhlování
// při přenosu.

/** Hotová dávka, kterou si nikdo neodebral — NABÍDKA k naskladnění, ne
 *  zápis. Naskladní se až ``POST /admin/storage/finished-stock``. */
export interface FinishedStockCandidate {
  batch_id: number;
  order_item_id: number;
  order_id: number;
  order_number: string;
  order_status: string;
  project_id: number;
  project_name: string;
  qty: string;
  uom: string;
}

/** KAN-2114 — definice se skladem hotových dílů (výběr místo ID). */
export interface FinishedStockDefinition {
  project_id: number;
  project_name: string;
  qty_on_hand: string;
}

export interface FinishedStockDefinitionsResponse {
  items: FinishedStockDefinition[];
}

export interface FinishedStockCandidatesResponse {
  items: FinishedStockCandidate[];
}

/** Jeden pohyb zásoby hotového dílu. Kladné ``qty`` = přírůstek,
 *  záporné = výdej (alokace ze skladu, KAN-313) — ``allocated_order_item_id``
 *  a ``source_entry_id`` jsou u výdeje vyplněné, u přírůstku obojí null. */
export interface FinishedStockEntry {
  id: number;
  project_id: number;
  order_item_id: number | null;
  batch_id: number | null;
  qty: string;
  uom: string;
  origin: string;
  allocated_order_item_id: number | null;
  source_entry_id: number | null;
}

/** Stav zásoby jedné definice + pohyby, ze kterých vznikl. ``qty_on_hand``
 *  je součet ``items[].qty``, ne uložený sloupec. */
export interface FinishedStockResponse {
  project_id: number;
  qty_on_hand: string;
  items: FinishedStockEntry[];
}

export interface FinishedStockCreateRequest {
  batch_id: number;
  origin?: string | null;
}

export interface FinishedStockOperation {
  id: number;
  process_type: string | null;
  order_index: number;
  iteration: number;
  template_node_id: string | null;
}

/** Odkud ten kus na skladě je — operace a šarže materiálu zvlášť. */
export interface FinishedStockTraceResponse {
  entry_id: number;
  order_item_id: number | null;
  batch_id: number | null;
  operations: FinishedStockOperation[];
  material_trail: Record<string, unknown>[];
}

/** Ocenění řádku zásoby hotového dílu (KAN-312) — NÁKLAD, ne cena.
 *  ``is_estimate`` je true, kdykoli do ocenění vstoupily PLÁNOVANÉ
 *  minuty (``basis`` "planned"/"mixed") — musí být v UI vidět a nesmí
 *  vypadat stejně důvěryhodně jako změřený náklad. */
export interface FinishedStockValuation {
  entry_id: number;
  order_item_id: number | null;
  qty: string;
  currency_code: string;
  unit_amount: string;
  unit_material_amount: string;
  unit_operation_amount: string;
  total_amount: string;
  basis: string;
  is_estimate: boolean;
}

// --- Admin: menu catalog (KAN-1373) ----------------------------------

/** Which admin shell a page belongs to — mirrors ``AdminPageScope`` in adminNav.ts. */
export type AdminMenuPageScope = "platform" | "tenant" | "shared";

export interface AdminMenuPagePlacement {
  group: string;
  /** ``null`` = the unlabelled leading section of the group. */
  subgroup: string | null;
  order: number;
}

export interface AdminMenuCatalogPage {
  page_id: string;
  to: string;
  scope: AdminMenuPageScope;
  /** Label = ``admin:nav.<i18n_key>``. */
  i18n_key: string;
  desc_key: string | null;
  requires_full_admin: boolean;
  platform_action_permission: string | null;
  visibility_page_ids: string[];
  default_placement: AdminMenuPagePlacement;
  is_app_page: boolean;
  /** App pages only (KAN-1379): the app's name (read-only), its sentence and icon. */
  label?: Record<string, string> | null;
  desc?: Record<string, string> | null;
  image?: string | null;
}

/**
 * ``GET /admin/menu-catalog`` (tenant: tenant + shared pages) and
 * ``GET /admin/platform/menu-catalog`` (platform: every page).
 */
export interface AdminMenuCatalog {
  /** Label = ``admin:navGroup.<i18n_key>``. */
  groups: { id: string; i18n_key: string }[];
  pages: AdminMenuCatalogPage[];
}

// --- Admin: menu layout (KAN-1374) -----------------------------------

/** Language code → text, over the platform's public languages. */
export type MenuLayoutLabel = Record<string, string>;

export interface MenuLayoutItem {
  page_id: string;
  /** ``null`` = the default layout's text, then the catalog i18n key. */
  label: MenuLayoutLabel | null;
  desc: MenuLayoutLabel | null;
  image: string | null;
}

export interface MenuLayoutSubgroup {
  /** ``"_"`` = the group's unlabelled section. */
  id: string;
  /** ``{}`` = unlabelled, or (tenant override) the default's label. */
  label: MenuLayoutLabel;
  /**
   * ``null`` = inherit (tenant override → the default's). In the menu the
   * fallback description / picture of the subgroup's items (KAN-1422).
   */
  desc?: MenuLayoutLabel | null;
  image?: string | null;
  items: MenuLayoutItem[];
}

export interface MenuLayoutGroup {
  id: string;
  /** ``{}`` in a tenant override = inherit the platform default's label. */
  label: MenuLayoutLabel;
  /** ``null`` = inherit; the last fallback description of its items (KAN-1422). */
  desc?: MenuLayoutLabel | null;
  image: string | null;
  subgroups: MenuLayoutSubgroup[];
}

export interface MenuLayoutTree {
  groups: MenuLayoutGroup[];
}

/** ``GET/PUT /admin/platform/menu-layout``. */
export interface PlatformMenuLayout {
  tree: MenuLayoutTree;
  /** ``null`` = nothing saved yet; ``tree`` is computed from the catalog. */
  updated_at: string | null;
  updated_by: number | null;
}

/** ``GET/PUT /admin/menu-layout`` (``DELETE`` → 204 = back to default). */
export interface TenantMenuLayout {
  /** ``null`` = no override. */
  tree: MenuLayoutTree | null;
  default_tree: MenuLayoutTree;
  updated_at: string | null;
  updated_by: number | null;
}

export type MenuItemSource =
  "override" | "default" | "catalog" | "manifest" | "anchor";

export interface EffectiveMenuItem extends MenuLayoutItem {
  to: string;
  source: MenuItemSource;
  /** Placed from the catalog — neither layout knows the page yet. */
  is_new: boolean;
  is_app_page: boolean;
  i18n_key: string;
  desc_key: string | null;
  requires_full_admin: boolean;
  platform_action_permission: string | null;
  visibility_page_ids: string[];
}

/**
 * ``POST /admin/menu-assets`` (tenant) and ``POST /admin/platform/menu-assets``
 * (KAN-1378). ``key`` (``asset:<id>``) goes into ``image`` of the layout tree;
 * ``url`` serves the bytes to a signed-in admin.
 */
export interface MenuAssetOut {
  key: string;
  url: string;
  mime: string;
  bytes: number;
}

/** ``GET /admin/menu`` (tenant) and ``GET /admin/platform/menu``. */
export interface EffectiveMenu {
  scope: "platform" | "tenant";
  groups: {
    id: string;
    label: MenuLayoutLabel;
    desc?: MenuLayoutLabel | null;
    image: string | null;
    subgroups: {
      id: string;
      label: MenuLayoutLabel;
      desc?: MenuLayoutLabel | null;
      image?: string | null;
      items: EffectiveMenuItem[];
    }[];
  }[];
}

/** Saved tenant state for the wizard; seed deltas are deliberately separate. */
export interface OnboardingPaymentMethod {
  key: string;
  label_key: string;
  help_key: string;
  fields: Array<{
    key: string;
    label_key: string;
    value: string | null;
    needed: boolean;
  }>;
  missing: string[];
}
export interface OnboardingShop {
  operations: Array<{
    slug: string;
    display_name_i18n: Record<string, string>;
  }>;
  machines: number;
  materials: number;
  carriers: number;
  country_codes: string[];
  payment_methods: OnboardingPaymentMethod[];
}

// --- Internal sales CRM „Obchod" (GET/POST /admin/crm/*, KAN-1567) ---------

export type CrmStatus =
  | "found"
  | "verified"
  | "prepared"
  | "contacted"
  | "replied"
  | "meeting"
  | "trial"
  | "customer"
  | "do_not_contact"
  | "lost";

export interface CrmCompanySummary {
  id: number;
  ico: string | null;
  name: string;
  domains: string[];
  segment: string | null;
  priority: string | null;
  status: CrmStatus;
  status_reason: string | null;
  next_step: string | null;
  next_step_due: string | null;
  last_contacted_at: string | null;
  /** Protective period — company in a final state (KAN-1570). */
  protected: boolean;
  /** Estimated share quoted automatically, whole percent (KAN-1732); both `null` = not estimated. */
  auto_quote_min_pct: number | null;
  auto_quote_max_pct: number | null;
  coverage_gaps: CrmCoverageGap[];
  updated_at: string;
  /** What the card already has for the first outreach (KAN-1725): a contact
   * person with an e-mail, a draft with a body, a demo shop, an overlap. */
  has_email: boolean;
  has_draft: boolean;
  has_demo: boolean;
  has_overlap: boolean;
}

/** What the platform does not cover for a company (KAN-1732). The kind is
 * the `key` of a {@link CrmCoverageKind}, `value_key` the `key` of one of its
 * {@link CrmCoverageValue}s (KAN-2184), `note` free specifics. A gap stored
 * before the value catalog has `value_key: null` and its free text in `value`. */
export interface CrmCoverageGap {
  kind: string;
  value_key?: string | null;
  note?: string | null;
  /** Legacy free text (before KAN-2184); a new gap does not send it. */
  value?: string | null;
}

/** A value of a gap kind — a catalog per kind the owner edits (KAN-2184). */
export interface CrmCoverageValue {
  id: number;
  /** What a gap's `value_key` holds; unique within the kind, fixed once created. */
  key: string;
  name: string;
  sort_order: number;
  /** No new gap may use it; cards that carry it keep reading. */
  archived: boolean;
}

/** A kind of coverage gap — a catalog the owner edits (KAN-2054). */
export interface CrmCoverageKind {
  id: number;
  /** What a gap's `kind` holds; fixed once created. */
  key: string;
  name: string;
  sort_order: number;
  /** No new gap may use it; cards that carry it keep reading. */
  archived: boolean;
  /** Its values (KAN-2184), archived ones included, in catalog order. */
  values: CrmCoverageValue[];
}

export interface CrmPerson {
  id: number;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  confidence: string | null;
  /** Row in the Kontakty directory (`crm_contact_persons:<id>`). */
  contact_id: string;
}

export interface CrmEvent {
  id: number;
  kind: string;
  occurred_at: string;
  actor_user_id: number | null;
  summary: string | null;
  body: string | null;
  from_status: CrmStatus | null;
  to_status: CrmStatus | null;
  reason: string | null;
  mail_thread_id: number | null;
  /** Reason tags picked with a move into a final state, or a re-tag note
   * (KAN-1746) — ids from `GET /admin/crm/reason-tags`, archived included. */
  reason_tag_ids: number[];
}

export interface CrmOverlap {
  id: number;
  name: string;
  status: CrmStatus;
}

export interface CrmDraft {
  to: string | null;
  subject: string | null;
  body: string | null;
  used_at: string | null;
}

/** The shop a CRM company became by signing up (KAN-1574). */
export interface CrmTenant {
  id: number;
  slug: string;
  name: string;
}

/** What the company did in the demo through its personal code (KAN-1575).
 * The uploads, prices and orders themselves are `demo_*` history entries. */
export interface CrmDemoActivity {
  sessions: number;
  visits: number;
  last_seen_at: string | null;
  uploads: number;
  prices: number;
  orders: number;
}

/** Where the paired shop is on its way to a first sale (KAN-1576) — read
 * from the onboarding funnel. Step keys are readiness sell checks. */
export interface CrmActivation {
  steps: { key: string; done: boolean }[];
  blocked_on: string | null;
  days_since_signup: number | null;
  days_to_first_sale: number | null;
  stalled: boolean;
  sold: boolean;
  paid_plan: boolean;
  /** Embed configurator views on their web, last 30 days. */
  embed_views: number;
}

/** The follow-up next in line after the outreach (KAN-1730). */
export interface CrmFollowUp {
  step: "outreach" | "follow_up_email" | "call";
  /** Position in the sequence — the outreach e-mail is 1. */
  number: number;
  total: number;
  due: string;
}

/** Direction of a shop metric, last 30 days against the 30 before —
 * never an amount (KAN-1745, owner decision 2026-09-30). */
export type CrmTrend = "up" | "flat" | "down";

/** The paired shop's computed phase (KAN-1745) — never moved by hand. */
export interface CrmHealth {
  phase: "ramping" | "live" | "dozing" | "growing";
  trends: { quotes: CrmTrend; orders: CrmTrend; revenue: CrmTrend };
  /** Last pricing or order in the shop. */
  last_activity_at: string | null;
  /** `growing` by a plan limit: the resource (`common:planLimits.resource.*`). */
  near_limit: string | null;
}

export interface CrmCompanyDetail extends CrmCompanySummary {
  main_field: string | null;
  vat_payer: boolean | null;
  registered_address: string | null;
  workshop_address: string | null;
  status_changed_at: string | null;
  demo_shop: string | null;
  outreach_variant: string | null;
  dossier_md: string | null;
  sources: string[];
  draft: CrmDraft;
  /** Goes into outreach links as `?p=<code>` (KAN-1574). */
  personal_code: string | null;
  tenant: CrmTenant | null;
  demo: CrmDemoActivity;
  /** `null` until the company signs up. */
  activation: CrmActivation | null;
  /** `null` = no follow-up pending (not contacted, reacted, or all done). */
  follow_up: CrmFollowUp | null;
  /** `null` until the company signs up. */
  health: CrmHealth | null;
  persons: CrmPerson[];
  events: CrmEvent[];
  overlaps: CrmOverlap[];
  /** The reason tags the card carries now — only in a final state (KAN-1746). */
  reason_tag_ids: number[];
  created_at: string;
}

export interface CrmCheckHit {
  query: string;
  /** `related_domain` = a card owns a parent domain or a subdomain of the query. */
  matched_by: "ico" | "domain" | "related_domain";
  known: boolean;
  company: CrmCompanySummary | null;
  /** The company objected and was deleted (KAN-1966) — never create a card. */
  do_not_contact: boolean;
}

/** DELETE /admin/crm/companies/{id} — objection to the outreach (KAN-1966). */
/** `POST /admin/crm/companies/{id}/call-outcome` — „Telefonát" (2026-10-26). */
export type CrmCallOutcome = "agreed" | "no_answer" | "declined";

export interface CrmCallOutcomeInput {
  outcome: CrmCallOutcome;
  /** `agreed` only — the e-mail given on the phone, stored on the person. */
  email?: string | null;
  note?: string | null;
  /** `declined` — reason tags and/or `reason`, at least one. */
  reason_tag_ids?: number[];
  reason?: string | null;
}

export interface CrmCompanyDeleteResult {
  blocked_domains: number;
}

export interface CrmCheckResult {
  results: CrmCheckHit[];
}

/** A lead written by hand from the page (POST /admin/crm/companies/upsert
 * with `origin: "manual"`, KAN-1726) — only creates, stays in *Nalezeno*. */
export interface CrmManualLeadInput {
  name: string;
  ico?: string;
  domains: string[];
  segment?: string;
  priority?: string;
  persons: { name: string; role?: string; email?: string; phone?: string }[];
  origin: "manual";
  /** Body of the „Karta založena ručně" history entry. */
  note?: string;
}

export interface CrmCompanyUpsertResult {
  created: boolean;
  status_kept: boolean;
  unresolved_overlaps: string[];
  company: CrmCompanyDetail;
}

/** One row of the „Dnes" queue (GET /admin/crm/today, KAN-1569). */
export interface CrmTodayItem {
  /** `replied` — answer them, `demo` — tried the demo (KAN-1575),
   * `stalled` — their shop is stuck (KAN-1576), `dozing` — their shop went
   * quiet (KAN-1745), `follow_up` — a follow-up step is due (KAN-1730),
   * `due` — next step due, `no_reply` — remind, `growing` — their shop nears
   * a plan limit or grows (KAN-1745), `ready` — researched, never contacted,
   * no next step (KAN-1724), `reapproach` — lost for a product reason that is
   * solved now, `revisit` — a reason tag's „remind in N months" came due
   * (KAN-1746/1747). */
  reason:
    | "replied"
    | "demo"
    | "stalled"
    | "dozing"
    | "follow_up"
    | "due"
    | "no_reply"
    | "growing"
    | "reapproach"
    | "revisit"
    | "ready";
  /** `stalled`: the readiness step the shop is stuck on; `growing`: the plan
   * limit it nears (`null` = it grows by its orders). */
  step?: string | null;
  /** `demo` only (KAN-1728): the strongest signal nobody reacted to yet. */
  signal?: "demo_visit" | "demo_upload" | "demo_price" | "demo_order" | null;
  /** `demo` only: what they did in the demo. */
  demo?: CrmDemoActivity | null;
  /** `demo` only: uploaded files that never got a price. */
  unpriced_files?: string[];
  /** `stalled` only: the whole activation of their shop. */
  activation?: CrmActivation | null;
  /** `follow_up`: the step that is due; `ready`: step 1, the outreach. */
  follow_up?: CrmFollowUp | null;
  /** `reapproach` / `revisit` only: the name of the reason tag. */
  tag?: string | null;
  /** When the rule started to apply — reply / send time or the due date. */
  since: string | null;
  company: CrmCompanySummary;
}

/** Agent draft vs. the outreach actually sent (GET /admin/crm/learning/drafts, KAN-1744). */
export interface CrmDraftPair {
  company_id: number;
  company_name: string;
  segment: string | null;
  sent_at: string;
  agent_subject: string | null;
  agent_body: string | null;
  sent_subject: string | null;
  sent_body: string | null;
  unchanged: boolean;
}

export interface CrmDraftLearning {
  /** Newest first. */
  pairs: CrmDraftPair[];
  sent: number;
  unchanged: number;
  /** `unchanged / sent`; `null` before the first send. */
  unchanged_share: number | null;
}

/** A recipient that belongs to a CRM company (GET /admin/crm/match-addresses, KAN-1571). */
export interface CrmAddressMatch {
  address: string;
  company: CrmCompanySummary;
}

/** A company pin on the CRM map (GET /admin/crm/map, KAN-1577). */
export interface CrmMapPin {
  id: number;
  name: string;
  status: CrmStatus;
  segment: string | null;
  priority: string | null;
  next_step: string | null;
  next_step_due: string | null;
  /** Municipality of `address` — for the list under the map (KAN-1733). */
  city: string | null;
  /** The workshop wins over the seat whenever it is known. */
  source: "workshop" | "registered";
  address: string;
  lat: number;
  lon: number;
}

export interface CrmMapUnplaced {
  id: number;
  name: string;
  status: CrmStatus;
  segment: string | null;
  priority: string | null;
  next_step: string | null;
  next_step_due: string | null;
  /** The address the register did not find; `null` = no address on the card. */
  address: string | null;
}

export interface CrmMap {
  pins: CrmMapPin[];
  unplaced: CrmMapUnplaced[];
  /** Cards still waiting for a lookup (POST /admin/crm/map/geocode). */
  pending: number;
}

/** Segment × outreach variant (GET /admin/crm/variants, KAN-1578). */
export interface CrmVariantRow {
  segment: string | null;
  variant: "with_link" | "without_link" | null;
  contacted: number;
  replied: number;
  demo_opened: number;
  signed_up: number;
  customers: number;
}

/** A heading of the CRM reason tag list (KAN-1746) — data, seeded Produkt / Obchod / Timing. */
export interface CrmReasonTagGroup {
  id: number;
  name: string;
  sort_order: number;
}

/** Why a company ended in Ztraceno / Neoslovovat (KAN-1746). */
export interface CrmReasonTag {
  id: number;
  group_id: number;
  name: string;
  /** The platform lacks something — feeds „Co nám brání" (KAN-1747). */
  is_product: boolean;
  /** The card comes back to „Dnes" this many months after it got the tag. */
  remind_after_months: number | null;
  sort_order: number;
  /** Removed by the owner, kept so the history still reads. */
  archived: boolean;
  ticket_key: string | null;
  ticket_draft: string | null;
  /** The manual „vyřešeno / nasazeno" switch (the platform reads no Jira). */
  ticket_resolved_at: string | null;
}

/** GET /admin/crm/reason-tags — archived tags included. */
export interface CrmReasonCatalog {
  groups: CrmReasonTagGroup[];
  tags: CrmReasonTag[];
}

/** One row of „Co nám brání" (GET /admin/crm/blockers, KAN-1747). */
export interface CrmBlocker {
  tag: CrmReasonTag;
  group: string;
  /** Cards in Ztraceno / Neoslovovat carrying the tag now. */
  leads: number;
  /** Leads weighted by priority (high 3, medium 2, low / none 1). */
  weight: number;
  segments: { segment: string | null; count: number }[];
  companies: {
    id: number;
    name: string;
    status: CrmStatus;
    priority: string | null;
    segment: string | null;
  }[];
}

export type CrmFunnelStageKey =
  "contacted" | "replied" | "demo" | "signed_up" | "paid_order";

/** One funnel stage (GET /admin/crm/funnel, KAN-1731). */
export interface CrmFunnelStage {
  key: CrmFunnelStageKey;
  count: number;
  /** How many of the previous stage's companies reached this one; `null` for the first. */
  of_previous: number | null;
}

export interface CrmFunnelGroup {
  /** Segment or outreach variant; `null` = not recorded on the card. */
  label: string | null;
  stages: CrmFunnelStage[];
}

export interface CrmStatusDuration {
  status: CrmStatus;
  /** Median of the finished stays in days; `null` = none yet. */
  median_days: number | null;
  samples: number;
}

export interface CrmFunnel {
  since: string | null;
  until: string | null;
  stages: CrmFunnelStage[];
  by_segment: CrmFunnelGroup[];
  by_variant: CrmFunnelGroup[];
  durations: CrmStatusDuration[];
}

/** KAN-1641 — položka číselníku důvodů zrušení (popisek v jazyce požadavku). */
export interface OrderCancelReason {
  code: string;
  label: string;
}

// --- Step-up (KAN-1264) --------------------------------------------------

/** Factor the account can re-prove; `email` only when it has neither of the others. */
export type StepUpMethod = "password" | "totp" | "email";

export interface StepUpRequest {
  password?: string;
  totp_code?: string;
  email_code?: string;
}

export interface StepUpResponse {
  expires_in: number;
  methods: StepUpMethod[];
}

/** KAN-1738 — co se na rušené objednávce už stalo (náhled pro dialog). */
export interface OrderCancelPreview {
  warehouse_enabled: boolean;
  has_activity: boolean;
  consumed_materials: {
    material_stock_id: number;
    material_code: string;
    material_label: string;
    qty: string;
    uom: string;
  }[];
  produced_parts: {
    order_item_id: number;
    label: string;
    qty: string;
    uom: string;
    stockable: boolean;
  }[];
  /** Q13 (v66) — aktivní dávky, ve kterých objednávka je. ``collecting`` =
   *  zrušením z dávky vypadne; ``released`` / ``in_progress`` = zůstane. */
  production_jobs?: { id: number; state: string }[];
}

/** KAN-1738 — odpověď admina: co s materiálem a díly, kam je položit. */
export interface CancelStockDisposition {
  material?: "return" | "write_off" | null;
  parts?: "stock" | "write_off" | null;
  storage_location_id?: number | null;
}

/** KAN-1888 — jeden chybějící materiál objednávky ``awaiting_material``. */
/** KAN-2185 — krok objednávky, který drží Dávka (uzel ``kind='job'``).
 *  Sám se nestartuje ani neodbaví — hýbe se jen s dávkou. */
export interface OrderJobWait {
  work_item_id: number;
  node_id: string | null;
  /** ``null`` = krok leží v zásobníku uzlu, dávka ještě nevznikla. */
  job_id: number | null;
  /** ``collecting`` / ``released`` / ``in_progress``; ``null`` = v zásobníku. */
  job_state: string | null;
  /** Naplnění kandidáta 0..1; ``null`` = kapacita se neměří. */
  fill: number | null;
  threshold_pct: number | null;
  releasable: boolean;
  burning: boolean;
  /** Slíbený konec výroby, který hlídá pojistka dávky. */
  deadline_at: string | null;
  /** Nejpozději vyjede kolem (slib − zbytek cesty − rezerva uzlu). */
  latest_release_at: string | null;
}

/** KAN-1929 — admin-only doplněk detailu objednávky
 *  (``GET /admin/orders/{n}/workbench``, jen admin obchodu). */
export interface AdminOrderWorkbench {
  /** KAN-1641 — co z tohoto stavu smí admin obchodu (``cancel``,
   *  ``plan_production``, ``create_shipment``, ``record_payment``,
   *  ``resend_invoice``). Jeden zdroj s endpointy, které akci provádějí. */
  admin_actions: string[];
  /** KAN-1867 — „Co teď" po výrobě: ``ship`` (vytvořit a odeslat zásilku),
   *  ``await_delivery`` (čeká na doručení), ``delivered`` (hotovo); ``null``
   *  = nic z toho (``order_actions.order_next_step``). */
  admin_next_step: string | null;
  /** KAN-1888 — co objednávce ``awaiting_material`` chybí (živý suchý
   *  průchod rezervace). Prázdné = zásoba stačí. */
  material_shortages: OrderMaterialShortage[];
  /** KAN-1784 — otevřená žádost zákazníka o zrušení; ``null`` = žádná. */
  cancel_request: OrderCancelRequest | null;
  /** KAN-1540/KAN-1655 (z ``OrderView`` přesunuto KAN-1934) — proč zaplacená
   *  objednávka fakturu nemá: ``"seller_ico_missing"`` = obchod nemá IČO
   *  (brána KAN-1534); ``"invoice_not_issued"`` = faktura při zaplacení
   *  nevznikla a admin ji dovystaví. ``null`` jinak. */
  invoice_blocked_reason: string | null;
  /** KAN-1934 — číslo faktury DODAVATELE obchodu (interní účetní odkaz).
   *  Edituje se přes ``PATCH /admin/orders/{n}/supplier-invoice``. */
  supplier_invoice_number: string | null;
  /** KAN-2009 — slíbený termín doručení a skutečnost pro admin kartu
   *  „Plán a termín"; zákaznický detail odhad po vydání maže. */
  promised_delivery_at?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
  /** KAN-2185 — kroky, které drží Dávka (prázdné u obchodu bez Dávky). */
  production_job_waits?: OrderJobWait[];
  /** KAN-1957 — interní PK objednávky (předání, štítky, sloty aplikací). */
  order_id: number;
  /** KAN-1957 — vlastník pro admin Kontakty (``ContactLink``); ``null`` = host. */
  user_id: number | null;
  /** KAN-2110 — účet, jehož historii ukazuje blok „Zákazník“; ``null``,
   *  když objednávku z účtu odeslal někdo za jiného odběratele. */
  customer_user_id?: number | null;
  /** KAN-748/KAN-1957 — testovací objednávka (badge TEST; mimo tržby,
   *  provize a statistiky; jde tvrdě smazat). */
  is_test: boolean;
  /** KAN-1957 — měna zobrazení → měna účetnictví obchodu. */
  fx_rate_to_base: number;
  /** KAN-1957 — ``slug`` → interní ``id`` každé položky objednávky. */
  items: Array<{ slug: string; id: number; production_notes?: ItemProductionNotes }>;
  /** KAN-1957 — celý zmrazený snapshot termínu s interními klíči. */
  delivery_selection: OrderDeliverySelection | null;
}

/** KAN-1957 — položka admin detailu: veřejná podoba + interní id z workbench
 *  (``null``, dokud workbench nedorazil). */
export interface AdminOrderItem extends OrderItem {
  id: number | null;
  /** KAN-2071 — z workbench; ``null``, dokud nedorazí. */
  production_notes: ItemProductionNotes | null;
}

/** KAN-2071 — co zákazník u dílu potvrdil, z dat položky
 *  (``app.services.item_production_notes``). */
export interface ItemProductionNotes {
  risk_acceptance: {
    /** ISO 8601 (UTC). */
    accepted_at: string;
    /** Přesný text upozornění, které zákazník potvrdil. */
    text: string;
    locale: string | null;
    /** Nálezy, kterých se souhlas týká — tvar varování (``code`` + ``params``). */
    findings: Array<{ code: string; params: Record<string, unknown> }>;
  } | null;
  /** Provedené automatické úpravy souboru (``kind`` + ``params``). */
  repairs: Array<{ kind: string; params: Record<string, unknown> }>;
  /** KAN-2080 — leží u položky soubor vydaný opravou? ``false`` + úpravy =
   *  ke stažení je jen originál, úpravy v něm nejsou. */
  repaired_file_available?: boolean;
}

/** KAN-1957 — admin detail objednávky: veřejný ``OrderDetail`` doplněný
 *  o interní klíče z ``AdminOrderWorkbench``. Dokud workbench nedorazí, jsou
 *  ``null`` / ``false`` — stránka se vykreslí a akce s id počkají. */
export interface AdminOrderView extends Omit<OrderDetail, "items"> {
  order_id: number | null;
  user_id: number | null;
  /** KAN-2110 — viz ``AdminOrderWorkbench.customer_user_id``. */
  customer_user_id: number | null;
  is_test: boolean;
  items: AdminOrderItem[];
}

/** Složí admin detail z veřejného detailu a workbench (KAN-1957). */
export function toAdminOrderView(
  order: OrderDetail,
  workbench: AdminOrderWorkbench | undefined,
): AdminOrderView {
  const idBySlug = new Map(
    (workbench?.items ?? []).map((ref) => [ref.slug, ref.id]),
  );
  const notesBySlug = new Map(
    (workbench?.items ?? []).map((ref) => [ref.slug, ref.production_notes ?? null]),
  );
  return {
    ...order,
    order_id: workbench?.order_id ?? null,
    user_id: workbench?.user_id ?? null,
    customer_user_id: workbench?.customer_user_id ?? null,
    is_test: workbench?.is_test ?? false,
    items: order.items.map((item) => ({
      ...item,
      id: idBySlug.get(item.slug) ?? null,
      production_notes: notesBySlug.get(item.slug) ?? null,
    })),
  };
}

/** KAN-1784 — otevřená žádost zákazníka o zrušení zaplacené objednávky. */
export interface OrderCancelRequest {
  reason: string;
  reason_label: string;
  note: string | null;
  requested_at: string;
}

export interface OrderMaterialShortage {
  material_id: number;
  material_code: string;
  material_name: string;
  dimensions: Record<string, unknown>;
  /** Popisky os/hodnot zmrazené z BE (tentýž resolver jako řádky objednávky). */
  dimension_labels?: Record<string, OrderLineDimensionLabel>;
  variant_signature_key: string;
  /** ``null`` = varianta ještě nemá skladový řádek. */
  stock_id: number | null;
  stock_uom: string;
  required_qty: string;
  available_qty: string;
  short_qty: string;
}

// --- KAN-1902 — výpadek stroje a zavírací dny obchodu -----------------

/** Důvod výpadku. ``closure`` nese jen zavírací den celého obchodu. */
export type DowntimeReason = "breakdown" | "maintenance" | "closure" | "other";

/** ``GET /admin/downtimes`` — odstávka stroje nebo zavírací den obchodu. */
export interface ProductionDowntime {
  id: number;
  /** ``machine`` = jeden stroj, ``shop`` = celý obchod. */
  scope: "machine" | "shop";
  machine_id: number | null;
  machine_name: string | null;
  starts_at: string;
  ends_at: string;
  /** Zavírací dny v pásmu obchodu (jen ``scope='shop'``), včetně posledního. */
  start_date: string | null;
  end_date: string | null;
  reason: DowntimeReason;
  note: string | null;
  created_at: string | null;
}

/** Zakázka, které výpadek posunul termín (``GET /admin/schedule-shifts``). */
export interface ScheduleShift {
  id: number;
  order_id: number;
  order_number: string | null;
  customer: string | null;
  downtime_id: number | null;
  machine_name: string | null;
  reason: DowntimeReason;
  previous_production_end_at: string | null;
  new_production_end_at: string | null;
  /** Odhad doručení zákazníkovi; ``null`` u objednávky bez něj. */
  previous_delivery_at: string | null;
  new_delivery_at: string | null;
  /** Kdy odešel e-mail o zpoždění; ``null`` = ještě ne. */
  notified_at: string | null;
  created_at: string | null;
}

/** KAN-2050 — ruční rezervace, do které výpadek zasáhl. Zůstává, admin o ní ví. */
export interface ReservationDowntimeConflict {
  order_id: number;
  order_number: string;
  public_number: string | null;
  work_item_id: number;
  process_type: string;
  machine_id: number;
  machine_name: string | null;
  reservation_start_at: string;
  reservation_end_at: string;
  downtime_start_at: string;
  downtime_end_at: string;
  /** Q1 — rezervace výrobní úlohy („Dávka"); `order_*` = první člen. */
  job_id?: number | null;
  job_order_numbers?: string[];
}

export interface DowntimeCreated {
  downtime: ProductionDowntime;
  shifts: ScheduleShift[];
  /** KAN-2050 — ruční rezervace, které výpadek neposunul. */
  reservation_conflicts: ReservationDowntimeConflict[];
}

export interface MachineDowntimeCreate {
  starts_at: string;
  ends_at: string;
  reason: Exclude<DowntimeReason, "closure">;
  note?: string | null;
}

export interface ShopClosureCreate {
  start_date: string;
  end_date: string;
  note?: string | null;
}

export type ScheduleShiftNotifyStatus =
  | "sent"
  | "already_sent"
  | "no_recipient"
  | "opted_out"
  | "failed"
  | "not_found";

export interface ScheduleShiftNotifyResult {
  results: { shift_id: number; status: ScheduleShiftNotifyStatus }[];
}

// --- KAN-1919 — platform material catalog as a tenant adopts from it ------

/** ``GET /admin/material-catalog`` — one active platform catalog row. */
export interface CatalogMaterial {
  code: string;
  /** Tenant's language first, then any translation, then the code. */
  display_name: string;
  note: string | null;
  category_slug: string;
  /** Indicative price of the whole material; ``null`` when the catalog has none. */
  reference_cost: {
    unit_cost: string;
    cost_uom: string;
    currency_code: string;
  } | null;
  /** Distinct variants the catalog describes (0 = one plain item). */
  variant_count: number;
  /** The shop's own material with this code, ``null`` = not in the shop yet. */
  material_id: number | null;
}

export interface CatalogMaterialList {
  items: CatalogMaterial[];
}

/** ``POST /admin/material-catalog/{code}/adopt``. */
export interface CatalogAdoptResult {
  material_id: number;
  code: string;
  /** ``false`` = the shop already had the code; nothing was copied. */
  created: boolean;
}

/** KAN-1988 — ``GET /changelog-announcements``: novinky changelogu v adminu. */
export interface ChangelogAnnouncementModule {
  key: string;
  label: string;
}

export interface ChangelogAnnouncement {
  post_id: number;
  /** Článek na webu platformy: ``/{lang}/changelog/{slug}``. */
  slug: string;
  lang: string;
  title: string;
  excerpt: string | null;
  tier: string;
  version_tag: string | null;
  published_at: string;
  released_at: string | null;
  modules: ChangelogAnnouncementModule[];
  read: boolean;
}

export interface ChangelogAnnouncementList {
  items: ChangelogAnnouncement[];
  unread_count: number;
}

// ---- KAN-2000: uložené rozložení tabulek -------------------------------
// Jak si uživatel uspořádal jednu tabulku administrace (IngotTable):
// pořadí sloupců, skryté sloupce, šířky. Patří uživateli, ne prohlížeči.
// ``null`` = nic neuloženo, tabulka je ve výchozí podobě.

export interface TableLayoutBody {
  order: string[] | null;
  hidden: string[] | null;
  widths: Record<string, number> | null;
}

export interface TableLayoutOut {
  table_id: string;
  layout: TableLayoutBody;
}

// ---- KAN-2037/2039: výrobní úloha (UI „Dávka") --------------------------
// Admin API ``/admin/production-jobs`` a ``/admin/production-job-candidates``.
// Kandidáti jsou odvozený pohled — neukládají se, každý dotaz je přepočítá.

export type ProductionJobState = "collecting" | "released" | "in_progress" | "completed" | "cancelled";

export interface ProductionJobMember {
  work_item_id: number;
  order_id: number;
  order_number: string | null;
  order_item_id: number;
  process_type: string;
  /** Stav pracovní položky člena — odvozený z úlohy. */
  state: string;
  qty: string;
}

export interface ProductionJobReservation {
  machine_id: number | null;
  resource_label: string | null;
  scheduled_start_at: string;
  scheduled_end_at: string;
  source: string;
}

/** Důvod vyjetí — zrcadlo BE ``TRIGGER_*`` v ``production_job_release``. */
export type ProductionJobReleaseTrigger = "fill" | "due" | "manual";

/** ``result_json["release"]`` — zapisuje ``release_job`` (KAN-2039). */
export interface ProductionJobReleaseMeta {
  trigger?: ProductionJobReleaseTrigger | string;
  released_at?: string;
  /** Některý člen neměl odhad doby — rezervace stroje je jen minimální. */
  duration_estimate_missing?: boolean;
}

/**
 * ``ProductionJob.result_json`` — naplnění od ``JobFill.as_json`` (KAN-2043)
 * a důvod vyjetí. Všechno volitelné: úloha bez kapacitního limitu naplnění
 * nemá a appka (KAN-2047) sem smí přidat vlastní klíče.
 */
export interface ProductionJobResult {
  /** 0..1, nejtěsnější limit. */
  fill?: number | null;
  /** Klíč nejtěsnějšího limitu (``artefakt/stroj``). */
  tightest?: string | null;
  /** Naplnění per limit, 0..1. */
  ratios?: Record<string, number>;
  /** Limity, které na dílech nešly změřit. */
  unmeasured?: string[];
  /** KAN-2047 — čí je ``fill``: ``estimate`` (odhad jádra) / ``app``
   * (aplikace dávající úloze podobu; odhad pak leží pod ``estimate``). */
  fill_source?: "estimate" | "app" | null;
  release?: ProductionJobReleaseMeta;
  [key: string]: unknown;
}

export interface ProductionJob {
  id: number;
  /** KAN-2186 — číslo pro lidi: pořadí úlohy v obchodu (``id`` je sdílené obchody). */
  number?: number | null;
  node_id: string | null;
  machine_id: number | null;
  format_key: string | null;
  /** Čitelný štítek formátu polotovaru (``1×2 m``); ``null`` = bez formátu. */
  format_label?: string | null;
  state: ProductionJobState;
  released_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  /** Nejbližší termín člena; neukládá se. */
  due_at: string | null;
  /** Naplnění (``fill``, ``ratios``…) a důvod vyjetí (``release.trigger``). */
  result_json: ProductionJobResult;
  members: ProductionJobMember[];
  reservation: ProductionJobReservation | null;
}

export interface ProductionJobList {
  items: ProductionJob[];
}

/**
 * KAN-2047 — soubor, který na úlohu uložila aplikace dávající jí podobu.
 * ``GET /admin/production-jobs/{id}/files``; ``url`` je krátkodobý odkaz.
 */
export interface ProductionJobFile {
  id: number;
  name: string;
  mime: string;
  size_bytes: number;
  created_at: string;
  url: string;
  expires_seconds: number;
}

export interface ProductionJobFileList {
  items: ProductionJobFile[];
  /** Q4 — výkres nese kusy zrušené zakázky; platí do přepočtu aplikací. */
  layout_stale?: boolean;
}

export interface ProductionJobComposeBody {
  machine_id: number;
  work_item_ids: number[];
  node_id?: string | null;
}

export interface ProductionJobReleaseBody {
  machine_id?: number | null;
}

/** KAN-2166 — přesun nezačaté dávky na jiný schopný stroj. */
export interface ProductionJobMoveBody {
  machine_id: number;
}

/** Schopný stroj s nejbližším volným oknem, které by dávka dostala. */
export interface ProductionJobMoveTarget {
  machine_id: number;
  machine_name: string;
  /** Stroj, na kterém dávka teď je. */
  current: boolean;
  scheduled_start_at: string;
  scheduled_end_at: string;
}

export interface ProductionJobMoveTargetList {
  items: ProductionJobMoveTarget[];
}

export interface ProductionJobCandidate {
  node_id: string;
  machine_id: number;
  process_type: string;
  /** Neprůhledná identita skupiny (hodnota klíče sdružování). */
  group_key: string;
  work_item_ids: number[];
  /** Kroky skupiny, které se na tenhle stroj nevešly (kapacita). */
  overflow_work_item_ids: number[];
  /** 0..1, nejtěsnější limit; ``null`` = operace kapacitu nemodeluje. */
  fill: number | null;
  threshold_pct: number;
  due_at: string | null;
  /** Q7 — vlastní termín každého dílu (`work_item_id` → termín); `due_at` je termín skupiny. */
  work_item_due_at?: Record<string, string | null>;
  /** Některému členu hoří termín — vyjede bez ohledu na procento. */
  burning: boolean;
  /** Vyjela by při uvolnění stroje (práh NEBO termín). */
  releasable: boolean;
  /** Formát polotovaru skladem (kandidát per stroj × formát); ``null`` = bez formátu. */
  format_key?: string | null;
  format_label?: string | null;
  /** Stabilní id kandidáta (KAN-2063) — na něj zapisuje naplnění aplikace. */
  candidate_id?: string | null;
  /** Odkud je ``fill``: ``estimate`` (jádro) / ``app`` (aplikace). */
  fill_source?: "estimate" | "app" | null;
  /** KAN-2104 — čitelné hodnoty klíče sdružování ``{část klíče: text}``
   *  (``variant`` = „S235JR · Tloušťka 3 mm", ``order`` = číslo zakázky). */
  group_value_labels?: Record<string, string>;
  /** KAN-2103 — materiál formát polotovaru mít může, ale skladem žádný
   *  není: skladová varianta, kde se formát zakládá. */
  format_setup?: { material_id: number; variant_key: string } | null;
  /** KAN-2104 — může si stroj teď vzít práci? ``false`` = obsazený / stojí. */
  machine_free?: boolean | null;
}

export interface ProductionJobCandidateList {
  items: ProductionJobCandidate[];
}

export interface ProductionJobCandidateReleaseBody {
  node_id: string;
  machine_id: number;
  group_key: string;
  format_key?: string | null;
}
